import { Fragment, type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Archive,
  AlertTriangle,
  Bell,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  FileText,
  LayoutDashboard,
  LogOut,
  Plus,
  Search,
  Route as RouteIcon,
  Shield,
  SlidersHorizontal,
  Truck,
  UserCog,
  UsersRound,
  Wifi,
  WifiOff
} from "lucide-react";
import clsx from "clsx";
import readXlsxFile from "read-excel-file";
import { canOpen, getAllRoles, roleAccess, roleLabels, weekDays } from "../data/appModel";
import type {
  AppUser,
  AppState,
  Carrier,
  CarrierDocument,
  CustomRole,
  Driver,
  DriverType,
  NavKey,
  OperationalRequest,
  Role,
  RoutePlan,
  RoutePoint,
  ScheduleEntry,
  SessionUser,
  TaskItem,
  Vehicle
} from "../types";

type DashboardProps = {
  user: SessionUser;
  appState: AppState;
  onLogout: () => void;
  onRoleChange: (userId: string, role: Role) => void;
  onAppStateChange: (updater: (current: AppState) => AppState) => void;
  onResetLocalState: () => void;
  initialView?: NavKey;
  cloudOnline?: boolean;
};

const navItems: Array<{ key: NavKey; label: string; icon: typeof LayoutDashboard }> = [
  { key: "pulpit", label: "Pulpit", icon: LayoutDashboard },
  { key: "grafik", label: "Grafik", icon: CalendarDays },
  { key: "dodajWpis", label: "Dodaj wpis", icon: FileText },
  { key: "dodajTrase", label: "Dodaj trasę", icon: RouteIcon },
  { key: "szukaj", label: "Szukaj kierowcy", icon: Search },
  { key: "braki", label: "Sprawdź braki", icon: AlertTriangle },
  { key: "zgloszenia", label: "Zgłoszenia", icon: ClipboardList },
  { key: "powiadomienia", label: "Powiadomienia", icon: Bell },
  { key: "archiwum", label: "Archiwum", icon: Archive },
  { key: "administracja", label: "Administracja", icon: UserCog }
];

const statusLabels: Record<ScheduleEntry["status"], string> = {
  P: "Zrealizowane",
  PLAN: "Zaplanowane",
  BRAK: "Brak",
  W: "Wolne",
  U: "Urlop",
  L4: "L4",
  NIEOBECNY: "Nieobecny"
};

function scheduleStatusMatchesFilter(status: ScheduleEntry["status"], filter: string) {
  if (filter === "WSZYSTKIE") return true;
  if (filter === "ALERT") return status === "BRAK" || status === "L4" || status === "NIEOBECNY";
  if (filter === "WOLNE") return status === "W" || status === "U";
  return status === filter;
}

const vbaViews: NavKey[] = ["grafik", "dodajWpis", "dodajTrase", "szukaj", "braki", "zgloszenia", "archiwum"];

const warehousePoint: RoutePoint = {
  shopNo: "MAG",
  city: "Łódź",
  code: "92-410",
  type: "Magazyn",
  address: "aleja Ofiar Terroryzmu 11 Września 12, 92-410 Łódź",
  notes: "Miejsce załadunku"
};

function createId(prefix: string) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function nowStamp() {
  return new Date().toLocaleString("pl-PL");
}

function archiveItem(category: string, title: string, owner: string, details = "") {
  return {
    id: createId("arc"),
    category,
    title,
    owner,
    archivedAt: nowStamp(),
    details
  };
}

function statusToLabel(status: ScheduleEntry["status"]) {
  if (status === "P") return "Praca";
  if (status === "PLAN") return "Plan";
  if (status === "BRAK") return "Brak";
  if (status === "W") return "Wolne";
  if (status === "L4") return "L4";
  if (status === "NIEOBECNY") return "Nieobecny";
  return "Urlop";
}

function statusShort(status: ScheduleEntry["status"] | "") {
  if (!status) return "-";
  if (status === "PLAN") return "P";
  if (status === "BRAK") return "B";
  if (status === "NIEOBECNY") return "N";
  return status;
}

function dayName(date: string) {
  const value = new Date(`${date}T12:00:00`);
  if (Number.isNaN(value.getTime())) return "";
  return value.toLocaleDateString("pl-PL", { weekday: "long" });
}

function weekNumber(date: string) {
  const value = new Date(`${date}T12:00:00`);
  if (Number.isNaN(value.getTime())) return "";
  const firstDay = new Date(value.getFullYear(), 0, 1);
  const days = Math.floor((value.getTime() - firstDay.getTime()) / 86400000);
  return `${Math.ceil((days + firstDay.getDay() + 1) / 7)}/${value.getFullYear()}`;
}

function toIsoDate(value: unknown) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  const text = String(value ?? "").trim();
  const match = text.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})$/);
  if (match) {
    const year = match[3].length === 2 ? `20${match[3]}` : match[3];
    return `${year}-${match[2].padStart(2, "0")}-${match[1].padStart(2, "0")}`;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text;
  }

  return "";
}

function dateRange(start: string, end: string) {
  const dates: string[] = [];
  const current = new Date(`${start}T12:00:00`);
  const last = new Date(`${end || start}T12:00:00`);

  if (Number.isNaN(current.getTime()) || Number.isNaN(last.getTime())) {
    return dates;
  }

  while (current <= last) {
    dates.push(current.toISOString().slice(0, 10));
    current.setDate(current.getDate() + 1);
  }

  return dates;
}

function normalizeCity(value: string) {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l")
    .replace(/ą/g, "a")
    .replace(/ć/g, "c")
    .replace(/ę/g, "e")
    .replace(/ń/g, "n")
    .replace(/ó/g, "o")
    .replace(/ś/g, "s")
    .replace(/ż/g, "z")
    .replace(/ź/g, "z")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ");
}

function parseActiveFlag(value: unknown, fallback = true) {
  const normalized = normalizeCity(String(value ?? ""));
  if (!normalized) return fallback;
  if (["nie", "nieaktywny", "nieaktywna", "dezaktywowany", "dezaktywowana", "false", "0", "inactive"].includes(normalized)) return false;
  if (["tak", "aktywny", "aktywna", "true", "1", "active"].includes(normalized)) return true;
  return fallback;
}

function parseDelimitedRows(text: string) {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.split(line.includes("\t") ? "\t" : line.includes(";") ? ";" : ",").map((cell) => cell.trim()));
}

function vehicleDisplayName(vehicle?: Vehicle | null) {
  if (!vehicle) return "";
  return vehicle.name || [vehicle.model, vehicle.registration].filter(Boolean).join(" ");
}

function VehiclePhotoPlaceholder({ size = 54 }: { size?: number }) {
  return (
    <span className="vehicle-photo-placeholder" aria-hidden="true">
      <Truck size={size} />
    </span>
  );
}

function vehicleModelFileName(model: string) {
  return model
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^\w.-]/g, "")
    .replace(/_+/g, "_");
}

function fileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Nie udało się odczytać pliku."));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsDataURL(file);
  });
}

async function vehicleImageDataUrl(file: File) {
  return fileToDataUrl(file);
}

function textDataUrl(text: string) {
  return `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`;
}

function carrierOperationsText(appState: AppState, carrier: Carrier) {
  const carrierKey = normalizeCity(carrier.name);
  const driverKeys = appState.drivers
    .filter((driver) => driver.carrierId === carrier.id)
    .map((driver) => normalizeCity(driver.name))
    .filter(Boolean);

  const operations = appState.archive.filter((item) => {
    const haystack = normalizeCity([item.category, item.title, item.owner, item.details ?? ""].join(" "));
    return Boolean(carrierKey && haystack.includes(carrierKey)) || driverKeys.some((driverKey) => haystack.includes(driverKey));
  });

  const lines = operations.map((item) =>
    [
      item.archivedAt,
      item.category,
      item.owner,
      item.title,
      item.details ?? ""
    ].filter(Boolean).join(" | ")
  );

  return [
    `OPERACJE PRZEWOZNIKA: ${carrier.name}`,
    "",
    ...(lines.length ? lines : ["Brak operacji zapisanych dla tego przewoznika."])
  ].join("\n");
}

function carrierOperationsDocument(appState: AppState, carrier: Carrier): CarrierDocument {
  const text = carrierOperationsText(appState, carrier);
  return {
    id: `carrier-ops-${carrier.id}`,
    carrierId: carrier.id,
    folder: "Operacje",
    name: "operacje.txt",
    size: text.length,
    mimeType: "text/plain",
    uploadedAt: "auto",
    dataUrl: textDataUrl(text),
    previewText: text
  };
}

function isWarehousePoint(point?: RoutePoint) {
  if (!point) return false;
  return point.type === "Magazyn" && normalizeCity(`${point.city} ${point.address}`).includes("ofiar terroryzmu");
}

function withWarehouse(points: RoutePoint[] = []) {
  if (isWarehousePoint(points[0])) return points;
  return [warehousePoint, ...points.filter((point) => !isWarehousePoint(point))];
}

function routePointQuery(point: RoutePoint) {
  return [point.address, point.code, point.city, "Polska"]
    .map((item) => item?.trim())
    .filter(Boolean)
    .join(", ");
}

function googleRouteUrls(points: RoutePoint[]) {
  const labels = withWarehouse(points).map(routePointQuery).filter(Boolean);
  if (labels.length === 0) {
    return {
      embedUrl: "https://maps.google.com/maps?q=%C5%81%C3%B3d%C5%BA%2C%20Polska&output=embed",
      openUrl: "https://www.google.com/maps"
    };
  }

  if (labels.length === 1) {
    const query = encodeURIComponent(labels[0]);
    return {
      embedUrl: `https://maps.google.com/maps?q=${query}&output=embed`,
      openUrl: `https://www.google.com/maps/search/?api=1&query=${query}`
    };
  }

  const [origin, ...rest] = labels;
  const destination = rest[rest.length - 1];
  const waypoints = rest.slice(0, -1);
  const params = new URLSearchParams({ api: "1", origin, destination });
  if (waypoints.length > 0) params.set("waypoints", waypoints.join("|"));

  const directionsPath = labels.map((label) => encodeURIComponent(label)).join("/");

  return {
    embedUrl: `https://www.google.com/maps/dir/${directionsPath}/?hl=pl&output=embed`,
    openUrl: `https://www.google.com/maps/dir/?${params.toString()}`
  };
}

function routeNameFromPoints(points: RoutePoint[]) {
  return withWarehouse(points)
    .map((point) => point.city.trim())
    .filter(Boolean)
    .filter((city, index, cities) => cities.findIndex((candidate) => normalizeCity(candidate) === normalizeCity(city)) === index)
    .join(" -> ");
}

function scheduleTypeKey(value?: string | null) {
  const normalized = normalizePersonName(value ?? "");
  if (normalized.includes("food bus")) return "food bus";
  if (normalized.includes("dedyk")) return "dedyk";
  if (normalized.includes("food")) return "food";
  return normalized;
}

function cleanTypeLabel(value?: string | null, allowedTypes: string[] = []) {
  const key = scheduleTypeKey(value);
  const base = ["Food Bus", "Dedyk", "Food"].find((type) => scheduleTypeKey(type) === key);
  if (base) return base;
  const allowed = allowedTypes.find((type) => scheduleTypeKey(type) === key);
  if (allowed) return allowed;
  return String(value ?? "")
    .replace(/\b(Poranny|Wieczorny|Popołudniowy|Popoludniowy|Ranny)\s+/gi, "")
    .trim();
}

function cleanScheduleNote(value?: string | null) {
  const note = String(value ?? "").trim();
  if (!note) return "";
  const normalized = normalizeCity(note);
  if (normalized.includes("import excel") || normalized.includes("wolne z importu") || normalized.includes("brak wpisu w grafiku")) return "";
  return note
    .replace(/\b(Poranny|Wieczorny|Popołudniowy|Popoludniowy|Ranny)\s+(Food Bus|Food|Dedyk)\b/gi, "$2")
    .trim();
}

function hasEntryNote(entry?: ScheduleEntry | null) {
  return Boolean(cleanScheduleNote(entry?.notes));
}

function entryNoteText(entry?: ScheduleEntry | null) {
  return cleanScheduleNote(entry?.notes) || "-";
}

function isDriverActive(driver?: Driver | null) {
  return parseActiveFlag((driver as { active?: unknown } | undefined)?.active, true);
}

function isVehicleActive(vehicle?: Vehicle | null) {
  return parseActiveFlag((vehicle as { active?: unknown } | undefined)?.active, true);
}

function entryHasActiveDriver(appState: AppState, entry: ScheduleEntry) {
  if (!entry.driverId) return false;
  return appState.drivers.some((driver) => driver.id === entry.driverId && isDriverActive(driver));
}

function isDeletedDriverScheduleEntry(entry: ScheduleEntry) {
  return String(entry.notes ?? "").toLowerCase().includes("kierowca usuni");
}

function entryHasActiveDriverOrOpenShortage(appState: AppState, entry: ScheduleEntry) {
  if (entry.driverId) return entryHasActiveDriver(appState, entry);
  return entry.status === "BRAK" && !isDeletedDriverScheduleEntry(entry);
}

function entryDriverLabel(appState: AppState, entry?: ScheduleEntry, fallback = "Nie wskazano kierowcy") {
  if (!entry) return "";
  const driver = appState.drivers.find((candidate) => candidate.id === entry.driverId);
  if (driver) return driver.name;
  const fromNotes = String(entry.notes ?? "").match(/Kierowca:\s*([^|\n]+)/i)?.[1]?.trim();
  if (fromNotes && fromNotes !== "-") return fromNotes;
  return fallback;
}

function notesChangedByCleanup(value?: string | null) {
  return String(value ?? "").trim() !== cleanScheduleNote(value);
}

function DriverAvatar({ driver, className }: { driver?: Driver; className?: string }) {
  return (
    <div className={clsx("driver-avatar", className, driver?.photoDataUrl && "has-photo")}>
      {driver?.photoDataUrl ? <img src={driver.photoDataUrl} alt="" /> : driver?.name?.slice(0, 1) || "?"}
    </div>
  );
}

function ActiveCheck({
  checked,
  disabled,
  onChange,
  label
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
}) {
  return (
    <span className={clsx("checkbox-wrapper", disabled && "is-disabled")} title={label}>
      <input
        checked={checked}
        disabled={disabled}
        type="checkbox"
        onChange={(event) => onChange(event.target.checked)}
      />
      <svg viewBox="0 0 35.6 35.6" aria-hidden="true">
        <circle className="background" cx="17.8" cy="17.8" r="17.8" />
        <circle className="stroke" cx="17.8" cy="17.8" r="14.37" />
        <polyline className="check" points="11.78 18.12 15.55 22.23 25.17 12.87" />
      </svg>
    </span>
  );
}

function NeonSearchInput({
  value,
  onChange,
  placeholder,
  onFilterClick,
  filterActive,
  className
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  onFilterClick?: () => void;
  filterActive?: boolean;
  className?: string;
}) {
  const searchId = useId();

  return (
    <div className={clsx("neon-search-shell", className, filterActive && "is-filter-active")}>
      <div className="search-glow" />
      <div className="search-dark-border-bg" />
      <div className="search-white" />
      <div className="search-border" />
      <div className="search-main">
        <label className="sr-only" htmlFor={searchId}>{placeholder}</label>
        <input
          id={searchId}
          className="neon-search-input"
          placeholder={placeholder}
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        {onFilterClick && (
          <>
            <div className="search-filter-border" />
            <button
              className="search-filter-icon"
              type="button"
              aria-label="Filtry"
              title="Filtry"
              onClick={onFilterClick}
            >
              <SlidersHorizontal size={18} />
            </button>
          </>
        )}
        <span className="search-icon" aria-hidden="true">
          <Search size={20} />
        </span>
      </div>
    </div>
  );
}

function GoogleRouteMap({ points }: { points: RoutePoint[] }) {
  const { embedUrl } = googleRouteUrls(points);

  return (
    <div className="google-route-map">
      <iframe
        title="Trasa w Google Maps"
        src={embedUrl}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
}

function normalizePersonName(value: string) {
  return normalizeCity(value)
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenPrefixMatch(source: string, candidate: string) {
  const sourceTokens = source.split(" ").filter(Boolean);
  const candidateTokens = candidate.split(" ").filter(Boolean);
  if (!sourceTokens.length || !candidateTokens.length) return false;

  let candidateIndex = 0;
  for (const sourceToken of sourceTokens) {
    let matched = false;
    while (candidateIndex < candidateTokens.length) {
      const candidateToken = candidateTokens[candidateIndex];
      candidateIndex += 1;
      if (
        candidateToken === sourceToken ||
        candidateToken.startsWith(sourceToken) ||
        sourceToken.startsWith(candidateToken) ||
        (sourceToken.length === 1 && candidateToken.startsWith(sourceToken))
      ) {
        matched = true;
        break;
      }
    }
    if (!matched) return false;
  }

  return true;
}

function driverMatchesImportName(driver: Driver, importedName: string) {
  const source = normalizePersonName(importedName);
  const full = normalizePersonName(driver.name);
  const [first = "", ...lastParts] = full.split(" ");
  const last = lastParts.join(" ");
  const reversed = normalizePersonName(`${last} ${first}`);
  const initialLast = normalizePersonName(`${first.slice(0, 1)} ${last}`);
  const variants = [full, reversed, initialLast].filter(Boolean);

  return variants.some((variant) =>
    source === variant ||
    variant.includes(source) ||
    tokenPrefixMatch(source, variant)
  );
}

function sameScheduleType(left?: string, right?: string) {
  return Boolean(left && right && scheduleTypeKey(left) === scheduleTypeKey(right));
}

function findRouteByType(routes: RoutePlan[], type?: string) {
  return routes.find((route) => sameScheduleType(route.serviceType, type));
}

function findRouteForDriver(routes: RoutePlan[], driver?: Driver | null, fallbackType?: string) {
  if (driver) {
    return routes.find((route) => route.driverId === driver.id && sameScheduleType(route.serviceType, driver.type)) ??
      routes.find((route) => route.driverId === driver.id) ??
      findRouteByType(routes, fallbackType);
  }

  return findRouteByType(routes, fallbackType) ?? routes[0];
}

function entryScheduleType(appState: AppState, entry: ScheduleEntry) {
  const driver = appState.drivers.find((candidate) => candidate.id === entry.driverId);
  if (driver?.type) return driver.type;
  return appState.routes.find((route) => route.id === entry.routeId)?.serviceType ?? "";
}

function syntheticShortageEntry(appState: AppState, driver: Driver, date: string): ScheduleEntry {
  const route = findRouteForDriver(appState.routes, driver) ?? appState.routes[0];
  return {
    id: `missing-${driver.id}-${date}`,
    date,
    driverId: driver.id,
    carrierId: driver.carrierId,
    routeId: route?.id ?? "",
    status: "BRAK",
    start: driver.awizacjaOd1 || route?.defaultStart || "",
    end: "",
    awizacjaOd: driver.awizacjaOd1 || route?.defaultStart || "",
    notes: "Brak wpisu w grafiku",
    createdBy: "SNG"
  };
}

function orderedScheduleGroups(groups: string[]) {
  const preferred = ["Food", "Food Bus", "Dedyk"];
  const ordered = preferred
    .map((preferredGroup) => groups.find((group) => sameScheduleType(group, preferredGroup)))
    .filter((group): group is string => Boolean(group));
  const rest = groups.filter((group) => !ordered.some((orderedGroup) => sameScheduleType(orderedGroup, group)));
  return [...ordered, ...rest];
}

function roleLabel(role: Role) {
  return roleLabels[role] ?? role;
}

function canManagePeople(user: SessionUser, customRoles: CustomRole[] = []) {
  const customRole = customRoles.find((role) => role.name === user.role);
  return user.role === "administrator" || user.role === "kierownik" || customRole?.template === "wysokie" || customRole?.template === "admin";
}

function notificationMatchesUser(notification: { recipient: string }, user: SessionUser) {
  if (notification.recipient === "WSZYSCY") return true;
  if (notification.recipient === `ROLA:${user.role}`) return true;
  if (notification.recipient === `USER:${user.login}`) return true;
  if (notification.recipient === `CARRIER:${user.carrierId}`) return true;
  if (notification.recipient === "PRZEWOZNICY" && user.role === "przewoznik") return true;
  return user.role === "administrator" || user.role === "kierownik";
}

function isNotificationUnread(notification: { read: boolean; readBy?: string[] }, user: SessionUser) {
  return !notification.read && !(notification.readBy ?? []).includes(user.login);
}

export function Dashboard({ user, appState, onLogout, onRoleChange, onAppStateChange, onResetLocalState, initialView = "pulpit", cloudOnline = false }: DashboardProps) {
  const canUserOpen = (view: NavKey) => canOpen(user, view, appState.customRoles);
  const visibleNav = useMemo(() => navItems.filter((item) => canOpen(user, item.key, appState.customRoles)), [user, appState.customRoles]);
  const [activeView, setActiveView] = useState<NavKey>(canOpen(user, initialView, appState.customRoles) ? initialView : visibleNav[0]?.key ?? "pulpit");
  const [routeEditor, setRouteEditor] = useState<{ routeId?: string; driverId?: string } | null>(null);
  const [scheduleFilter, setScheduleFilter] = useState<{ driverId?: string; type?: string; date?: string; status?: string } | null>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const safeView = canUserOpen(activeView) ? activeView : visibleNav[0]?.key ?? "pulpit";
  const isVbaView = vbaViews.includes(safeView);
  const visibleNotifications = appState.notifications.filter((notification) => notificationMatchesUser(notification, user));
  const unreadNotifications = visibleNotifications.filter((notification) => isNotificationUnread(notification, user)).length;
  const scopedDrivers = user.role === "przewoznik" && user.carrierId
    ? appState.drivers.filter((driver) => driver.carrierId === user.carrierId)
    : appState.drivers;
  const scopedDriverIds = new Set(scopedDrivers.map((driver) => driver.id));
  const scopedSchedule = user.role === "przewoznik" && user.carrierId
    ? appState.schedule.filter((entry) => entry.carrierId === user.carrierId || (entry.driverId ? scopedDriverIds.has(entry.driverId) : false))
    : appState.schedule;

  useEffect(() => {
    onAppStateChange((current) => {
      let changed = false;
      const routeTypes = Array.from(new Set(current.routeTypes.map((type) => cleanTypeLabel(type, current.routeTypes)).filter(Boolean)));
      if (routeTypes.length !== current.routeTypes.length || routeTypes.some((type, index) => type !== current.routeTypes[index])) {
        changed = true;
      }

      const drivers = current.drivers.map((driver) => {
        const type = cleanTypeLabel(driver.type, routeTypes);
        if (type === driver.type) return driver;
        changed = true;
        return { ...driver, type };
      });

      const routes = current.routes.map((route) => {
        const serviceType = cleanTypeLabel(route.serviceType, routeTypes);
        if (serviceType === route.serviceType) return route;
        changed = true;
        return { ...route, serviceType };
      });

      const schedule = current.schedule.map((entry) => {
        if (!notesChangedByCleanup(entry.notes)) return entry;
        changed = true;
        return { ...entry, notes: cleanScheduleNote(entry.notes) };
      });

      return changed ? { ...current, routeTypes, drivers, routes, schedule } : current;
    });
  }, [appState.routeTypes, appState.drivers, appState.routes, appState.schedule, onAppStateChange]);

  useEffect(() => {
    const hasMismatchedRouteType = appState.schedule.some((entry) => {
      const driver = appState.drivers.find((candidate) => candidate.id === entry.driverId);
      const route = appState.routes.find((candidate) => candidate.id === entry.routeId);
      return Boolean(driver && route && !sameScheduleType(driver.type, route.serviceType));
    });
    if (!hasMismatchedRouteType) return;

    onAppStateChange((current) => {
      let fixedCount = 0;
      const schedule = current.schedule.map((entry) => {
        const driver = current.drivers.find((candidate) => candidate.id === entry.driverId);
        const route = current.routes.find((candidate) => candidate.id === entry.routeId);
        if (!driver || !route || sameScheduleType(driver.type, route.serviceType)) return entry;

        const matchingRoute = findRouteForDriver(current.routes, driver);
        if (!matchingRoute || matchingRoute.id === entry.routeId) return entry;

        fixedCount += 1;
        return {
          ...entry,
          routeId: matchingRoute.id,
          start: entry.start || driver.awizacjaOd1 || matchingRoute.defaultStart,
          awizacjaOd: entry.awizacjaOd || driver.awizacjaOd1 || matchingRoute.defaultStart
        };
      });

      if (!fixedCount) return current;

      return {
        ...current,
        schedule,
        archive: [
          archiveItem("System", `Korekta typów grafiku: ${fixedCount}`, "SNG", "Wpisy przepięte na trasy zgodne z typem kierowcy."),
          ...current.archive
        ]
      };
    });
  }, [appState.schedule, appState.drivers, appState.routes, onAppStateChange]);

  useEffect(() => {
    onAppStateChange((current) => {
      const carrierIds = new Set(current.carriers.map((carrier) => carrier.id));
      let changed = false;
      let folders = current.carrierDocumentFolders.filter((folder) =>
        !folder.id.startsWith("carrier-ops-folder-") || carrierIds.has(folder.carrierId)
      );
      let documents = current.carrierDocuments.filter((document) =>
        !document.id.startsWith("carrier-ops-") || carrierIds.has(document.carrierId)
      );

      for (const carrier of current.carriers) {
        const folderId = `carrier-ops-folder-${carrier.id}`;
        if (!folders.some((folder) => folder.id === folderId)) {
          folders = [
            ...folders,
            { id: folderId, carrierId: carrier.id, name: "Operacje", createdAt: "auto" }
          ];
          changed = true;
        }

        const nextDocument = carrierOperationsDocument(current, carrier);
        const currentIndex = documents.findIndex((document) => document.id === nextDocument.id);
        if (currentIndex === -1) {
          documents = [...documents, nextDocument];
          changed = true;
        } else if (documents[currentIndex].previewText !== nextDocument.previewText) {
          documents = documents.map((document, index) => index === currentIndex ? nextDocument : document);
          changed = true;
        }
      }

      return changed ? { ...current, carrierDocumentFolders: folders, carrierDocuments: documents } : current;
    });
  }, [appState.carriers, appState.drivers, appState.archive, onAppStateChange]);
  const scopedRequests = user.role === "przewoznik"
    ? appState.requests.filter((request) => request.sender === user.login || request.payload?.carrierId === user.carrierId)
    : appState.requests;

  const activeScopedDrivers = scopedDrivers.filter(isDriverActive);
  const activeScopedDriverIds = new Set(activeScopedDrivers.map((driver) => driver.id));
  const activeScopedSchedule = scopedSchedule.filter((entry) => entryHasActiveDriver(appState, entry) && activeScopedDriverIds.has(entry.driverId ?? ""));
  const todayForMetrics = new Date().toISOString().slice(0, 10);
  const activeDrivers = activeScopedDrivers.length;
  const missingDrivers = activeScopedDrivers.filter((driver) => !activeScopedSchedule.some((entry) => entry.driverId === driver.id && entry.date === todayForMetrics)).length;
  const confirmed = activeScopedSchedule.filter((entry) => entry.status === "P").length;
  const pendingRequests = scopedRequests.filter((request) => request.status === "oczekuje").length;

  function carrierName(id: string) {
    return appState.carriers.find((carrier) => carrier.id === id)?.name ?? "Brak przewoźnika";
  }

  function driverName(id?: string) {
    if (!id) {
      return "Brak kierowcy";
    }

    return appState.drivers.find((driver) => driver.id === id)?.name ?? "Nieznany kierowca";
  }

  function routeName(id: string) {
    return appState.routes.find((route) => route.id === id)?.name ?? "Nieznana trasa";
  }

  function markNotificationRead(id: string) {
    onAppStateChange((current) => ({
      ...current,
      notifications: current.notifications.map((notification) =>
        notification.id === id
          ? { ...notification, readBy: Array.from(new Set([...(notification.readBy ?? []), user.login])), read: canManagePeople(user, appState.customRoles) ? true : notification.read }
          : notification
      )
    }));
  }

  function clearVisibleNotifications() {
    onAppStateChange((current) => ({
      ...current,
      notifications: canManagePeople(user, appState.customRoles)
        ? current.notifications.filter((notification) => !visibleNotifications.some((visible) => visible.id === notification.id))
        : current.notifications.map((notification) =>
            visibleNotifications.some((visible) => visible.id === notification.id)
              ? { ...notification, readBy: Array.from(new Set([...(notification.readBy ?? []), user.login])) }
              : notification
          )
    }));
    setNotificationsOpen(false);
  }

  if (safeView === "administracja") {
    return (
      <main className="admin-standalone-shell">
        <AdminView
          appState={appState}
          onRoleChange={onRoleChange}
          onAppStateChange={onAppStateChange}
          onResetLocalState={onResetLocalState}
          onClose={() => setActiveView("pulpit")}
        />
        <NotificationBell
          notifications={visibleNotifications}
          unreadCount={unreadNotifications}
          open={notificationsOpen}
          user={user}
          mode="floating"
          onToggle={() => setNotificationsOpen((value) => !value)}
          onRead={markNotificationRead}
          onClear={clearVisibleNotifications}
          canClear={canManagePeople(user, appState.customRoles)}
        />
      </main>
    );
  }

  return (
    <main className="app-shell">
      <aside className="sidebar">
        <div
          className={clsx("sidebar__connection", cloudOnline ? "is-online" : "is-offline")}
          title={cloudOnline ? "Połączono z chmurą" : "Brak połączenia z chmurą"}
          aria-label={cloudOnline ? "Połączono z chmurą" : "Brak połączenia z chmurą"}
        >
          {cloudOnline ? <Wifi size={18} /> : <WifiOff size={18} />}
        </div>
        <div className="sidebar__brand">
          <span>SN</span>
          <strong>G</strong>
        </div>
        <nav className="nav-list" aria-label="Nawigacja">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            return (
              <button
                className={clsx(safeView === item.key && "is-active")}
                type="button"
                onClick={() => {
                  if (item.key === "dodajTrase") setRouteEditor(null);
                  if (item.key === "grafik") setScheduleFilter(null);
                  setActiveView(item.key);
                }}
                data-tooltip={item.label}
                title={item.label}
                key={item.key}
              >
                <Icon size={18} />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="sidebar__user">
          <span>{user.name.slice(0, 2).toUpperCase()}</span>
          <div>
            <strong>{user.name}</strong>
            <small>{roleLabel(user.role)}</small>
          </div>
        </div>
      </aside>

      <section className={clsx("workspace", isVbaView && "workspace--vba")}>
            <header className="topbar">
              <div className="topbar__brand">
                <strong>Panel operacyjny</strong>
                <span>{titleForView(safeView)} i obsada tras</span>
              </div>
              <div className="topbar__actions">
                <button className="topbar-icon-button" type="button" onClick={() => setActiveView("szukaj")} aria-label="Szukaj">
                  <Search size={18} />
                </button>
                <NotificationBell
                  notifications={visibleNotifications}
                  unreadCount={unreadNotifications}
                  open={notificationsOpen}
                  user={user}
                  onToggle={() => setNotificationsOpen((value) => !value)}
                  onRead={markNotificationRead}
                  onClear={clearVisibleNotifications}
                  canClear={canManagePeople(user, appState.customRoles)}
                />
                <button className="topbar-icon-button topbar-icon-button--logout" type="button" onClick={onLogout} aria-label="Wyloguj">
                  <LogOut size={18} />
                </button>
              </div>
            </header>

        {!isVbaView && (
            <section className="metrics" aria-label="Podsumowanie">
              <Metric label="Kierowcy" value={activeDrivers} hint="aktywni w bazie" />
              <Metric label="Zrealizowane" value={confirmed} hint="wpisy pracy w grafiku" onClick={() => setActiveView("grafik")} />
              <Metric label="Braki" value={missingDrivers} hint="wymagają obsady" tone={missingDrivers > 0 ? "warning" : "ok"} onClick={() => setActiveView("braki")} />
              <Metric label="Zgłoszenia" value={pendingRequests} hint="oczekują na decyzję" onClick={() => setActiveView(canUserOpen("administracja") ? "administracja" : "zgloszenia")} />
            </section>
        )}

        {safeView === "pulpit" && (
          <Overview
            appState={appState}
            carrierName={carrierName}
            driverName={driverName}
            routeName={routeName}
            user={user}
            onNavigate={setActiveView}
            onAppStateChange={onAppStateChange}
            onOpenScheduleFilter={(filter) => {
              setScheduleFilter(filter);
              setActiveView("grafik");
            }}
          />
        )}
        {safeView === "grafik" && (
          <FullScheduleVbaView
            appState={appState}
            carrierName={carrierName}
            driverName={driverName}
            routeName={routeName}
            onAppStateChange={onAppStateChange}
            onClose={() => setActiveView("pulpit")}
            user={user}
            initialFilter={scheduleFilter}
            onFilterConsumed={() => setScheduleFilter(null)}
          />
        )}
        {safeView === "dodajWpis" && (
          <AddScheduleEntryVbaView appState={appState} carrierName={carrierName} onAppStateChange={onAppStateChange} user={user} />
        )}
        {safeView === "dodajTrase" && (
          <AddRouteVbaView
            appState={appState}
            carrierName={carrierName}
            onAppStateChange={onAppStateChange}
            onOpenDrivers={() => setActiveView("szukaj")}
            initialRouteId={routeEditor?.routeId}
            lockedDriverId={routeEditor?.driverId}
            onDoneEdit={() => setRouteEditor(null)}
          />
        )}
        {safeView === "szukaj" && (
          <SearchDriverVbaView
            appState={appState}
            carrierName={carrierName}
            user={user}
            onAppStateChange={onAppStateChange}
            onOpenSchedule={(driverId) => {
              setScheduleFilter({ driverId });
              setActiveView("grafik");
            }}
            onOpenRoute={(routeId, driverId) => {
              setRouteEditor({ routeId, driverId });
              setActiveView("dodajTrase");
            }}
          />
        )}
        {safeView === "braki" && (
          <ShortagesView
            appState={appState}
            carrierName={carrierName}
            driverName={driverName}
            routeName={routeName}
            user={user}
            onAppStateChange={onAppStateChange}
          />
        )}
        {safeView === "kierowcy" && <DriversView drivers={appState.drivers} carrierName={carrierName} />}
        {safeView === "przewoznicy" && (
          <CarriersView carriers={appState.carriers} drivers={appState.drivers} schedule={appState.schedule} />
        )}
        {safeView === "trasy" && <RoutesView routes={appState.routes} />}
        {safeView === "zgloszenia" && <RequestsView appState={appState} user={user} onAppStateChange={onAppStateChange} />}
        {safeView === "powiadomienia" && <NotificationsView appState={appState} user={user} onAppStateChange={onAppStateChange} />}
        {safeView === "archiwum" && <ArchiveView appState={appState} user={user} />}
      </section>
    </main>
  );
}

function titleForView(view: NavKey) {
  const item = navItems.find((nav) => nav.key === view);
  return item?.label ?? "Pulpit";
}

function NotificationBell({
  notifications,
  unreadCount,
  open,
  user,
  onToggle,
  onRead,
  onClear,
  canClear,
  mode = "inline"
}: {
  notifications: AppState["notifications"];
  unreadCount: number;
  open: boolean;
  user: SessionUser;
  onToggle: () => void;
  onRead: (id: string) => void;
  onClear: () => void;
  canClear: boolean;
  mode?: "inline" | "floating";
}) {
  return (
    <div className={clsx("floating-notifications", mode === "floating" && "floating-notifications--floating")}>
      <button className="floating-notifications__button" type="button" onClick={onToggle} aria-label="Powiadomienia">
        <Bell size={18} />
        {unreadCount > 0 && <span>{unreadCount}</span>}
      </button>
      {open && (
        <div className="notification-popover">
          <header>
            <strong>Powiadomienia</strong>
            {notifications.length > 0 && <button type="button" onClick={onClear}>{canClear ? "Wyczyść" : "Oznacz"}</button>}
          </header>
          {notifications.length === 0 && <p>Brak powiadomień.</p>}
          {notifications.slice(0, 8).map((notification) => (
            <button
              className={clsx(isNotificationUnread(notification, user) && "is-unread")}
              type="button"
              key={notification.id}
              onClick={() => onRead(notification.id)}
            >
              <span>{notification.recipient}</span>
              <strong>{notification.message}</strong>
              <small>{notification.createdAt}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, hint, tone, onClick }: { label: string; value: string | number; hint: string; tone?: "ok" | "warning"; onClick?: () => void }) {
  return (
    <article className={clsx(tone === "warning" && "is-warning", tone === "ok" && "is-ok", onClick && "is-clickable")} onClick={onClick}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{hint}</small>
      <svg className="metric-sparkline" viewBox="0 0 240 34" aria-hidden="true">
        <path d="M2 26 L34 20 L66 23 L96 18 L128 24 L160 15 L192 21 L238 13" />
      </svg>
    </article>
  );
}

function Overview({
  appState,
  carrierName,
  driverName,
  routeName,
  user,
  onNavigate,
  onAppStateChange,
  onOpenScheduleFilter
}: {
  appState: AppState;
  carrierName: (id: string) => string;
  driverName: (id?: string) => string;
  routeName: (id: string) => string;
  user: SessionUser;
  onNavigate: (view: NavKey) => void;
  onAppStateChange: (updater: (current: AppState) => AppState) => void;
  onOpenScheduleFilter: (filter: { driverId?: string; type?: string; date?: string; status?: string }) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [rangePreset, setRangePreset] = useState<"today" | "tomorrow" | "week" | "nextWeek">("today");
  const [noteText, setNoteText] = useState("");
  const notes = appState.notes ?? [];

  function saveNotes(nextNotes: string[]) {
    onAppStateChange((current) => ({
      ...current,
      notes: nextNotes
    }));
  }

  function addNote() {
    if (!noteText.trim()) return;
    saveNotes([`${new Date().toLocaleDateString("pl-PL")} · ${noteText.trim()}`, ...notes]);
    setNoteText("");
  }

  function rangeDates() {
    if (rangePreset === "today") return [today];
    if (rangePreset === "tomorrow") return [tomorrow];
    const start = new Date(`${today}T12:00:00`);
    const day = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - day + (rangePreset === "nextWeek" ? 7 : 0));
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return dateRange(start.toISOString().slice(0, 10), end.toISOString().slice(0, 10));
  }

  const selectedDates = rangeDates();
  const scopedSchedule = user.role === "przewoznik" && user.carrierId
    ? appState.schedule.filter((entry) => entry.carrierId === user.carrierId)
    : appState.schedule;
  const scopedDrivers = (user.role === "przewoznik" && user.carrierId
    ? appState.drivers.filter((driver) => driver.carrierId === user.carrierId)
    : appState.drivers).filter(isDriverActive);
  const activeDriverIds = new Set(scopedDrivers.map((driver) => driver.id));
  const visibleSchedule = scopedSchedule.filter((entry) => selectedDates.includes(entry.date) && entryHasActiveDriver(appState, entry) && activeDriverIds.has(entry.driverId ?? ""));
  const workEntries = visibleSchedule.filter((entry) => entry.status === "P");
  const freeEntries = visibleSchedule.filter((entry) => entry.status === "W" || entry.status === "U");
  const l4Entries = visibleSchedule.filter((entry) => entry.status === "L4" || entry.status === "NIEOBECNY");
  const shortageEntries = scopedDrivers.flatMap((driver) =>
    selectedDates
      .filter((date) => !scopedSchedule.some((entry) => entry.driverId === driver.id && entry.date === date))
      .map((date) => syntheticShortageEntry(appState, driver, date))
  );
  const ownNotifications = appState.notifications.filter((notification) => notificationMatchesUser(notification, user)).slice(0, 4);
  const recentArchive = appState.archive
    .filter((item) => item.owner === user.login || item.owner === user.name)
    .slice(0, 6);
  const weekStart = (() => {
    const start = new Date(`${today}T12:00:00`);
    const day = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - day);
    return start.toISOString().slice(0, 10);
  })();
  const weekEnd = (() => {
    const end = new Date(`${weekStart}T12:00:00`);
    end.setDate(end.getDate() + 6);
    return end.toISOString().slice(0, 10);
  })();
  const upcomingMissing = scopedDrivers.flatMap((driver) =>
    dateRange(weekStart, weekEnd)
      .filter((date) => !scopedSchedule.some((entry) => entry.driverId === driver.id && entry.date === date))
      .map((date) => syntheticShortageEntry(appState, driver, date))
  ).slice(0, 5);
  const myTasks = appState.tasks.filter((task) => !task.completed && (!task.assignedTo || task.assignedTo === user.login || task.assignedTo === user.name)).slice(0, 5);
  const dueLabels: Record<TaskItem["due"], string> = {
    today: "Dziś",
    tomorrow: "Jutro",
    week: "Tydzień",
    nextWeek: "Następny"
  };
  const rangeLabel = {
    today: "Dzisiaj",
    tomorrow: "Jutro",
    week: "Ten tydzień",
    nextWeek: "Następny tydzień"
  }[rangePreset];

  function toggleTask(taskId: string) {
    onAppStateChange((current) => ({
      ...current,
      tasks: current.tasks.map((task) => task.id === taskId ? { ...task, completed: !task.completed } : task),
      archive: [archiveItem("Zadania", `Zmieniono status zadania ${taskId}`, user.login), ...current.archive]
    }));
  }

  return (
    <section className="dashboard-pro">
      <div className="dashboard-pro__head">
        <div>
          <span className="eyebrow">Pulpit użytkownika</span>
          <h2>Witaj, {user.name}</h2>
        </div>
        <div className="range-tabs">
          <button className={clsx(rangePreset === "today" && "is-active")} onClick={() => setRangePreset("today")}>Dzisiaj</button>
          <button className={clsx(rangePreset === "tomorrow" && "is-active")} onClick={() => setRangePreset("tomorrow")}>Jutro</button>
          <button className={clsx(rangePreset === "week" && "is-active")} onClick={() => setRangePreset("week")}>Ten tydzień</button>
          <button className={clsx(rangePreset === "nextWeek" && "is-active")} onClick={() => setRangePreset("nextWeek")}>Następny tydzień</button>
        </div>
      </div>

      <div className="dashboard-stat-grid">
        <button onClick={() => onOpenScheduleFilter({ date: selectedDates[0] ?? today, status: "P" })}><UsersRound size={26} /><span>Pracuje</span><strong>{workEntries.length}</strong><small>{rangeLabel}</small></button>
        <button onClick={() => onNavigate("grafik")}><CheckCircle2 size={26} /><span>Wolne/urlop</span><strong>{freeEntries.length}</strong><small>{rangeLabel}</small></button>
        <button onClick={() => onNavigate("grafik")}><ClipboardList size={26} /><span>L4 / nieobecni</span><strong>{l4Entries.length}</strong><small>{rangeLabel}</small></button>
        <button onClick={() => onNavigate("braki")}><AlertTriangle size={26} /><span>Braki</span><strong>{shortageEntries.length}</strong><small>wymagają obsady</small></button>
      </div>

      <div className="dashboard-grid">
        <section className="dashboard-card">
          <PanelTitle title="MOJE ZADANIA" />
          {myTasks.map((task) => (
            <label key={task.id}>
              <input type="checkbox" checked={task.completed} onChange={() => toggleTask(task.id)} />
              {task.title}
              <span>{task.priority === "wysoki" ? "Pilne" : dueLabels[task.due]}</span>
            </label>
          ))}
          {myTasks.length === 0 && <p className="muted-line">Brak aktywnych zadań.</p>}
          <button className="link-row" onClick={() => onNavigate("braki")}>Zobacz zadania i braki</button>
        </section>

        <section className="dashboard-card">
          <PanelTitle title="BRAKI DO UZUPEŁNIENIA" />
          <div className="mini-table">
            {upcomingMissing.map((entry) => (
              <button key={entry.id} onClick={() => onNavigate("braki")}>
                <span>{driverName(entry.driverId)}</span>
                <span>{entry.date}</span>
                <b>{entryScheduleType(appState, entry) || "-"}</b>
              </button>
            ))}
          </div>
          <button className="link-row" onClick={() => onNavigate("braki")}>Zobacz wszystkie braki</button>
        </section>

        <section className="dashboard-card">
          <PanelTitle title="KOMUNIKATY" />
          <div className="stack-list stack-list--compact">
            {ownNotifications.map((notification) => (
              <article key={notification.id}>
                <strong>{notification.message}</strong>
                <span>{notification.createdAt}</span>
              </article>
            ))}
          </div>
          <button className="link-row" onClick={() => onNavigate("powiadomienia")}>Zobacz wszystkie komunikaty</button>
        </section>

        <section className="dashboard-card dashboard-card--actions">
          <PanelTitle title="SZYBKIE AKCJE" />
          <button onClick={() => onNavigate("dodajWpis")}><Plus size={18} />Dodaj wpis</button>
          <button onClick={() => onNavigate("szukaj")}><Search size={18} />Szukaj kierowcy</button>
          <button onClick={() => onOpenScheduleFilter({})}><CalendarDays size={18} />Pełny grafik</button>
          <button onClick={() => onNavigate("braki")}><AlertTriangle size={18} />Sprawdź braki</button>
        </section>

        <section className="dashboard-card dashboard-card--shortcuts">
          <PanelTitle title="PRZYPIĘTE SKRÓTY" />
          {appState.routeTypes.slice(0, 4).map((type) => <button key={type} onClick={() => onOpenScheduleFilter({ type })}>{type}</button>)}
          <button onClick={() => onOpenScheduleFilter({ date: today, status: "P" })}>Aktywni dziś</button>
        </section>

        <section className="dashboard-card">
          <PanelTitle title="MOJE NOTATKI" />
          <div className="notes-input">
            <input value={noteText} onChange={(event) => setNoteText(event.target.value)} placeholder="Dodaj krótką notatkę..." />
            <button type="button" onClick={addNote}>Dodaj</button>
          </div>
          <div className="stack-list stack-list--notes">
            {notes.slice(0, 4).map((note, index) => (
              <article key={`${note}-${index}`}>
                <strong>{note}</strong>
                <button type="button" onClick={() => saveNotes(notes.filter((_, noteIndex) => noteIndex !== index))}>Usuń</button>
              </article>
            ))}
          </div>
        </section>

        <section className="dashboard-card dashboard-card--wide">
          <PanelTitle title="OSTATNIE DZIAŁANIA" />
          <div className="recent-actions">
            {recentArchive.map((item) => (
              <button key={item.id} onClick={() => onNavigate("archiwum")}>
                <span>{item.category}</span>
                <b>{item.title}</b>
                <small>{item.owner}</small>
                <time>{item.archivedAt}</time>
              </button>
            ))}
            {recentArchive.length === 0 && <p className="muted-line">Brak Twoich ostatnich dziaĹ‚aĹ„.</p>}
          </div>
        </section>
      </div>
    </section>
  );
}

function FullScheduleVbaView({
  appState,
  carrierName,
  driverName,
  routeName,
  onAppStateChange,
  onClose,
  user,
  initialFilter,
  onFilterConsumed
}: {
  appState: AppState;
  carrierName: (id: string) => string;
  driverName: (id?: string) => string;
  routeName: (id: string) => string;
  onAppStateChange: (updater: (current: AppState) => AppState) => void;
  onClose: () => void;
  user: SessionUser;
  initialFilter?: { driverId?: string; type?: string; date?: string; status?: string } | null;
  onFilterConsumed?: () => void;
}) {
  type ScheduleMode = "day" | "week" | "month";
  const groups = appState.routeTypes.length ? appState.routeTypes : ["Food", "Dedyk", "Food Bus"];
  const [search, setSearch] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [viewMode, setViewMode] = useState<ScheduleMode>("day");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState("WSZYSTKIE");
  const [statusFilter, setStatusFilter] = useState("WSZYSTKIE");
  const [driverFilter, setDriverFilter] = useState(initialFilter?.driverId ?? "WSZYSCY");
  const selected = new Date(`${selectedDate}T12:00:00`);

  useEffect(() => {
    if (!initialFilter) return;
    setDriverFilter(initialFilter.driverId ?? "WSZYSCY");
    setTypeFilter(initialFilter.type ?? "WSZYSTKIE");
    setStatusFilter(initialFilter.status ?? "WSZYSTKIE");
    if (initialFilter.date) {
      setSelectedDate(initialFilter.date);
      setViewMode("day");
    }
    onFilterConsumed?.();
  }, [initialFilter, onFilterConsumed]);

  function rangeForMode() {
    if (Number.isNaN(selected.getTime())) return [selectedDate];
    if (viewMode === "day") return [selectedDate];
    if (viewMode === "week") {
      const start = new Date(selected);
      const day = (start.getDay() + 6) % 7;
      start.setDate(start.getDate() - day);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return dateRange(start.toISOString().slice(0, 10), end.toISOString().slice(0, 10));
    }
    const start = new Date(selected.getFullYear(), selected.getMonth(), 1, 12);
    const end = new Date(selected.getFullYear(), selected.getMonth() + 1, 0, 12);
    return dateRange(start.toISOString().slice(0, 10), end.toISOString().slice(0, 10));
  }

  const visibleDates = rangeForMode();
  const startDate = visibleDates[0] ?? selectedDate;
  const endDate = visibleDates[visibleDates.length - 1] ?? selectedDate;
  const driversVisibleToUser = user.role === "przewoznik" && user.carrierId
    ? appState.drivers.filter((driver) => driver.carrierId === user.carrierId)
    : appState.drivers;
  const activeDriversVisibleToUser = driversVisibleToUser.filter(isDriverActive);
  const filteredDrivers = activeDriversVisibleToUser.filter((driver) =>
    (driver.name.toLowerCase().includes(search.toLowerCase()) ||
    carrierName(driver.carrierId).toLowerCase().includes(search.toLowerCase())) &&
    (typeFilter === "WSZYSTKIE" || sameScheduleType(driver.type, typeFilter)) &&
    (driverFilter === "WSZYSCY" || driver.id === driverFilter)
  );
  const selectedRangeLabel = `${startDate} - ${endDate}`;

  function routeForDriver(driver: Driver) {
    return findRouteForDriver(appState.routes, driver) ?? appState.routes[0];
  }

  function entryFor(driver: Driver, date: string) {
    return appState.schedule.find((entry) => {
      return entry.driverId === driver.id && entry.date === date;
    });
  }

  function displayEntryFor(driver: Driver, date: string) {
    return entryFor(driver, date) ?? syntheticShortageEntry(appState, driver, date);
  }

  function entryMatchesStatusFilter(entry: ScheduleEntry) {
    return scheduleStatusMatchesFilter(entry.status, statusFilter);
  }

  const rangeEntries = filteredDrivers
    .flatMap((driver) => visibleDates.map((date) => displayEntryFor(driver, date)))
    .filter(entryMatchesStatusFilter);
  const unfilteredRangeEntries = filteredDrivers.flatMap((driver) => visibleDates.map((date) => displayEntryFor(driver, date)));
  const visibleTableDrivers = filteredDrivers.filter((driver) => {
    return statusFilter === "WSZYSTKIE" || visibleDates.some((date) => entryMatchesStatusFilter(displayEntryFor(driver, date)));
  });
  const doneCount = unfilteredRangeEntries.filter((entry) => entry.status === "P").length;
  const plannedCount = unfilteredRangeEntries.filter((entry) => entry.status === "PLAN").length;
  const alertCount = unfilteredRangeEntries.filter((entry) => entry.status === "BRAK" || entry.status === "L4" || entry.status === "NIEOBECNY").length;
  const freeCount = unfilteredRangeEntries.filter((entry) => entry.status === "W" || entry.status === "U").length;
  const donutTotal = Math.max(1, unfilteredRangeEntries.length);
  const donePct = doneCount / donutTotal * 100;
  const plannedPct = plannedCount / donutTotal * 100;
  const alertPct = alertCount / donutTotal * 100;
  const donutStyle = {
    background: `radial-gradient(circle, #101824 0 42%, transparent 43%), conic-gradient(#58d77b 0 ${donePct}%, #ff9f33 ${donePct}% ${donePct + plannedPct}%, #ff6464 ${donePct + plannedPct}% ${donePct + plannedPct + alertPct}%, #1f4f82 ${donePct + plannedPct + alertPct}% 100%)`
  };

  function shiftRange(days: number) {
    const next = new Date(`${selectedDate}T12:00:00`);
    if (Number.isNaN(next.getTime())) return;
    if (viewMode === "month") next.setMonth(next.getMonth() + days);
    else next.setDate(next.getDate() + days);
    setSelectedDate(next.toISOString().slice(0, 10));
  }

  function exportCsv() {
    const exportedEntries = visibleTableDrivers
      .flatMap((driver) => visibleDates.map((date) => displayEntryFor(driver, date)))
      .filter(entryMatchesStatusFilter);
    const rows = viewMode === "day"
      ? [
          ["typ", "kierowca", "status", "awizacja od", "uwaga", "przewoznik"],
          ...exportedEntries.map((entry) => {
            return [entryScheduleType(appState, entry), driverName(entry.driverId), statusToLabel(entry.status), entry.awizacjaOd ?? entry.start, entryNoteText(entry), carrierName(entry.carrierId)];
          })
        ]
      : [
          ["kierowca", "typ", ...visibleDates],
          ...visibleTableDrivers.map((driver) => [
            driver.name,
            driver.type,
            ...visibleDates.map((date) => {
              const entry = displayEntryFor(driver, date);
              return entryMatchesStatusFilter(entry) ? `${statusShort(entry.status)}${hasEntryNote(entry) ? "*" : ""} ${entry.awizacjaOd ?? entry.start}` : "";
            })
          ])
        ];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sng-grafik-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function archiveSave() {
    onAppStateChange((current) => ({
      ...current,
      archive: [archiveItem("Grafik", `Zapis grafiku ${selectedRangeLabel}`, user.login), ...current.archive]
    }));
  }

  function parseTextRows(text: string) {
    return text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => line.split(line.includes("\t") ? "\t" : line.includes(";") ? ";" : ",").map((cell) => cell.trim()));
  }

  function importRows(rows: unknown[][]) {
    const [header, ...body] = rows.filter((row) => row.some((cell) => String(cell ?? "").trim()));
    if (!header || header.length < 2) {
      window.alert("Nie rozpoznano tabeli. Pierwsza kolumna musi być kierowcą, kolejne datami.");
      return;
    }

    const dates = header.slice(1).map(toIsoDate);

    onAppStateChange((current) => {
      let drivers = [...current.drivers];
      const matchableDrivers = () => user.role === "przewoznik" && user.carrierId
        ? drivers.filter((candidate) => candidate.carrierId === user.carrierId)
        : drivers;
      const newEntries: ScheduleEntry[] = [];

      body.forEach((row) => {
        const importedName = String(row[0] ?? "").trim();
        if (!importedName) return;

        let driver = matchableDrivers().find((candidate) => driverMatchesImportName(candidate, importedName));
        if (!driver) {
          const inferredType =
            current.routeTypes.find((type) => normalizePersonName(importedName).includes(normalizePersonName(type))) ??
            (typeFilter !== "WSZYSTKIE" ? typeFilter : current.routeTypes[0] ?? "Food");
          driver = {
            id: createId("drv"),
            name: importedName,
            type: inferredType,
            active: true,
            carrierId: user.role === "przewoznik" && user.carrierId ? user.carrierId : current.carriers[0]?.id ?? "",
            privatePhone: "",
            workPhone: "",
            phoneId: "",
            awizacja: "",
            vehicle: "",
            registration: "",
            pallets: 0,
            capacityKg: 0,
            city: "",
            postalCode: "",
            address: "",
            notes: "Dodany automatycznie z importu grafiku"
          };
          drivers = [driver, ...drivers];
        }

        const route = findRouteForDriver(current.routes, driver) ?? current.routes[0];
        if (!route) return;

        dates.forEach((date, index) => {
          if (!date) return;
          const mark = String(row[index + 1] ?? "").trim().toLowerCase();
          const works = ["x", "p", "1", "tak", "praca"].includes(mark);
          newEntries.push({
            id: createId("sch"),
            date,
            driverId: driver?.id,
            carrierId: driver?.carrierId ?? current.carriers[0]?.id ?? "",
            routeId: route.id,
            status: works ? "P" : "W",
            start: driver?.awizacjaOd1 || route.defaultStart,
            end: "",
            awizacjaOd: driver?.awizacjaOd1 || route.defaultStart,
            notes: "",
            createdBy: user.login
          });
        });
      });

      const importedDates = new Set(newEntries.map((entry) => entry.date));
      const importedDrivers = new Set(newEntries.map((entry) => entry.driverId));
      const nextSchedule = current.schedule.filter((entry) => {
        return !(importedDates.has(entry.date) && importedDrivers.has(entry.driverId));
      });

      return {
        ...current,
        drivers,
        schedule: [...newEntries, ...nextSchedule],
        archive: [archiveItem("Import grafiku", `Import ${newEntries.length} wpisów`, user.login, "Typ grafiku został pobrany z kart kierowców."), ...current.archive]
      };
    });
    setImportOpen(false);
    setImportText("");
  }

  async function importFile(file?: File) {
    if (!file) return;
    if (file.name.toLowerCase().endsWith(".xlsx")) {
      const rows = await readXlsxFile(file);
      importRows(rows);
      return;
    }

    const text = await file.text();
    importRows(parseTextRows(text));
  }

  return (
    <section className="vba-window vba-window--grafik">
      <div className="vba-titlebar">
        <CalendarDays size={17} />
        <strong>GRAFIK KIEROWCÓW</strong>
      </div>
      <div className="vba-toolbar">
        <button className="vba-nav-button" onClick={() => shiftRange(viewMode === "month" ? -1 : -7)}>‹</button>
        <span className="vba-range-label">{viewMode === "month" ? selected.toLocaleDateString("pl-PL", { month: "long", year: "numeric" }) : weekNumber(startDate)}</span>
        <button className="vba-nav-button" onClick={() => shiftRange(viewMode === "month" ? 1 : 7)}>›</button>
        <label className="vba-date-control">Data <input type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} /></label>
        <div className="mode-switch">
          <button className={clsx(viewMode === "day" && "is-active")} onClick={() => setViewMode("day")}>Dzień</button>
          <button className={clsx(viewMode === "week" && "is-active")} onClick={() => setViewMode("week")}>Tydzień</button>
          <button className={clsx(viewMode === "month" && "is-active")} onClick={() => setViewMode("month")}>Miesiąc</button>
        </div>
        <NeonSearchInput
          className="vba-search"
          value={search}
          placeholder="Szukaj kierowcy..."
          filterActive={filtersOpen}
          onChange={setSearch}
          onFilterClick={() => setFiltersOpen((value) => !value)}
        />
        <button onClick={() => setPreviewOpen(true)}>DRUKUJ</button>
        <button onClick={exportCsv}>EKSPORT</button>
        <button onClick={() => setImportOpen((value) => !value)}>IMPORT</button>
      </div>
      {filtersOpen && (
        <div className="vba-filter-panel">
          <label>Typ kierowcy
            <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
              <option>WSZYSTKIE</option>
              {groups.map((group) => <option key={group}>{group}</option>)}
            </select>
          </label>
          <label>Kierowca
            <select value={driverFilter} onChange={(event) => setDriverFilter(event.target.value)}>
              <option value="WSZYSCY">WSZYSCY</option>
              {activeDriversVisibleToUser.map((driver) => <option value={driver.id} key={driver.id}>{driver.name}</option>)}
            </select>
          </label>
          <label>Status
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="WSZYSTKIE">WSZYSTKIE</option>
              <option value="P">PRACA</option>
              <option value="PLAN">ZAPLANOWANE</option>
              <option value="WOLNE">WOLNE / URLOP</option>
              <option value="ALERT">BRAKI / L4 / NIEOBECNI</option>
            </select>
          </label>
        </div>
      )}
      {importOpen && (
        <div className="vba-import-panel">
          <div>
            <strong>Import z Excela</strong>
            <span>Obsługa `.xlsx`, CSV albo tekstu z Excela. Pierwsza kolumna: kierowca, kolejne: daty, `x` oznacza pracę, puste pole wolne. Typ bierze się z karty kierowcy.</span>
          </div>
          <label>Plik Excel/CSV
            <input type="file" accept=".xlsx,.csv,.txt,.tsv" onChange={(event) => importFile(event.target.files?.[0])} />
          </label>
          <textarea value={importText} onChange={(event) => setImportText(event.target.value)} placeholder={"Kierowca\t25.05.2026\t26.05.2026\nJakub Bla\tx\t"} />
          <button className="is-save" onClick={() => importRows(parseTextRows(importText))}>IMPORTUJ WKLEJONĄ TABELĘ</button>
        </div>
      )}
      <div className={clsx("schedule-manager", viewMode === "month" && "is-month")}>
        <div className="schedule-table">
          <div className="schedule-table__head" style={{ gridTemplateColumns: `220px repeat(${visibleDates.length}, minmax(${viewMode === "month" ? 54 : 92}px, 1fr)) 110px` }}>
            <span>Kierowca</span>
            {visibleDates.map((date) => <span key={date}>{date.slice(8)}<small>{dayName(date).slice(0, 3)}</small></span>)}
            <span>Podsum.</span>
          </div>
          {visibleTableDrivers.map((driver) => {
            const entries = visibleDates.map((date) => displayEntryFor(driver, date));
            return (
              <div className="schedule-table__row" style={{ gridTemplateColumns: `220px repeat(${visibleDates.length}, minmax(${viewMode === "month" ? 54 : 92}px, 1fr)) 110px` }} key={driver.id}>
                <div className="schedule-driver">
                  <DriverAvatar driver={driver} />
                  <div><strong>{driver.name}</strong><span>{carrierName(driver.carrierId)}</span><small>{driver.type}</small></div>
                </div>
                {visibleDates.map((date) => {
                  const entry = displayEntryFor(driver, date);
                  const status = entry.status;
                  const matchesFilter = entryMatchesStatusFilter(entry);
                  return (
                    <button
                      className={clsx("schedule-cell", `is-${status.toLowerCase()}`, !matchesFilter && statusFilter !== "WSZYSTKIE" && "is-muted-filtered")}
                      key={`${driver.id}-${date}`}
                      title={hasEntryNote(entry) ? entryNoteText(entry) : undefined}
                      onClick={() => {
                        setSelectedDate(date);
                        if (hasEntryNote(entry)) {
                          window.alert(`Uwagi do wpisu\n${driver.name} | ${date}\n\n${entryNoteText(entry)}`);
                        }
                      }}
                    >
                      <strong>{viewMode === "month" ? statusShort(status) : statusToLabel(status)}{hasEntryNote(entry) ? " *" : ""}</strong>
                      {viewMode !== "month" && <small>{entry.awizacjaOd ?? entry.start ?? driver.awizacjaOd1 ?? "-"}</small>}
                      {hasEntryNote(entry) && <em title={entryNoteText(entry)}>Uwaga</em>}
                    </button>
                  );
                })}
                <div className="schedule-week-total">
                  <span>{entries.filter((entry) => entry?.status === "P").length}</span>
                  <span>{entries.filter((entry) => entry?.status === "W" || entry?.status === "U").length}</span>
                  <span>{entries.filter((entry) => entry?.status === "BRAK").length}</span>
                </div>
              </div>
            );
          })}
        </div>
        <aside className="schedule-side-summary">
          <PanelTitle title={viewMode === "month" ? "PODSUMOWANIE MIESIĄCA" : viewMode === "day" ? "PODSUMOWANIE DNIA" : "PODSUMOWANIE TYGODNIA"} />
          <div className="summary-donut summary-donut--interactive" style={donutStyle}>
            <strong>{unfilteredRangeEntries.length}</strong>
            <span>wpisów</span>
          </div>
          <div className="status-filter-dots" aria-label="Filtry statusów">
            <button
              className={clsx("status-filter-dot is-work", statusFilter === "P" && "is-active")}
              type="button"
              title="Praca - pokaż tylko osoby pracujące"
              onClick={() => setStatusFilter(statusFilter === "P" ? "WSZYSTKIE" : "P")}
            >
              <span>{doneCount}</span>
            </button>
            <button
              className={clsx("status-filter-dot is-plan", statusFilter === "PLAN" && "is-active")}
              type="button"
              title="Zaplanowane - pokaż wpisy planowane"
              onClick={() => setStatusFilter(statusFilter === "PLAN" ? "WSZYSTKIE" : "PLAN")}
            >
              <span>{plannedCount}</span>
            </button>
            <button
              className={clsx("status-filter-dot is-alert", statusFilter === "ALERT" && "is-active")}
              type="button"
              title="Braki / L4 / nieobecni - pokaż tylko problemy"
              onClick={() => setStatusFilter(statusFilter === "ALERT" ? "WSZYSTKIE" : "ALERT")}
            >
              <span>{alertCount}</span>
            </button>
            <button
              className={clsx("status-filter-dot is-free", statusFilter === "WOLNE" && "is-active")}
              type="button"
              title="Wolne / urlop - pokaż dni wolne"
              onClick={() => setStatusFilter(statusFilter === "WOLNE" ? "WSZYSTKIE" : "WOLNE")}
            >
              <span>{freeCount}</span>
            </button>
          </div>
          <PanelTitle title="NAJWIĘKSZE BRAKI" />
          <div className="shortage-side-list">
            {rangeEntries.filter((entry) => entry.status === "BRAK").slice(0, 5).map((entry) => (
              <button key={entry.id}>{entry.date} · {entryDriverLabel(appState, entry, driverName(entry.driverId))} · {carrierName(entry.carrierId)}</button>
            ))}
          </div>
          <PanelTitle title="SZYBKIE AKCJE" />
          <div className="schedule-quick-actions">
            <button onClick={archiveSave}>Zapisz</button>
            <button onClick={() => setPreviewOpen(true)}>Drukuj</button>
            <button onClick={exportCsv}>Eksportuj</button>
          </div>
        </aside>
      </div>
      <div className="vba-summary-row">
        {groups.map((group) => {
          const groupEntries = rangeEntries.filter((entry) => sameScheduleType(entryScheduleType(appState, entry), group));
          const groupDriversCount = visibleTableDrivers.filter((driver) => sameScheduleType(driver.type, group)).length;
          return (
          <article key={group}>
            <strong>Podsumowanie: {group}</strong>
            <span>{groupDriversCount} kierowców</span>
            <span>{groupEntries.filter((entry) => entry.status === "P").length} praca</span>
            <span>{groupEntries.filter((entry) => entry.status === "W").length} wolne</span>
            <span>{groupEntries.filter((entry) => entry.status === "BRAK").length} braki</span>
          </article>
        );})}
      </div>
      <div className="vba-footer-actions">
        <button onClick={() => setPreviewOpen(true)}>DRUKUJ GRAFIK</button>
        <button onClick={() => setPreviewOpen(true)}>PODGLĄD</button>
        <button className="is-save" onClick={archiveSave}>ZAPISZ</button>
        <button className="is-danger" onClick={onClose}>ZAMKNIJ</button>
      </div>
      {previewOpen && (
        <PreviewModal title={`Podgląd wydruku grafiku ${selectedRangeLabel}`} onClose={() => setPreviewOpen(false)} onPrint={() => window.print()}>
          {viewMode === "day" ? (
            <DailyPrintSheet
              appState={appState}
              date={selectedDate}
              groups={groups}
              drivers={visibleTableDrivers}
              statusFilter={statusFilter}
              driverName={driverName}
              carrierName={carrierName}
            />
          ) : (
            <MonthlyPrintSheet appState={appState} dates={visibleDates} drivers={visibleTableDrivers} driverName={driverName} />
          )}
        </PreviewModal>
      )}
    </section>
  );
}

function DailyPrintSheet({
  appState,
  date,
  groups,
  drivers,
  statusFilter,
  driverName,
  carrierName
}: {
  appState: AppState;
  date: string;
  groups: string[];
  drivers: Driver[];
  statusFilter: string;
  driverName: (id?: string) => string;
  carrierName: (id: string) => string;
}) {
  const driverDayEntries = drivers
    .flatMap((driver) => appState.schedule.find((entry) => entry.driverId === driver.id && entry.date === date) ?? syntheticShortageEntry(appState, driver, date))
    .filter((entry) => entryHasActiveDriverOrOpenShortage(appState, entry))
    .filter((entry) => scheduleStatusMatchesFilter(entry.status, statusFilter));
  const openShortages = appState.schedule
    .filter((entry) => entry.date === date && !entry.driverId && entry.status === "BRAK")
    .filter((entry) => !isDeletedDriverScheduleEntry(entry))
    .filter((entry) => scheduleStatusMatchesFilter(entry.status, statusFilter));
  const dayEntries = [...driverDayEntries, ...openShortages];
  const count = (status: ScheduleEntry["status"]) => dayEntries.filter((entry) => entry.status === status).length;
  const printGroups = orderedScheduleGroups(groups);
  const rowsByGroup = new Map(
    printGroups.map((group) => [
      group,
      dayEntries.filter((entry) => sameScheduleType(entryScheduleType(appState, entry), group))
    ])
  );
  const rowCount = Math.max(8, ...printGroups.map((group) => rowsByGroup.get(group)?.length ?? 0));
  const rowIndexes = Array.from({ length: rowCount }, (_, index) => index);

  function dailyDriverText(entry?: ScheduleEntry) {
    if (!entry) return "";
    return entryDriverLabel(appState, entry, driverName(entry.driverId));
  }

  function dailyDetailText(entry?: ScheduleEntry) {
    if (!entry) return "";
    const status = entry.status === "P" || entry.status === "PLAN" ? "" : statusShort(entry.status);
    const carrier = !entry.driverId ? carrierName(entry.carrierId) : "";
    return [entry.awizacjaOd ?? entry.start, status, entryNoteText(entry) !== "-" ? entryNoteText(entry) : "", carrier]
      .filter(Boolean)
      .join(", ");
  }

  return (
    <div className="print-preview-sheet print-preview-sheet--daily">
      <header className="print-brand-head">
        <div className="print-logo">SNG<span>Transport</span></div>
        <div>
          <h2>GRAFIK KIEROWCÓW - WYDRUK DZIENNY</h2>
          <h3>{date} ({dayName(date)})</h3>
        </div>
        <div className="print-counters">
          <span>Pracuje <b>{count("P")}</b></span>
          <span>Wolne <b>{count("W") + count("U")}</b></span>
          <span>L4 <b>{count("L4")}</b></span>
          <span>Braki <b>{count("BRAK")}</b></span>
        </div>
      </header>
      <table className="print-daily-grid">
        <thead>
          <tr>
            {printGroups.map((group) => <th colSpan={2} key={group}>{group}</th>)}
          </tr>
        </thead>
        <tbody>
          {rowIndexes.map((rowIndex) => (
            <tr key={rowIndex}>
              {printGroups.map((group) => {
                const entry = rowsByGroup.get(group)?.[rowIndex];
                return (
                  <Fragment key={`${group}-${rowIndex}`}>
                    <td>{dailyDriverText(entry)}</td>
                    <td>{dailyDetailText(entry)}</td>
                  </Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <PrintSummary appState={appState} entries={dayEntries} groups={printGroups} title="PODSUMOWANIE DNIA" />
    </div>
  );
}

function MonthlyPrintSheet({
  appState,
  dates,
  drivers,
  driverName
}: {
  appState: AppState;
  dates: string[];
  drivers: Driver[];
  driverName: (id?: string) => string;
}) {
  const entries = drivers.flatMap((driver) =>
    dates.map((date) => appState.schedule.find((entry) => entry.driverId === driver.id && entry.date === date) ?? syntheticShortageEntry(appState, driver, date))
  );

  return (
    <div className="print-preview-sheet print-preview-sheet--month">
      <header className="print-brand-head">
        <div className="print-logo">SNG<span>Transport</span></div>
        <div>
          <h2>GRAFIK KIEROWCÓW - WYDRUK {dates.length > 10 ? "MIESIĘCZNY" : "TYGODNIOWY"}</h2>
          <h3>{dates[0]} - {dates[dates.length - 1]}</h3>
        </div>
      </header>
      <table className="print-month-table">
        <thead>
          <tr><th>Lp.</th><th>Kierowca</th><th>Typ</th>{dates.map((date) => <th key={date}>{date.slice(8)}<br />{dayName(date).slice(0, 3)}</th>)}</tr>
        </thead>
        <tbody>
          {drivers.map((driver, index) => (
            <tr key={driver.id}>
              <td>{index + 1}</td>
              <td>{driverName(driver.id)}</td>
              <td>{driver.type}</td>
              {dates.map((date) => {
                const entry = entries.find((candidate) => candidate.driverId === driver.id && candidate.date === date);
                const compact = dates.length > 10;
                return <td key={`${driver.id}-${date}`}>{entry ? compact ? `${statusShort(entry.status)}${hasEntryNote(entry) ? "*" : ""}` : `${statusShort(entry.status)}${hasEntryNote(entry) ? "*" : ""} ${entry.awizacjaOd ?? entry.start ?? ""}` : ""}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <PrintSummary appState={appState} entries={entries} groups={appState.routeTypes} title={dates.length > 10 ? "PODSUMOWANIE MIESIĄCA" : "PODSUMOWANIE TYGODNIA"} />
    </div>
  );
}

function PrintSummary({ appState, entries, groups, title }: { appState: AppState; entries: ScheduleEntry[]; groups: string[]; title: string }) {
  return (
    <section className="print-summary-table">
      <h3>{title}</h3>
      <table>
        <thead><tr><th></th>{groups.map((group) => <th key={group}>{group.toUpperCase()}</th>)}<th>RAZEM</th></tr></thead>
        <tbody>
          {["P", "W", "L4", "BRAK"].map((status) => (
            <tr key={status}>
              <td>{statusToLabel(status as ScheduleEntry["status"])}</td>
              {groups.map((group) => {
                return <td key={group}>{entries.filter((entry) => sameScheduleType(entryScheduleType(appState, entry), group) && entry.status === status).length}</td>;
              })}
              <td>{entries.filter((entry) => entry.status === status).length}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function AddScheduleEntryVbaView({
  appState,
  carrierName,
  onAppStateChange,
  user
}: {
  appState: AppState;
  carrierName: (id: string) => string;
  onAppStateChange: (updater: (current: AppState) => AppState) => void;
  user: SessionUser;
}) {
  const firstDriver = appState.drivers.find(isDriverActive) ?? appState.drivers[0];
  const firstRoute = findRouteForDriver(appState.routes, firstDriver) ?? appState.routes[0];
  const [selectedDriverId, setSelectedDriverId] = useState(firstDriver?.id ?? "");
  const [selectedEntryId, setSelectedEntryId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [entryType, setEntryType] = useState(firstDriver?.type ?? firstRoute?.serviceType ?? appState.routeTypes[0] ?? "Food");
  const [status, setStatus] = useState<ScheduleEntry["status"]>("P");
  const [start, setStart] = useState(firstDriver?.awizacjaOd1 ?? firstRoute?.defaultStart ?? "06:00");
  const [notes, setNotes] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const selectedDriver = appState.drivers.find((driver) => driver.id === selectedDriverId) ?? firstDriver;
  const selectedRoute = findRouteForDriver(appState.routes, selectedDriver, entryType) ?? firstRoute;

  function chooseScheduleDriver(driverId: string) {
    const driver = appState.drivers.find((candidate) => candidate.id === driverId);
    const route = findRouteForDriver(appState.routes, driver);
    setSelectedDriverId(driverId);
    if (driver) {
      setEntryType(driver.type);
      setStart(driver.awizacjaOd1 ?? route?.defaultStart ?? start);
    }
  }

  function loadEntry(entry: ScheduleEntry) {
    const entryDriver = appState.drivers.find((driver) => driver.id === entry.driverId);
    setSelectedEntryId(entry.id);
    setSelectedDriverId(entry.driverId ?? "");
    setDate(entry.date);
    setDateTo(entry.dateTo ?? entry.date);
    setEntryType(entryDriver?.type ?? appState.routes.find((route) => route.id === entry.routeId)?.serviceType ?? entryType);
    setStatus(entry.status);
    setStart(entry.awizacjaOd ?? entry.start);
    setNotes(entry.notes);
  }

  function clearForm() {
    setSelectedEntryId("");
    setSelectedDriverId(firstDriver?.id ?? "");
    setDate(new Date().toISOString().slice(0, 10));
    setDateTo(new Date().toISOString().slice(0, 10));
    setEntryType(firstDriver?.type ?? firstRoute?.serviceType ?? appState.routeTypes[0] ?? "Food");
    setStatus("P");
    setStart(firstDriver?.awizacjaOd1 ?? firstRoute?.defaultStart ?? "06:00");
    setNotes("");
  }

  function saveEntry() {
    if (!selectedDriver && status !== "BRAK") {
      window.alert("Wybierz kierowcę albo ustaw status BRAK.");
      return;
    }

    if (!selectedRoute) {
      window.alert("Wybierz trasę.");
      return;
    }

    const carrierId = selectedDriver?.carrierId ?? appState.carriers[0]?.id ?? "";
    const entries: ScheduleEntry[] = dateRange(date, dateTo).map((rangeDate) => ({
      id: selectedEntryId && rangeDate === date ? selectedEntryId : createId("sch"),
      date: rangeDate,
      dateTo,
      driverId: selectedDriver?.id,
      carrierId,
      routeId: selectedRoute.id,
      status,
      start,
      end: "",
      awizacjaOd: start,
      notes: status === "BRAK"
        ? `${notes || "Brak wpisu w grafiku"} | Kierowca: ${selectedDriver?.name ?? "-"}`
        : notes,
      createdBy: user.login
    }));

    onAppStateChange((current) => ({
      ...current,
      schedule: selectedEntryId
        ? [...entries, ...current.schedule.filter((candidate) => candidate.id !== selectedEntryId)]
        : [...entries, ...current.schedule],
      archive: [
        archiveItem(
          "Grafik",
          `${selectedEntryId ? "Edycja" : "Dodanie"} wpisu ${date} - ${dateTo} - ${selectedDriver?.name ?? "brak kierowcy"}`,
          user.login
        ),
        ...current.archive
      ]
    }));
    setSelectedEntryId(entries[0]?.id ?? "");
  }

  function deleteEntry() {
    if (!selectedEntryId) {
      window.alert("Najpierw wybierz wpis z listy.");
      return;
    }

    onAppStateChange((current) => ({
      ...current,
      schedule: current.schedule.filter((entry) => entry.id !== selectedEntryId),
      archive: [archiveItem("Grafik", `Usunięcie wpisu ${selectedEntryId}`, "SNG"), ...current.archive]
    }));
    clearForm();
  }

  return (
    <section className="vba-window vba-two-pane">
      <div className="vba-titlebar">
        <CalendarDays size={18} />
        <strong>DODAJ / EDYTUJ WPIS W GRAFIKU</strong>
      </div>
      <div className="vba-two-pane__left">
        <PanelTitle icon={<UsersRound size={16} />} title="WYBÓR KIEROWCY" />
        <select value={selectedDriverId} onChange={(event) => chooseScheduleDriver(event.target.value)}>
          {appState.drivers.map((driver) => (
            <option value={driver.id} key={driver.id}>{driver.name}</option>
          ))}
        </select>
        <div className="vba-driver-list">
          {appState.drivers.map((driver) => (
            <article
              className={clsx(driver.id === selectedDriver?.id && "is-selected")}
              key={driver.id}
              onClick={() => chooseScheduleDriver(driver.id)}
            >
              <DriverAvatar driver={driver} />
              <div>
                <strong>{driver.name}</strong>
                <span>{driver.city}</span>
                <small>{carrierName(driver.carrierId)}</small>
              </div>
              <span className="type-pill">{driver.type}</span>
            </article>
          ))}
        </div>
      </div>
      <div className="vba-two-pane__main">
        <PanelTitle icon={<FileText size={16} />} title="SZCZEGÓŁY WPISU" />
        <div className="vba-form-grid">
          <label>Kierowca
            <select value={selectedDriverId} onChange={(event) => chooseScheduleDriver(event.target.value)}>
              {appState.drivers.map((driver) => <option value={driver.id} key={driver.id}>{driver.name.toUpperCase()}</option>)}
            </select>
          </label>
          <label>Typ
            <select
              value={entryType}
              onChange={(event) => {
                const route = findRouteByType(appState.routes, event.target.value);
                setEntryType(event.target.value);
                if (route) {
                  setStart(route.defaultStart);
                }
              }}
            >
              {appState.routeTypes.map((type) => <option value={type} key={type}>{type}</option>)}
            </select>
          </label>
          <label>Data od <input type="date" value={date} onChange={(event) => { setDate(event.target.value); if (dateTo < event.target.value) setDateTo(event.target.value); }} /></label>
          <label>Data do <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
          <label>Dzień startu <input value={dayName(date)} readOnly /></label>
          <label>Status
            <select value={status} onChange={(event) => setStatus(event.target.value as ScheduleEntry["status"])}>
              <option value="P">Praca</option>
              <option value="PLAN">Plan</option>
              <option value="BRAK">Brak kierowcy</option>
              <option value="W">Wolne</option>
              <option value="U">Urlop</option>
              <option value="L4">L4</option>
              <option value="NIEOBECNY">Nieobecny</option>
            </select>
          </label>
          <label>Autor wpisu <input value={selectedEntryId ? appState.schedule.find((entry) => entry.id === selectedEntryId)?.createdBy ?? "brak danych" : user.login} readOnly /></label>
          <label>Awizacja od <input type="time" value={start} onChange={(event) => setStart(event.target.value)} /></label>
          <label className="span-2">Uwaga <textarea value={notes} placeholder="Wpisz uwagi dotyczące tego dnia..." onChange={(event) => setNotes(event.target.value)} /></label>
        </div>
        <PanelTitle icon={<CheckCircle2 size={16} />} title="PODGLĄD WPISU" />
        <div className="vba-entry-preview">
          <strong>{date.slice(-2)}</strong>
          <span>{date.slice(5, 7)} {date.slice(0, 4)}</span>
          <div>
            <b>{statusToLabel(status)}</b>
            <p>{selectedDriver?.name.toUpperCase() ?? "BRAK KIEROWCY"}</p>
            <small>{weekNumber(date)} · {date} - {dateTo}</small>
          </div>
          <Truck size={38} />
        </div>
        <PanelTitle title="WPISY W GRAFIKU" />
        <div className="vba-list-box vba-list-box--compact">
          {appState.schedule.map((entry) => (
            <button className={clsx(entry.id === selectedEntryId && "is-active")} type="button" key={entry.id} onClick={() => loadEntry(entry)}>
              {entry.date}{entry.dateTo && entry.dateTo !== entry.date ? `-${entry.dateTo}` : ""} | {appState.drivers.find((driver) => driver.id === entry.driverId)?.name ?? "Brak kierowcy"} | {entryScheduleType(appState, entry)} | {statusToLabel(entry.status)} | {entry.createdBy ?? "-"}
            </button>
          ))}
        </div>
        <div className="vba-footer-actions">
          <button className="is-save" onClick={saveEntry}>ZAPISZ</button>
          <button onClick={clearForm}>WYCZYŚĆ</button>
          <button onClick={() => setPreviewOpen(true)}>PODGLĄD</button>
          <button className="is-danger" onClick={deleteEntry}>USUŃ WPIS</button>
        </div>
        {previewOpen && (
          <PreviewModal title="Podgląd wpisu w grafiku" onClose={() => setPreviewOpen(false)} onPrint={() => window.print()}>
            <div className="print-preview-sheet">
              <h2>{selectedDriver?.name ?? "Brak kierowcy"}</h2>
              <p>Data: {date} ({dayName(date)})</p>
              <p>Zakres: {date} - {dateTo}</p>
              <p>Typ: {entryType}</p>
              <p>Status: {statusToLabel(status)}</p>
              <p>Awizacja od: {start}</p>
              <p>Uwagi: {notes || "-"}</p>
              <p>Autor: {user.login}</p>
            </div>
          </PreviewModal>
        )}
      </div>
    </section>
  );
}

function AddRouteVbaView({
  appState,
  carrierName,
  onAppStateChange,
  onOpenDrivers,
  initialRouteId,
  lockedDriverId,
  onDoneEdit
}: {
  appState: AppState;
  carrierName: (id: string) => string;
  onAppStateChange: (updater: (current: AppState) => AppState) => void;
  onOpenDrivers: () => void;
  initialRouteId?: string;
  lockedDriverId?: string;
  onDoneEdit?: () => void;
}) {
  const routeToEdit = appState.routes.find((route) => route.id === initialRouteId);
  const activeDrivers = appState.drivers.filter(isDriverActive);
  const firstDriver = appState.drivers.find((driver) => driver.id === (lockedDriverId ?? routeToEdit?.driverId)) ?? activeDrivers[0] ?? appState.drivers[0];
  const visibleDrivers = lockedDriverId ? appState.drivers.filter((driver) => driver.id === lockedDriverId) : activeDrivers;
  const [selectedDriverId, setSelectedDriverId] = useState(lockedDriverId ?? routeToEdit?.driverId ?? firstDriver?.id ?? "");
  const [routeIdText, setRouteIdText] = useState(routeToEdit?.id.toUpperCase() ?? `T${String(appState.routes.length + 1).padStart(3, "0")}`);
  const [routeNo, setRouteNo] = useState(`${String(appState.routes.length + 1).padStart(3, "0")}/05/2026`);
  const [name, setName] = useState(routeToEdit?.name ?? "");
  const [nameEdited, setNameEdited] = useState(Boolean(routeToEdit?.name));
  const [start, setStart] = useState(routeToEdit?.defaultStart ?? firstDriver?.awizacjaOd1 ?? "");
  const [end, setEnd] = useState(routeToEdit?.defaultEnd ?? "");
  const [serviceType, setServiceType] = useState<DriverType>(routeToEdit?.serviceType ?? firstDriver?.type ?? appState.routeTypes[0] ?? "Food");
  const [notes, setNotes] = useState(routeToEdit?.notes ?? "");
  const [point, setPoint] = useState<RoutePoint>({ shopNo: "", city: "", code: "", type: appState.pointTypes[0] ?? "Sklep", address: "", notes: "" });
  const [routePoints, setRoutePoints] = useState<RoutePoint[]>(withWarehouse(routeToEdit?.points?.length ? routeToEdit.points : [warehousePoint]));
  const [previewOpen, setPreviewOpen] = useState(false);
  const selectedDriver = appState.drivers.find((driver) => driver.id === selectedDriverId) ?? firstDriver;
  const mapsUrl = googleRouteUrls(routePoints).openUrl;

  function updatePoint(field: keyof RoutePoint, value: string) {
    setPoint((current) => ({ ...current, [field]: value }));
  }

  function addPoint() {
    if (!point.city.trim()) {
      window.alert("Podaj miejscowość punktu.");
      return;
    }

    setRoutePoints((current) => {
      const nextShopNo = point.shopNo || String(100 + current.filter((routePoint) => !isWarehousePoint(routePoint)).length + 1);
      const next = [...current, { ...point, shopNo: nextShopNo }];
      if (!nameEdited) {
        setName(routeNameFromPoints(next));
      }
      return next;
    });
    setPoint({ shopNo: "", city: "", code: "", type: appState.pointTypes[0] ?? "Sklep", address: "", notes: "" });
  }

  function removePoint(index: number) {
    if (index === 0) {
      window.alert("Magazyn jest stałym miejscem załadunku i zostaje pierwszym punktem trasy.");
      return;
    }

    setRoutePoints((current) => {
      const next = current.filter((_, pointIndex) => pointIndex !== index);
      if (!nameEdited) setName(routeNameFromPoints(next));
      return next;
    });
  }

  function movePoint(index: number, direction: -1 | 1) {
    setRoutePoints((current) => {
      const targetIndex = index + direction;
      if (index === 0 || targetIndex <= 0 || targetIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      if (!nameEdited) setName(routeNameFromPoints(next));
      return next;
    });
  }

  function clearRoute() {
    setRouteIdText(`T${String(appState.routes.length + 2).padStart(3, "0")}`);
    setRouteNo(`${String(appState.routes.length + 2).padStart(3, "0")}/05/2026`);
    setName("");
    setNameEdited(false);
    setStart(firstDriver?.awizacjaOd1 ?? "");
    setEnd("");
    setServiceType(firstDriver?.type ?? appState.routeTypes[0] ?? "Food");
    setNotes("");
    setRoutePoints([warehousePoint]);
  }

  function chooseRouteDriver(driverId: string) {
    const driver = appState.drivers.find((candidate) => candidate.id === driverId);
    setSelectedDriverId(driverId);
    if (!driver) return;
    setServiceType(driver.type);
    if (!routeToEdit) {
      setStart(driver.awizacjaOd1 ?? "");
    }
  }

  function saveRoute(clearAfter = false) {
    const pointsWithWarehouse = withWarehouse(routePoints);
    const generatedName = routeNameFromPoints(pointsWithWarehouse);
    const finalName = name.trim() || generatedName;
    if (!finalName) {
      window.alert("Dodaj przynajmniej jeden punkt trasy z miejscowością.");
      return;
    }

    const route: RoutePlan = {
      id: createId("rou"),
      name: finalName,
      cityFrom: pointsWithWarehouse[0]?.city || "Start",
      cityTo: pointsWithWarehouse[pointsWithWarehouse.length - 1]?.city || "Koniec",
      defaultStart: start,
      defaultEnd: end,
      serviceType,
      requiredDrivers: 1,
      driverId: selectedDriverId,
      points: pointsWithWarehouse,
      notes
    };

    onAppStateChange((current) => ({
      ...current,
      routes: routeToEdit
        ? current.routes.map((candidate) => candidate.id === routeToEdit.id ? { ...route, id: routeToEdit.id } : candidate)
        : [route, ...current.routes],
      archive: [archiveItem("Trasy", `${routeToEdit ? "Edycja" : "Dodanie"} trasy ${routeIdText} / ${route.name}`, "SNG", `Kierowca: ${selectedDriver?.name ?? "-"}`), ...current.archive]
    }));

    if (clearAfter) {
      clearRoute();
    } else {
      onDoneEdit?.();
    }
  }

  return (
    <section className="vba-window vba-route-builder">
      <div className="vba-titlebar">
        <RouteIcon size={18} />
        <strong>DODAJ TRASĘ</strong>
      </div>
      <aside className="vba-route-sidebar">
        <select value={selectedDriverId} onChange={(event) => chooseRouteDriver(event.target.value)} disabled={Boolean(lockedDriverId)}>
          {visibleDrivers.map((driver) => (
            <option value={driver.id} key={driver.id}>{driver.name}</option>
          ))}
        </select>
        {visibleDrivers.map((driver) => (
          <article className={clsx(driver.id === selectedDriver?.id && "is-selected")} key={driver.id} onClick={() => chooseRouteDriver(driver.id)}>
            <DriverAvatar driver={driver} />
            <div>
              <strong>{driver.name}</strong>
              <span>{carrierName(driver.carrierId)}</span>
            </div>
            <span className="type-pill">{driver.type}</span>
          </article>
        ))}
        <button onClick={onOpenDrivers}>Dodaj kierowcę</button>
      </aside>
      <div className="vba-route-main">
        <PanelTitle icon={<RouteIcon size={16} />} title="DANE TRASY" />
        <div className="vba-form-grid vba-form-grid--route">
          <label>Kierowca <select value={selectedDriverId} onChange={(event) => chooseRouteDriver(event.target.value)} disabled={Boolean(lockedDriverId)}>{visibleDrivers.map((driver) => <option value={driver.id} key={driver.id}>{driver.name}</option>)}</select></label>
          <label>ID trasy <input value={routeIdText} onChange={(event) => setRouteIdText(event.target.value)} /></label>
          <label>Nr trasy <input value={routeNo} onChange={(event) => setRouteNo(event.target.value)} /></label>
          <label>Nazwa trasy <input value={name} onChange={(event) => { setNameEdited(true); setName(event.target.value); }} /></label>
          <label>Awizacja od <input type="time" value={start} onChange={(event) => setStart(event.target.value)} /></label>
          <label>Typ <select value={serviceType} onChange={(event) => setServiceType(event.target.value)}>{appState.routeTypes.map((type) => <option value={type} key={type}>{type}</option>)}</select></label>
          <label className="span-3">Uwagi <input value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
        </div>
        <PanelTitle icon={<RouteIcon size={16} />} title="PUNKTY TRASY" />
        <div className="vba-form-grid vba-form-grid--route">
          <label>Nr sklepu <input value={point.shopNo} placeholder="np. 101" onChange={(event) => updatePoint("shopNo", event.target.value)} /></label>
          <label>Miejscowość <input value={point.city} placeholder="np. Warszawa" onChange={(event) => updatePoint("city", event.target.value)} /></label>
          <label>Kod <input value={point.code} placeholder="np. 00-001" onChange={(event) => updatePoint("code", event.target.value)} /></label>
          <label>Typ punktu <select value={point.type} onChange={(event) => updatePoint("type", event.target.value)}>{appState.pointTypes.map((type) => <option value={type} key={type}>{type}</option>)}</select></label>
          <label className="span-2">Adres sklepu <input value={point.address} placeholder="ul. Przykładowa 1" onChange={(event) => updatePoint("address", event.target.value)} /></label>
          <label className="span-2">Uwagi punktu <input value={point.notes} placeholder="np. Dostawa od 06:00" onChange={(event) => updatePoint("notes", event.target.value)} /></label>
          <button className="is-save" onClick={addPoint}>DODAJ PUNKT</button>
        </div>
        <div className="vba-route-content">
          <div className="vba-points-table">
            <header>PUNKTY DODANE DO TRASY</header>
            {routePoints.map((routePoint, index) => (
              <div key={`${routePoint.city}-${index}`}>
                <span>{index + 1}</span>
                <span>{routePoint.shopNo}</span>
                <span>{routePoint.city}</span>
                <span>{routePoint.type}</span>
                <span>{routePoint.notes}</span>
                <span className="row-actions">
                  {index === 0 ? (
                    <small>Miejsce załadunku</small>
                  ) : (
                    <>
                      <button onClick={() => movePoint(index, -1)}>↑</button>
                      <button onClick={() => movePoint(index, 1)}>↓</button>
                      <button className="is-danger" onClick={() => removePoint(index)}>×</button>
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
          <div className="vba-map-card vba-map-card--google">
            <GoogleRouteMap points={routePoints} />
          </div>
        </div>
        <div className="vba-footer-actions">
          <button className="is-danger" onClick={clearRoute}>ANULUJ</button>
          <button onClick={() => saveRoute(true)}>ZAPISZ I DODAJ KOLEJNĄ</button>
          <button onClick={() => setPreviewOpen(true)}>PODGLĄD</button>
          <button className="is-save" onClick={() => saveRoute(false)}>ZAPISZ TRASĘ</button>
        </div>
        {previewOpen && (
          <PreviewModal title="Podgląd trasy" onClose={() => setPreviewOpen(false)} onPrint={() => window.print()}>
            <div className="print-preview-sheet">
              <h2>{name}</h2>
              <p>Kierowca: {selectedDriver?.name ?? "-"}</p>
              <p>Typ: {serviceType} | Awizacja od: {start}</p>
              <ol>
                {routePoints.map((routePoint, index) => (
                  <li key={`${routePoint.city}-preview-${index}`}>{routePoint.city} - {routePoint.address || routePoint.type} - {routePoint.notes}</li>
                ))}
              </ol>
              <p>
                <a href={mapsUrl} target="_blank" rel="noreferrer">
                  Otwórz trasę w Google Maps
                </a>
              </p>
            </div>
          </PreviewModal>
        )}
      </div>
    </section>
  );
}

function SearchDriverVbaView({
  appState,
  carrierName,
  user,
  onAppStateChange,
  onOpenSchedule,
  onOpenRoute
}: {
  appState: AppState;
  carrierName: (id: string) => string;
  user: SessionUser;
  onAppStateChange: (updater: (current: AppState) => AppState) => void;
  onOpenSchedule: (driverId: string) => void;
  onOpenRoute: (routeId: string, driverId: string) => void;
}) {
  const allowedCarrierId = user.role === "przewoznik" ? user.carrierId : undefined;
  const driversAllowedForUser = appState.drivers.filter((driver) => !allowedCarrierId || driver.carrierId === allowedCarrierId);
  const emptyDriver = (): Driver => ({
    id: "",
    name: "",
    type: appState.routeTypes[0] ?? "Food",
    active: true,
    carrierId: allowedCarrierId ?? appState.carriers[0]?.id ?? "",
    vehicleId: "",
    privatePhone: "",
    workPhone: "",
    phoneId: "",
    awizacja: "",
    vehicle: "",
    registration: "",
    pallets: 0,
    capacityKg: 0,
    city: "",
    postalCode: "",
    address: "",
    notes: ""
  });
  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [typeFilter, setTypeFilter] = useState<"WSZYSCY" | DriverType>("WSZYSCY");
  const [carrierFilter, setCarrierFilter] = useState(allowedCarrierId ?? "WSZYSCY");
  const [activeFilter, setActiveFilter] = useState<"WSZYSCY" | "TAK" | "NIE">("TAK");
  const [selectedDriverId, setSelectedDriverId] = useState(driversAllowedForUser[0]?.id ?? "");
  const [editMode, setEditMode] = useState(false);
  const [routePreview, setRoutePreview] = useState<RoutePlan | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const selectedDriver = driversAllowedForUser.find((driver) => driver.id === selectedDriverId) ?? driversAllowedForUser[0];
  const visibleCarriersInSearch = allowedCarrierId ? appState.carriers.filter((carrier) => carrier.id === allowedCarrierId) : appState.carriers;
  const [draft, setDraft] = useState<Driver>(selectedDriver ?? emptyDriver);
  const selectedDraftVehicle = appState.vehicles.find((vehicle) => vehicle.id === draft.vehicleId);
  const vehiclesForDraftCarrier = appState.vehicles.filter((vehicle) => vehicle.carrierId === draft.carrierId && isVehicleActive(vehicle));
  const filteredDrivers = driversAllowedForUser.filter((driver) => {
    const vehicle = appState.vehicles.find((candidate) => candidate.id === driver.vehicleId);
    const matchesSearch = `${driver.name} ${carrierName(driver.carrierId)} ${driver.vehicle} ${driver.registration} ${vehicleDisplayName(vehicle)}`.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === "WSZYSCY" || sameScheduleType(driver.type, typeFilter);
    const matchesCarrier = carrierFilter === "WSZYSCY" || driver.carrierId === carrierFilter;
    const matchesActive = activeFilter === "WSZYSCY" || (activeFilter === "TAK" ? isDriverActive(driver) : !isDriverActive(driver));
    return matchesSearch && matchesType && matchesCarrier && matchesActive;
  });

  function selectDriver(driver: Driver) {
    setSelectedDriverId(driver.id);
    setDraft(driver);
    setEditMode(false);
  }

  function startAddDriver() {
    const next = emptyDriver();
    setSelectedDriverId("");
    setDraft(next);
    setEditMode(true);
  }

  function updateDraft<K extends keyof Driver>(field: K, value: Driver[K]) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  function updateDraftCarrier(carrierId: string) {
    setDraft((current) => ({
      ...current,
      carrierId,
      vehicleId: "",
      vehicle: "",
      registration: "",
      pallets: 0,
      capacityKg: 0
    }));
  }

  function applyVehicleToDraft(vehicleId: string) {
    const vehicle = appState.vehicles.find((candidate) => candidate.id === vehicleId);
    setDraft((current) =>
      vehicle
        ? {
            ...current,
            vehicleId: vehicle.id,
            vehicle: vehicle.model,
            registration: vehicle.registration,
            pallets: vehicle.pallets,
            capacityKg: vehicle.loadKg
          }
        : { ...current, vehicleId: "", vehicle: "", registration: "", pallets: 0, capacityKg: 0 }
    );
  }

  function saveDriver() {
    if (!draft.name.trim()) {
      window.alert("Podaj imię i nazwisko kierowcy.");
      return;
    }

    const selectedVehicle = appState.vehicles.find((vehicle) => vehicle.id === draft.vehicleId);
    const driverToSave: Driver = {
      ...draft,
      id: draft.id || createId("drv"),
      name: draft.name.trim(),
      vehicle: selectedVehicle?.model || draft.vehicle,
      registration: selectedVehicle?.registration || draft.registration,
      pallets: Number(selectedVehicle?.pallets ?? draft.pallets) || 0,
      capacityKg: Number(selectedVehicle?.loadKg ?? draft.capacityKg) || 0
    };

    onAppStateChange((current) => {
      const routeForSavedDriver = findRouteForDriver(current.routes, driverToSave);
      return {
        ...current,
        drivers: draft.id
          ? current.drivers.map((driver) => (driver.id === draft.id ? driverToSave : driver))
          : [driverToSave, ...current.drivers],
        schedule: routeForSavedDriver
          ? current.schedule.map((entry) => entry.driverId === driverToSave.id ? { ...entry, routeId: routeForSavedDriver.id } : entry)
          : current.schedule,
        archive: [
          archiveItem("Kierowcy", `${draft.id ? "Edycja" : "Dodanie"} kierowcy ${driverToSave.name}`, "SNG"),
          ...current.archive
        ]
      };
    });
    setSelectedDriverId(driverToSave.id);
    setDraft(driverToSave);
    setEditMode(false);
  }

  function deactivateDriver() {
    if (!canManagePeople(user, appState.customRoles)) {
      window.alert("Tylko kierownik albo administrator może dezaktywować kierowcę.");
      return;
    }
    if (!selectedDriver) return;

    onAppStateChange((current) => ({
      ...current,
      drivers: current.drivers.map((driver) =>
        driver.id === selectedDriver.id ? { ...driver, active: !isDriverActive(driver) } : driver
      ),
      archive: [
        archiveItem("Kierowcy", `${selectedDriver.active ? "Dezaktywacja" : "Aktywacja"} kierowcy ${selectedDriver.name}`, "SNG"),
        ...current.archive
      ]
    }));
  }

  function deleteDriver() {
    if (!canManagePeople(user, appState.customRoles)) {
      window.alert("Tylko kierownik albo administrator może usunąć kierowcę z bazy.");
      return;
    }
    if (!selectedDriver) return;
    if (!window.confirm(`Usunąć kierowcę ${selectedDriver.name}? Jego wpisy zostaną usunięte z grafiku i nie będą pokazywane jako braki.`)) return;

    onAppStateChange((current) => ({
      ...current,
      drivers: current.drivers.filter((driver) => driver.id !== selectedDriver.id),
      schedule: current.schedule.filter((entry) => entry.driverId !== selectedDriver.id),
      archive: [archiveItem("Kierowcy", `Usunięcie kierowcy ${selectedDriver.name}`, "SNG"), ...current.archive]
    }));
    const nextDriver = appState.drivers.find((driver) => driver.id !== selectedDriver.id);
    setSelectedDriverId(nextDriver?.id ?? "");
    setDraft(nextDriver ?? emptyDriver());
  }

  const displayedDriver = editMode ? draft : selectedDriver;
  const driverSchedule = displayedDriver
    ? appState.schedule.filter((entry) => entry.driverId === displayedDriver.id).slice(0, 6)
    : [];
  const driverRoutes = displayedDriver ? appState.routes.filter((route) => route.driverId === displayedDriver.id) : [];

  function loadPhoto(file?: File) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => updateDraft("photoDataUrl", String(reader.result ?? ""));
    reader.readAsDataURL(file);
  }

  return (
    <section className="vba-window vba-search-driver">
      <div className={clsx("vba-filterbar", !filtersOpen && "is-collapsed")}>
        <NeonSearchInput
          value={search}
          placeholder="Szukaj kierowcy"
          filterActive={filtersOpen}
          onChange={setSearch}
          onFilterClick={() => setFiltersOpen((value) => !value)}
        />
        <label>Typ:
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value as "WSZYSCY" | DriverType)}>
            <option>WSZYSCY</option>{appState.routeTypes.map((type) => <option key={type}>{type}</option>)}
          </select>
        </label>
        <label>Przewoźnik:
          <select value={carrierFilter} onChange={(event) => setCarrierFilter(event.target.value)} disabled={Boolean(allowedCarrierId)}>
            {!allowedCarrierId && <option value="WSZYSCY">WSZYSCY</option>}
            {visibleCarriersInSearch.map((carrier) => <option value={carrier.id} key={carrier.id}>{carrier.name}</option>)}
          </select>
        </label>
        <label>Aktywny:
          <select value={activeFilter} onChange={(event) => setActiveFilter(event.target.value as "WSZYSCY" | "TAK" | "NIE")}>
            <option>TAK</option><option>NIE</option><option>WSZYSCY</option>
          </select>
        </label>
      </div>
      <div className="vba-search-layout">
        <section>
          <PanelTitle title={`LISTA KIEROWCÓW (${filteredDrivers.length})`} />
          <div className="vba-driver-list">
            {filteredDrivers.map((driver) => (
              <article className={clsx(driver.id === selectedDriver?.id && "is-selected")} key={driver.id} onClick={() => selectDriver(driver)}>
                <span className={clsx("status-dot", isDriverActive(driver) ? "is-online" : "is-offline")} />
                <DriverAvatar driver={driver} />
                <div>
                  <strong>{driver.name}</strong>
                  <span>{carrierName(driver.carrierId)}</span>
                </div>
                <span className="type-pill">{driver.type}</span>
              </article>
            ))}
          </div>
        </section>
        <section>
          <PanelTitle title="SZCZEGÓŁY KIEROWCY" />
          <div className="vba-driver-details">
            <div className="driver-photo-large" onDoubleClick={() => editMode && photoInputRef.current?.click()} title="Kliknij dwa razy, aby wybrać zdjęcie">
              {draft.photoDataUrl ? <img src={draft.photoDataUrl} alt="" /> : displayedDriver?.name?.slice(0, 1) || "+"}
            </div>
            <label>Imię i nazwisko:<input value={draft.name} onChange={(event) => updateDraft("name", event.target.value)} disabled={!editMode} /></label>
            <label>Typ:
              <select value={draft.type} onChange={(event) => updateDraft("type", event.target.value as DriverType)} disabled={!editMode}>
                {appState.routeTypes.map((type) => <option key={type}>{type}</option>)}
              </select>
            </label>
            <label>Przewoźnik:
              <select value={draft.carrierId} onChange={(event) => updateDraftCarrier(event.target.value)} disabled={!editMode || Boolean(allowedCarrierId)}>
                {visibleCarriersInSearch.map((carrier) => <option value={carrier.id} key={carrier.id}>{carrier.name}</option>)}
              </select>
            </label>
            <label>Pojazd przewoĹşnika:
              <select value={draft.vehicleId ?? ""} onChange={(event) => applyVehicleToDraft(event.target.value)} disabled={!editMode}>
                <option value="">Bez przypisanego pojazdu</option>
                {vehiclesForDraftCarrier.map((vehicle) => <option value={vehicle.id} key={vehicle.id}>{vehicleDisplayName(vehicle)}</option>)}
              </select>
            </label>
            <label>Tel. prywatny:<input value={draft.privatePhone} onChange={(event) => updateDraft("privatePhone", event.target.value)} disabled={!editMode} /></label>
            <label>Tel. służbowy:<input value={draft.workPhone} onChange={(event) => updateDraft("workPhone", event.target.value)} disabled={!editMode} /></label>
            <label>Auto:<input value={selectedDraftVehicle?.model ?? draft.vehicle} onChange={(event) => updateDraft("vehicle", event.target.value)} disabled={!editMode || Boolean(selectedDraftVehicle)} /></label>
            <label>Rejestracja:<input value={selectedDraftVehicle?.registration ?? draft.registration} onChange={(event) => updateDraft("registration", event.target.value)} disabled={!editMode || Boolean(selectedDraftVehicle)} /></label>
            <label>Palety:<input value={draft.pallets} onChange={(event) => updateDraft("pallets", Number(event.target.value) as Driver["pallets"])} disabled={!editMode} /></label>
            <label>Udźwig kg:<input value={draft.capacityKg} onChange={(event) => updateDraft("capacityKg", Number(event.target.value) as Driver["capacityKg"])} disabled={!editMode} /></label>
            <label>Miasto:<input value={draft.city} onChange={(event) => updateDraft("city", event.target.value)} disabled={!editMode} /></label>
            <label>Kod:<input value={draft.postalCode} onChange={(event) => updateDraft("postalCode", event.target.value)} disabled={!editMode} /></label>
            <label className="active-check-row">Status:
              <ActiveCheck
                checked={draft.active}
                disabled={!editMode}
                label={draft.active ? "Aktywny" : "Nieaktywny"}
                onChange={(checked) => updateDraft("active", checked)}
              />
              <span>{draft.active ? "AKTYWNY" : "NIEAKTYWNY"}</span>
            </label>
            <label>Zdjęcie:<input ref={photoInputRef} type="file" accept="image/*" onChange={(event) => loadPhoto(event.target.files?.[0])} disabled={!editMode} /></label>
            <label>Awizacja od 1:<input value={draft.awizacjaOd1 ?? ""} onChange={(event) => updateDraft("awizacjaOd1", event.target.value)} disabled={!editMode} /></label>
            <label>Awizacja od 2:<input value={draft.awizacjaOd2 ?? ""} onChange={(event) => updateDraft("awizacjaOd2", event.target.value)} disabled={!editMode} /></label>
            <label className="span-2">Uwagi:<textarea value={draft.notes} onChange={(event) => updateDraft("notes", event.target.value)} disabled={!editMode} /></label>
          </div>
        </section>
        <section>
          <PanelTitle title="SZYBKIE AKCJE" />
          <div className="quick-actions">
            <button onClick={startAddDriver}>DODAJ KIEROWCĘ</button>
            <button onClick={() => { if (selectedDriver) { setDraft(selectedDriver); setEditMode(true); } }}>EDYTUJ DANE</button>
            <button onClick={() => displayedDriver && onOpenSchedule(displayedDriver.id)}>ZOBACZ GRAFIK</button>
            {canManagePeople(user, appState.customRoles) && <button onClick={deactivateDriver}>DEZAKTYWUJ</button>}
            <button className="is-save" onClick={saveDriver} disabled={!editMode}>ZAPISZ DANE</button>
            {canManagePeople(user, appState.customRoles) && <button className="is-danger" onClick={deleteDriver}>USUŃ Z BAZY</button>}
          </div>
          <PanelTitle title="PODGLĄD / INFO" />
          <pre>Kierowca: {displayedDriver?.name ?? "-"}{"\n"}Przewoźnik: {displayedDriver ? carrierName(displayedDriver.carrierId) : "-"}{"\n"}Pojazd: {selectedDraftVehicle ? vehicleDisplayName(selectedDraftVehicle) : displayedDriver?.vehicle ?? "-"}{"\n"}Rejestracja: {selectedDraftVehicle?.registration || displayedDriver?.registration || "-"}{"\n"}Palety: {selectedDraftVehicle?.pallets ?? displayedDriver?.pallets ?? 0}{"\n"}Udźwig: {selectedDraftVehicle?.loadKg ?? displayedDriver?.capacityKg ?? 0} kg{"\n"}Uwagi: {displayedDriver?.notes ?? "-"}</pre>
          <PanelTitle title="NAJBLIŻSZE WPISY I TRASY" />
          <div className="vba-list-box vba-list-box--compact">
            {driverRoutes.map((route) => (
              <button type="button" key={`route-${route.id}`} onClick={() => displayedDriver && onOpenRoute(route.id, displayedDriver.id)}>
                TRASA | {route.name} | {route.serviceType} | {route.defaultStart}
              </button>
            ))}
            {driverSchedule.map((entry) => {
              const route = appState.routes.find((candidate) => candidate.id === entry.routeId);
              return (
                <button type="button" key={entry.id} onClick={() => route && displayedDriver && onOpenRoute(route.id, displayedDriver.id)}>
                  {entry.date} | {statusToLabel(entry.status)} | {route?.name ?? "brak trasy"} | {entry.notes || "-"}
                </button>
              );
            })}
          </div>
        </section>
      </div>
      {routePreview && (
        <PreviewModal title={`Trasa: ${routePreview.name}`} onClose={() => setRoutePreview(null)} onPrint={() => window.print()}>
          <div className="print-preview-sheet">
            <h2>{routePreview.name}</h2>
            <p>Typ: {routePreview.serviceType}</p>
            <p>{routePreview.cityFrom} - {routePreview.cityTo}</p>
            <p>Awizacja od: {routePreview.defaultStart}</p>
            <ol>
              {(routePreview.points ?? []).map((point, index) => (
                <li key={`${point.city}-${index}`}>{point.city} | {point.address} | {point.notes}</li>
              ))}
            </ol>
          </div>
        </PreviewModal>
      )}
    </section>
  );
}

function ShortagesView({
  appState,
  carrierName,
  driverName,
  routeName,
  user,
  onAppStateChange
}: {
  appState: AppState;
  carrierName: (id: string) => string;
  driverName: (id?: string) => string;
  routeName: (id: string) => string;
  user: SessionUser;
  onAppStateChange: (updater: (current: AppState) => AppState) => void;
}) {
  const shortageStatuses: ScheduleEntry["status"][] = ["BRAK", "L4", "NIEOBECNY"];
  const allowedCarrierId = user.role === "przewoznik" ? user.carrierId : undefined;
  const visibleDrivers = appState.drivers.filter((driver) => isDriverActive(driver) && (!allowedCarrierId || driver.carrierId === allowedCarrierId));
  const visibleCarriers = appState.carriers.filter((carrier) => !allowedCarrierId || carrier.id === allowedCarrierId);
  const recordedShortageEntries = appState.schedule.filter((entry) =>
    shortageStatuses.includes(entry.status) &&
    (!allowedCarrierId || entry.carrierId === allowedCarrierId) &&
    entryHasActiveDriverOrOpenShortage(appState, entry)
  );
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));
  const [shortageMode, setShortageMode] = useState<"day" | "week" | "month">("week");
  const [statusFilter, setStatusFilter] = useState("WSZYSTKIE");
  const [driverFilter, setDriverFilter] = useState("WSZYSCY");
  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const syntheticRangeShortages = visibleDrivers.flatMap((driver) =>
    dateRange(dateFrom, dateTo)
      .filter((date) => !appState.schedule.some((entry) => entry.driverId === driver.id && entry.date === date))
      .map((date) => syntheticShortageEntry(appState, driver, date))
  );
  const shortageEntries = [...recordedShortageEntries, ...syntheticRangeShortages]
    .sort((left, right) => `${left.date}-${entryDriverLabel(appState, left, driverName(left.driverId))}`.localeCompare(`${right.date}-${entryDriverLabel(appState, right, driverName(right.driverId))}`));
  const [selectedEntryId, setSelectedEntryId] = useState(shortageEntries[0]?.id ?? "");
  const selectedEntry = shortageEntries.find((entry) => entry.id === selectedEntryId) ?? shortageEntries[0];
  const firstRoute = appState.routes[0];
  const firstCarrier = visibleCarriers[0] ?? appState.carriers[0];
  const [draft, setDraft] = useState({
    date: selectedEntry?.date ?? dateFrom,
    driverId: selectedEntry?.driverId ?? visibleDrivers[0]?.id ?? "",
    routeId: selectedEntry?.routeId ?? firstRoute?.id ?? "",
    carrierId: selectedEntry?.carrierId ?? firstCarrier?.id ?? "",
    status: (selectedEntry?.status ?? "BRAK") as ScheduleEntry["status"],
    start: selectedEntry?.start ?? firstRoute?.defaultStart ?? "06:00",
    end: selectedEntry?.end ?? firstRoute?.defaultEnd ?? "14:00",
    amount: "1",
    priority: "Średni",
    location: "",
    notes: selectedEntry?.notes ?? ""
  });

  function routeFor(routeId: string) {
    return appState.routes.find((route) => route.id === routeId) ?? firstRoute;
  }

  function syncDraft(entry: ScheduleEntry) {
    const route = routeFor(entry.routeId);
    setSelectedEntryId(entry.id);
    setDraft({
      date: entry.date,
      routeId: entry.routeId,
      carrierId: entry.carrierId,
      driverId: entry.driverId ?? visibleDrivers[0]?.id ?? "",
      status: entry.status,
      start: entry.start,
      end: entry.end,
      amount: "1",
      priority: entry.status === "BRAK" ? "Wysoki" : "Średni",
      location: route ? `${route.cityFrom} - ${route.cityTo}` : "",
      notes: entry.notes
    });
  }

  const filteredShortages = shortageEntries.filter((entry) => {
    const driver = appState.drivers.find((candidate) => candidate.id === entry.driverId && isDriverActive(candidate));
    if (entry.driverId && !driver) return false;
    const text = `${entryDriverLabel(appState, entry, driverName(entry.driverId))} ${driver?.workPhone ?? ""} ${driver?.privatePhone ?? ""} ${carrierName(entry.carrierId)} ${entry.notes}`.toLowerCase();
    const inDate = entry.date >= dateFrom && entry.date <= dateTo;
    const matchesStatus = statusFilter === "WSZYSTKIE" || entry.status === statusFilter;
    const matchesDriver = driverFilter === "WSZYSCY" || entry.driverId === driverFilter;
    return inDate && matchesStatus && matchesDriver && text.includes(search.toLowerCase());
  });

  useEffect(() => {
    if (filteredShortages.some((entry) => entry.id === selectedEntryId)) {
      return;
    }

    const nextEntry = filteredShortages[0];
    if (nextEntry) {
      syncDraft(nextEntry);
      return;
    }

    if (selectedEntryId) {
      setSelectedEntryId("");
    }
  }, [dateFrom, dateTo, statusFilter, driverFilter, search, appState.schedule, appState.drivers, selectedEntryId]);

  function setShortagePeriod(mode: "day" | "week" | "month") {
    setShortageMode(mode);
    const base = new Date(`${dateFrom}T12:00:00`);
    if (Number.isNaN(base.getTime())) return;
    if (mode === "day") {
      setDateTo(dateFrom);
      return;
    }
    if (mode === "week") {
      const day = (base.getDay() + 6) % 7;
      base.setDate(base.getDate() - day);
      const end = new Date(base);
      end.setDate(base.getDate() + 6);
      setDateFrom(base.toISOString().slice(0, 10));
      setDateTo(end.toISOString().slice(0, 10));
      return;
    }
    const start = new Date(base.getFullYear(), base.getMonth(), 1, 12);
    const end = new Date(base.getFullYear(), base.getMonth() + 1, 0, 12);
    setDateFrom(start.toISOString().slice(0, 10));
    setDateTo(end.toISOString().slice(0, 10));
  }

  function createNewShortage() {
    const driver = appState.drivers.find((candidate) => candidate.id === draft.driverId);
    const route = findRouteForDriver(appState.routes, driver) ?? routeFor(draft.routeId);
    const entry: ScheduleEntry = {
      id: createId("sch"),
      date: draft.date,
      driverId: draft.driverId || undefined,
      carrierId: draft.carrierId,
      routeId: route?.id ?? draft.routeId,
      status: draft.status,
      start: draft.start,
      end: "",
      awizacjaOd: draft.start,
      notes: `${draft.notes || "Brak kierowcy"} | Kierowca: ${driver?.name ?? "-"} | Tel.: ${driver?.workPhone || driver?.privatePhone || "-"} | Priorytet: ${draft.priority}`
    };
    onAppStateChange((current) => ({
      ...current,
      schedule: [entry, ...current.schedule],
      archive: [archiveItem("Braki", `Dodano brak ${entry.date} ${route?.serviceType ?? ""}`, "SNG", entry.notes), ...current.archive]
    }));
    setSelectedEntryId(entry.id);
  }

  function saveShortage() {
    if (!selectedEntry) {
      createNewShortage();
      return;
    }
    const driver = appState.drivers.find((candidate) => candidate.id === draft.driverId);
    const route = findRouteForDriver(appState.routes, driver) ?? routeFor(draft.routeId);

    onAppStateChange((current) => ({
      ...current,
      schedule: current.schedule.map((entry) =>
        entry.id === selectedEntry.id
          ? {
              ...entry,
              date: draft.date,
              driverId: draft.driverId || undefined,
              routeId: route?.id ?? draft.routeId,
              carrierId: draft.carrierId,
              status: draft.status,
              start: draft.start,
              end: "",
              awizacjaOd: draft.start,
              notes: `${draft.notes || "Brak kierowcy"} | Priorytet: ${draft.priority}`
            }
          : entry
      ),
      archive: [archiveItem("Braki", `Zaktualizowano brak ${draft.date}`, "SNG", draft.notes), ...current.archive]
    }));
  }

  function deleteShortage() {
    if (!selectedEntry) return;
    onAppStateChange((current) => ({
      ...current,
      schedule: current.schedule.filter((entry) => entry.id !== selectedEntry.id),
      archive: [archiveItem("Braki", `Usunięto brak ${selectedEntry.date}`, "SNG", selectedEntry.notes), ...current.archive]
    }));
    setSelectedEntryId("");
  }

  function exportShortages() {
    const rows = [
      ["data", "typ", "kierowca", "przewoznik", "awizacja_od", "status", "uwagi"],
      ...filteredShortages.map((entry) => {
        return [entry.date, entryScheduleType(appState, entry), driverName(entry.driverId), carrierName(entry.carrierId), entry.awizacjaOd ?? entry.start, entry.status, entry.notes];
      })
    ];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sng-braki-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  const selectedRoute = routeFor(draft.routeId);
  const selectedShortageDriver = appState.drivers.find((driver) => driver.id === draft.driverId);

  return (
    <section className="vba-window shortage-board">
      <div className="vba-titlebar">
        <AlertTriangle size={18} />
        <strong>BRAKI</strong>
      </div>
      <div className={clsx("shortage-toolbar", !filtersOpen && "is-collapsed")}>
        <div className="mode-switch">
          <button className={clsx(shortageMode === "day" && "is-active")} onClick={() => setShortagePeriod("day")}>Dzień</button>
          <button className={clsx(shortageMode === "week" && "is-active")} onClick={() => setShortagePeriod("week")}>Tydzień</button>
          <button className={clsx(shortageMode === "month" && "is-active")} onClick={() => setShortagePeriod("month")}>Miesiąc</button>
        </div>
        <label>Zakres dat <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label>
        <label>Do <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
        <label>Kierowca <select value={driverFilter} onChange={(event) => setDriverFilter(event.target.value)}><option value="WSZYSCY">WSZYSCY</option>{visibleDrivers.map((driver) => <option value={driver.id} key={driver.id}>{driver.name}</option>)}</select></label>
        <label>Status <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option>WSZYSTKIE</option><option>BRAK</option><option>L4</option><option>NIEOBECNY</option></select></label>
        <NeonSearchInput
          value={search}
          placeholder="Szukaj kierowcy, telefonu, przewoźnika, uwag..."
          filterActive={filtersOpen}
          onChange={setSearch}
          onFilterClick={() => setFiltersOpen((value) => !value)}
        />
      </div>
      <div className="shortage-layout">
        <section>
          <PanelTitle title="LISTA BRAKÓW" />
          <div className="shortage-list">
            {filteredShortages.map((entry) => {
              const driver = appState.drivers.find((candidate) => candidate.id === entry.driverId);
              return (
                <button type="button" className={clsx(entry.id === selectedEntry?.id && "is-active")} key={entry.id} onClick={() => syncDraft(entry)}>
                  <span>{entry.date} ({dayName(entry.date).slice(0, 3)})</span>
                  <b>{entryDriverLabel(appState, entry, driverName(entry.driverId))}</b>
                  <span>{driver?.workPhone || driver?.privatePhone || "bez tel."}</span>
                  <small>{carrierName(entry.carrierId)}</small>
                  <StatusChip status={entry.status} />
                </button>
              );
            })}
          </div>
        </section>
        <section>
          <PanelTitle title="SZCZEGÓŁY BRAKU / EDYTUJ BRAK" />
          <div className="shortage-form">
            <label>Data <input type="date" value={draft.date} onChange={(event) => setDraft((current) => ({ ...current, date: event.target.value }))} /></label>
            <label>Kierowca <select value={draft.driverId} onChange={(event) => {
              const driver = appState.drivers.find((candidate) => candidate.id === event.target.value);
              const route = findRouteForDriver(appState.routes, driver);
              setDraft((current) => ({ ...current, driverId: event.target.value, routeId: route?.id ?? current.routeId, carrierId: driver?.carrierId ?? current.carrierId, start: driver?.awizacjaOd1 ?? route?.defaultStart ?? current.start }));
            }}>{visibleDrivers.map((driver) => <option value={driver.id} key={driver.id}>{driver.name} ({driver.workPhone || driver.privatePhone || "bez tel."})</option>)}</select></label>
            <label>Telefon <input value={selectedShortageDriver?.workPhone || selectedShortageDriver?.privatePhone || "bez tel."} readOnly /></label>
            <label>Awizacja od <input value={draft.start} onChange={(event) => setDraft((current) => ({ ...current, start: event.target.value }))} /></label>
            <label>Przewoźnik <select value={draft.carrierId} onChange={(event) => setDraft((current) => ({ ...current, carrierId: event.target.value, driverId: visibleDrivers.find((driver) => driver.carrierId === event.target.value)?.id ?? current.driverId }))} disabled={Boolean(allowedCarrierId)}>{visibleCarriers.map((carrier) => <option value={carrier.id} key={carrier.id}>{carrier.name}</option>)}</select></label>
            <label>Priorytet <select value={draft.priority} onChange={(event) => setDraft((current) => ({ ...current, priority: event.target.value }))}><option>Niski</option><option>Średni</option><option>Wysoki</option></select></label>
            <label>Status <select value={draft.status} onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value as ScheduleEntry["status"] }))}><option>BRAK</option><option>L4</option><option>NIEOBECNY</option></select></label>
            <label className="span-2">Lokalizacja / rejon <input value={draft.location} onChange={(event) => setDraft((current) => ({ ...current, location: event.target.value }))} /></label>
            <label className="span-2">Opis / uwagi <textarea value={draft.notes} onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))} /></label>
          </div>
          <div className="shortage-actions">
            <button className="is-save" onClick={saveShortage}>Zapisz</button>
            <button onClick={() => selectedEntry && syncDraft(selectedEntry)}>Anuluj</button>
            <button className="is-danger" onClick={deleteShortage}>Usuń brak</button>
          </div>
        </section>
        <aside>
          <PanelTitle title="NAJBLIŻSZE BRAKI" />
          <div className="shortage-side-list">
            {filteredShortages.slice(0, 5).map((entry) => {
              const route = routeFor(entry.routeId);
              return <button key={entry.id} onClick={() => syncDraft(entry)}>{entry.date} · {entryDriverLabel(appState, entry, driverName(entry.driverId))} · {entry.awizacjaOd ?? entry.start}</button>;
            })}
          </div>
          <PanelTitle title="PODSUMOWANIE WG KIEROWCÓW" />
          <div className="shortage-summary">
            {visibleDrivers.slice(0, 5).map((driver) => {
              const count = filteredShortages.filter((entry) => entry.driverId === driver.id).length;
              return <div key={driver.id}><span>{driver.name}</span><strong>{count}</strong></div>;
            })}
          </div>
          <PanelTitle title="SZYBKIE AKCJE" />
          <div className="shortage-quick-actions">
            <button onClick={createNewShortage}>Dodaj brak</button>
            <button onClick={exportShortages}>Eksportuj</button>
            <button onClick={() => window.print()}>Drukuj</button>
          </div>
        </aside>
      </div>
      <PanelTitle title="HISTORIA ZMIAN BRAKÓW" />
      <div className="vba-list-box vba-list-box--compact">
        {appState.archive.filter((item) => item.category === "Braki" || item.title.toLowerCase().includes("brak")).slice(0, 8).map((item) => (
          <div key={item.id}>{item.archivedAt} | {item.owner} | {item.title}</div>
        ))}
      </div>
    </section>
  );
}

function ScheduleView({
  entries,
  carrierName,
  driverName,
  routeName
}: {
  entries: ScheduleEntry[];
  carrierName: (id: string) => string;
  driverName: (id?: string) => string;
  routeName: (id: string) => string;
}) {
  return (
    <section className="schedule-panel">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Tydzień 20</span>
          <h2>Plan zmian</h2>
        </div>
        <button className="secondary-button" type="button">
          <CalendarDays size={16} />
          Maj 2026
        </button>
      </div>

      <div className="week-grid">
        {weekDays.map((day, index) => {
          const entry = entries[index % entries.length];
          return (
            <div className={clsx("day-column", index >= 5 && "is-weekend")} key={day}>
              <header>{day}</header>
              <div className="day-column__slot is-filled">
                <span>{carrierName(entry.carrierId)}</span>
                <strong>{entry.start}-{entry.end}</strong>
              </div>
              <div className={clsx("day-column__slot", entry.status === "BRAK" ? "is-alert" : "is-filled")}>
                <span>{driverName(entry.driverId)}</span>
                <strong>{routeName(entry.routeId)}</strong>
              </div>
              <div className="day-column__slot">
                <span>{entry.notes}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function DriversView({ drivers, carrierName }: { drivers: Driver[]; carrierName: (id: string) => string }) {
  return (
    <section className="table-panel">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Baza KIEROWCY</span>
          <h2>Karty kierowców</h2>
        </div>
      </div>
      <div className="data-table data-table--drivers">
        <div className="data-table__row data-table__row--head">
          <span>ID</span>
          <span>Kierowca</span>
          <span>Typ</span>
          <span>Przewoźnik</span>
          <span>Telefon</span>
          <span>Pojazd</span>
          <span>Status</span>
        </div>
        {drivers.map((driver) => (
          <div className="data-table__row" key={driver.id}>
            <span>{driver.id.toUpperCase()}</span>
            <span>{driver.name}</span>
            <span>{driver.type}</span>
            <span>{carrierName(driver.carrierId)}</span>
            <span>{driver.workPhone}</span>
            <span>{driver.vehicle} · {driver.registration}</span>
            <span className={clsx("status-chip", isDriverActive(driver) ? "status-chip--gotowe" : "status-chip--ryzyko")}>
              {isDriverActive(driver) ? "Aktywny" : "Nieaktywny"}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function CarriersView({
  carriers,
  drivers,
  schedule
}: {
  carriers: Carrier[];
  drivers: Driver[];
  schedule: ScheduleEntry[];
}) {
  return (
    <section className="table-panel">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Baza PRZEWOŹNICY</span>
          <h2>Przewoźnicy i frekwencja</h2>
        </div>
      </div>
      <div className="data-table data-table--carriers">
        <div className="data-table__row data-table__row--head">
          <span>Nazwa</span>
          <span>E-mail</span>
          <span>Telefon</span>
          <span>Kierowcy</span>
          <span>Dni P</span>
          <span>Uwagi</span>
        </div>
        {carriers.map((carrier) => {
          const carrierDrivers = drivers.filter((driver) => driver.carrierId === carrier.id);
          const workDays = schedule.filter((entry) => entry.carrierId === carrier.id && entry.status === "P").length;

          return (
            <div className="data-table__row" key={carrier.id}>
              <span>{carrier.name}</span>
              <span>{carrier.email}</span>
              <span>{carrier.phone}</span>
              <span>{carrierDrivers.length}</span>
              <span>{workDays}</span>
              <span>{carrier.notes}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function RoutesView({ routes }: { routes: RoutePlan[] }) {
  return (
    <section className="entity-grid">
      {routes.map((route) => (
        <article className="entity-card" key={route.id}>
          <span className="eyebrow">{route.serviceType}</span>
          <h2>{route.name}</h2>
          <p>{route.cityFrom} → {route.cityTo}</p>
          <div className="entity-card__meta">
            <span>{route.defaultStart}-{route.defaultEnd}</span>
            <span>{route.requiredDrivers} kier.</span>
          </div>
        </article>
      ))}
    </section>
  );
}

function RequestsView({
  appState,
  user,
  onAppStateChange
}: {
  appState: AppState;
  user: SessionUser;
  onAppStateChange: (updater: (current: AppState) => AppState) => void;
}) {
  const userCarrierId = user.role === "przewoznik" ? user.carrierId : undefined;
  const visibleCarriers = appState.carriers.filter((carrier) => !userCarrierId || carrier.id === userCarrierId);
  const defaultCarrierId = visibleCarriers[0]?.id ?? appState.carriers[0]?.id ?? "";
  const driversForCarrier = (carrierId: string) => appState.drivers.filter((driver) => isDriverActive(driver) && driver.carrierId === carrierId);
  const routeForDriver = (driverId: string) => {
    const driver = appState.drivers.find((candidate) => candidate.id === driverId);
    return findRouteForDriver(appState.routes, driver) ?? appState.routes[0];
  };
  const [problem, setProblem] = useState({ subject: "Grafik", carrierId: defaultCarrierId, object: driversForCarrier(defaultCarrierId)[0]?.id ?? "", description: "", priority: "niski" as OperationalRequest["priority"] });
  const [changeRequest, setChangeRequest] = useState({ targetKind: "Kierowca", carrierId: defaultCarrierId, driverId: driversForCarrier(defaultCarrierId)[0]?.id ?? "", field: "Imię i nazwisko", value: "", description: "" });
  const [shortage, setShortage] = useState({ carrierId: defaultCarrierId, driverId: driversForCarrier(defaultCarrierId)[0]?.id ?? "", dateFrom: "2026-05-23", dateTo: "2026-05-23", status: "BRAK" as ScheduleEntry["status"], description: "" });
  const [selectedOwnRequestId, setSelectedOwnRequestId] = useState("");
  const visibleRequests = user.role === "administrator" || user.role === "kierownik"
    ? appState.requests
    : appState.requests.filter((request) => request.sender === user.login || request.sender === user.name);
  const selectedOwnRequest = visibleRequests.find((request) => request.id === selectedOwnRequestId) ?? visibleRequests[0];

  function submitRequest(request: Omit<OperationalRequest, "id" | "status" | "createdAt" | "sender">) {
    const created: OperationalRequest = {
      id: createId("REQ"),
      status: "oczekuje",
      createdAt: nowStamp(),
      sender: user.login,
      ...request
    };
    onAppStateChange((current) => ({
      ...current,
      requests: [created, ...current.requests],
      archive: [archiveItem("Zgłoszenia", `Dodano zgłoszenie ${created.subject}`, user.login), ...current.archive]
    }));
  }

  function submitProblem() {
    if (!problem.description.trim()) {
      window.alert("Wpisz opis zgłoszenia.");
      return;
    }
    submitRequest({
      type: "UWAGA",
      priority: problem.priority,
      target: problem.object || problem.carrierId || "Dyspozycja",
      subject: problem.subject,
      description: [
        `Przewoźnik: ${appState.carriers.find((carrier) => carrier.id === problem.carrierId)?.name ?? "-"}`,
        `Kierowca: ${appState.drivers.find((driver) => driver.id === problem.object)?.name ?? "-"}`,
        problem.description
      ].filter(Boolean).join("\n"),
      payload: {
        carrierId: problem.carrierId,
        driverId: problem.object
      }
    });
    setProblem((current) => ({ ...current, description: "" }));
  }

  function submitChange() {
    if (!changeRequest.value.trim()) {
      window.alert("Wpisz nową wartość.");
      return;
    }
    submitRequest({
      type: "ZMIANA_DANYCH",
      priority: "normalny",
      target: changeRequest.driverId || changeRequest.carrierId,
      subject: `Zmiana danych: ${changeRequest.field}`,
      description: [
        `Dotyczy: ${changeRequest.targetKind}`,
        `Przewoźnik: ${appState.carriers.find((carrier) => carrier.id === changeRequest.carrierId)?.name ?? "-"}`,
        `Kierowca: ${appState.drivers.find((driver) => driver.id === changeRequest.driverId)?.name ?? "-"}`,
        `${changeRequest.field} => ${changeRequest.value}`,
        changeRequest.description
      ].filter(Boolean).join("\n"),
      payload: {
        targetKind: changeRequest.targetKind,
        carrierId: changeRequest.carrierId,
        driverId: changeRequest.driverId,
        field: changeRequest.field,
        value: changeRequest.value
      }
    });
    setChangeRequest((current) => ({ ...current, value: "", description: "" }));
  }

  function submitShortage() {
    submitRequest({
      type: "BRAK_KIEROWCY",
      priority: "wysoki",
      target: shortage.driverId || shortage.carrierId,
      subject: `Brak kierowcy ${shortage.dateFrom} - ${shortage.dateTo}`,
      description: [
        `Przewoźnik: ${appState.carriers.find((carrier) => carrier.id === shortage.carrierId)?.name ?? "-"}`,
        `Kierowca: ${appState.drivers.find((driver) => driver.id === shortage.driverId)?.name ?? "Brak kierowcy"}`,
        `Zakres: ${shortage.dateFrom} - ${shortage.dateTo}`,
        `Status: ${shortage.status}`,
        shortage.description
      ].filter(Boolean).join("\n"),
      payload: {
        carrierId: shortage.carrierId,
        driverId: shortage.driverId,
        routeId: routeForDriver(shortage.driverId)?.id,
        dateFrom: shortage.dateFrom,
        dateTo: shortage.dateTo,
        status: shortage.status
      }
    });
    setShortage((current) => ({ ...current, description: "" }));
  }

  return (
    <section className="vba-window vba-requests">
      <div className="vba-titlebar">
        <ClipboardList size={18} />
        <strong>ZGŁOSZENIA I PROŚBY</strong>
      </div>
      <p>Zgłoś problem, zmianę danych lub brak kierowcy do administratora.</p>
      <div className="vba-request-cards">
        <article>
          <h2>ZGŁOŚ PROBLEM</h2>
          <label>Czego dotyczy
            <select value={problem.subject} onChange={(event) => setProblem((current) => ({ ...current, subject: event.target.value }))}>
              <option>Grafik</option><option>Kierowca</option><option>Trasa</option><option>Przewoźnik</option><option>Inne</option>
            </select>
          </label>
          <label>Przewoźnik
            <select value={problem.carrierId} onChange={(event) => {
              const carrierId = event.target.value;
              setProblem((current) => ({ ...current, carrierId, object: driversForCarrier(carrierId)[0]?.id ?? "" }));
            }}>
              {visibleCarriers.map((carrier) => <option value={carrier.id} key={carrier.id}>{carrier.name}</option>)}
            </select>
          </label>
          <label>Kierowca / obiekt
            <select value={problem.object} onChange={(event) => setProblem((current) => ({ ...current, object: event.target.value }))}>
              <option value="">Bez kierowcy</option>
              {driversForCarrier(problem.carrierId).map((driver) => <option value={driver.id} key={driver.id}>{driver.name}</option>)}
            </select>
          </label>
          <textarea value={problem.description} onChange={(event) => setProblem((current) => ({ ...current, description: event.target.value }))} />
          <select value={problem.priority} onChange={(event) => setProblem((current) => ({ ...current, priority: event.target.value as OperationalRequest["priority"] }))}>
            <option value="niski">Niski</option><option value="normalny">Normalny</option><option value="wysoki">Wysoki</option>
          </select>
          <button className="is-danger" onClick={submitProblem}>WYŚLIJ ZGŁOSZENIE</button>
        </article>
        <article>
          <h2>PROŚBA O ZMIANĘ DANYCH</h2>
          <label>Czego dotyczy <select value={changeRequest.targetKind} onChange={(event) => setChangeRequest((current) => ({ ...current, targetKind: event.target.value }))}><option>Kierowca</option><option>Pojazd</option><option>Przewoźnik</option></select></label>
          <label>Przewoźnik <select value={changeRequest.carrierId} onChange={(event) => {
            const carrierId = event.target.value;
            setChangeRequest((current) => ({ ...current, carrierId, driverId: driversForCarrier(carrierId)[0]?.id ?? "" }));
          }}>{visibleCarriers.map((carrier) => <option value={carrier.id} key={carrier.id}>{carrier.name}</option>)}</select></label>
          <label>Kierowca / pojazd <select value={changeRequest.driverId} onChange={(event) => setChangeRequest((current) => ({ ...current, driverId: event.target.value }))}>{driversForCarrier(changeRequest.carrierId).map((driver) => <option value={driver.id} key={driver.id}>{driver.name}</option>)}</select></label>
          <label>Co zmienić <select value={changeRequest.field} onChange={(event) => setChangeRequest((current) => ({ ...current, field: event.target.value }))}><option>Imię i nazwisko</option><option>Telefon</option><option>Auto</option><option>Rejestracja</option><option>Typ</option><option>Uwagi</option></select></label>
          <input placeholder="Nowa wartość" value={changeRequest.value} onChange={(event) => setChangeRequest((current) => ({ ...current, value: event.target.value }))} />
          <textarea placeholder="Uzasadnienie / komentarz" value={changeRequest.description} onChange={(event) => setChangeRequest((current) => ({ ...current, description: event.target.value }))} />
          <button onClick={submitChange}>WYŚLIJ PROŚBĘ</button>
        </article>
        <article>
          <h2>ZGŁOŚ BRAK KIEROWCY</h2>
          <label>Przewoźnik <select value={shortage.carrierId} onChange={(event) => {
            const carrierId = event.target.value;
            setShortage((current) => ({ ...current, carrierId, driverId: driversForCarrier(carrierId)[0]?.id ?? "" }));
          }}>{visibleCarriers.map((carrier) => <option value={carrier.id} key={carrier.id}>{carrier.name}</option>)}</select></label>
          <label>Kierowca <select value={shortage.driverId} onChange={(event) => setShortage((current) => ({ ...current, driverId: event.target.value }))}>{driversForCarrier(shortage.carrierId).map((driver) => <option value={driver.id} key={driver.id}>{driver.name} ({driver.workPhone || driver.privatePhone || "bez tel."})</option>)}</select></label>
          <label>Status w grafiku <select value={shortage.status} onChange={(event) => setShortage((current) => ({ ...current, status: event.target.value as ScheduleEntry["status"] }))}><option>BRAK</option><option>L4</option><option>NIEOBECNY</option></select></label>
          <label>Od kiedy <input type="date" value={shortage.dateFrom} onChange={(event) => setShortage((current) => ({ ...current, dateFrom: event.target.value }))} /></label>
          <label>Do kiedy <input type="date" value={shortage.dateTo} onChange={(event) => setShortage((current) => ({ ...current, dateTo: event.target.value }))} /></label>
          <textarea placeholder="Komentarz" value={shortage.description} onChange={(event) => setShortage((current) => ({ ...current, description: event.target.value }))} />
          <button className="is-blue" onClick={submitShortage}>ZGŁOŚ BRAK</button>
        </article>
      </div>
      <PanelTitle title="MOJE ZGŁOSZENIA I PROŚBY" />
      <div className="vba-list-box">
        {visibleRequests.map((request) => (
          <button type="button" className={clsx(request.id === selectedOwnRequest?.id && "is-active")} key={request.id} onClick={() => setSelectedOwnRequestId(request.id)}>
            {request.id} | {request.type} | {request.subject} | {request.status}
          </button>
        ))}
      </div>
      <PanelTitle title="SZCZEGÓŁY ZGŁOSZENIA" />
      <pre className="vba-description-box">{selectedOwnRequest ? `${selectedOwnRequest.subject}\n${selectedOwnRequest.description}\nStatus: ${selectedOwnRequest.status}` : "Zaznacz zgłoszenie, aby zobaczyć opis."}</pre>
    </section>
  );
}

function NotificationsView({
  appState,
  user,
  onAppStateChange
}: {
  appState: AppState;
  user: SessionUser;
  onAppStateChange: (updater: (current: AppState) => AppState) => void;
}) {
  const visibleNotifications = appState.notifications.filter((notification) => notificationMatchesUser(notification, user));

  function clearNotification(id: string) {
    onAppStateChange((current) => ({
      ...current,
      notifications: canManagePeople(user, appState.customRoles)
        ? current.notifications.filter((notification) => notification.id !== id)
        : current.notifications.map((notification) => notification.id === id ? { ...notification, readBy: Array.from(new Set([...(notification.readBy ?? []), user.login])) } : notification)
    }));
  }

  return (
    <section className="entity-grid">
      {visibleNotifications.map((notification) => (
        <article className="entity-card" key={notification.id}>
          <span className="eyebrow">{notification.recipient}</span>
          <h2>{notification.message}</h2>
          <p>{notification.createdAt}</p>
          <button type="button" onClick={() => clearNotification(notification.id)}>{canManagePeople(user, appState.customRoles) ? "Usuń" : "Odczytane"}</button>
        </article>
      ))}
    </section>
  );
}

function ArchiveView({ appState, user }: { appState: AppState; user: SessionUser }) {
  const visibleArchive = canManagePeople(user, appState.customRoles)
    ? appState.archive
    : appState.archive.filter((item) => item.owner === user.login || item.owner === user.name);
  const [selectedArchiveId, setSelectedArchiveId] = useState(visibleArchive[0]?.id ?? "");
  const selected = visibleArchive.find((item) => item.id === selectedArchiveId);

  return (
    <section className="vba-window vba-archive">
      <div className="vba-titlebar">
        <Archive size={18} />
        <strong>ARCHIWUM DZIAŁAŃ</strong>
      </div>
      <p>Odrzucenia, usunięcia, zapisy oraz zrealizowane zgłoszenia.</p>
      <div className="vba-list-box vba-list-box--large">
        {visibleArchive.map((item) => (
          <button className={clsx(item.id === selectedArchiveId && "is-active")} type="button" key={item.id} onClick={() => setSelectedArchiveId(item.id)}>
            {item.archivedAt} | {item.owner} | {item.category} | {item.title}
          </button>
        ))}
      </div>
      <PanelTitle title="PEŁNY OPIS / KOMENTARZ" />
      <pre className="vba-description-box">
        {selected ? `Data: ${selected.archivedAt}\nUżytkownik: ${selected.owner}\nAkcja: ${selected.category}\nObiekt: ${selected.title}\nSzczegóły: ${selected.details || "brak dodatkowego komentarza"}\nStatus: zapisano w archiwum` : "Zaznacz wpis archiwum, aby zobaczyć pełny opis i komentarz."}
      </pre>
      <div className="vba-footer-actions">
        <button>ODŚWIEŻ</button>
        <button className="is-danger">ZAMKNIJ</button>
      </div>
    </section>
  );
}

function AdminView({
  appState,
  onRoleChange,
  onAppStateChange,
  onResetLocalState,
  onClose
}: {
  appState: AppState;
  onRoleChange: (userId: string, role: Role) => void;
  onAppStateChange: (updater: (current: AppState) => AppState) => void;
  onResetLocalState: () => void;
  onClose: () => void;
}) {
  type AdminTab = "queue" | "ops" | "accounts" | "carriers" | "notifications" | "options" | "archive";
  type CarrierPeriod = "dzien" | "tydzien" | "miesiac" | "kwartal";
  type CarrierProfileTab = "data" | "vehicles" | "vehicleForm" | "vehicleDetails" | "documents" | "history" | "frequency";
  type CarrierFormDraft = Pick<Carrier, "name" | "email" | "phone" | "notes"> & {
    address: string;
    contactPerson: string;
    contactPhone: string;
    cooperationType: string;
    active: boolean;
    logoDataUrl: string;
  };
  const [tab, setTab] = useState<AdminTab>("queue");
  const [selectedRequestId, setSelectedRequestId] = useState(appState.requests[0]?.id ?? "");
  const [selectedUserId, setSelectedUserId] = useState(appState.users[0]?.id ?? "");
  const [selectedAdminArchiveId, setSelectedAdminArchiveId] = useState(appState.archive[0]?.id ?? "");
  const allRoleOptions = getAllRoles(appState.customRoles);
  const selectedUser = appState.users.find((account) => account.id === selectedUserId) ?? appState.users[0];
  const selectedRequest = appState.requests.find((request) => request.id === selectedRequestId) ?? appState.requests[0];
  const selectedAdminArchive = appState.archive.find((item) => item.id === selectedAdminArchiveId) ?? appState.archive[0];
  const [roleDraft, setRoleDraft] = useState<Role>(selectedUser?.role ?? "podglad");
  const [resetPassword, setResetPassword] = useState("TEST123");
  const [selectedCarrierId, setSelectedCarrierId] = useState(appState.carriers[0]?.id ?? "");
  const [carrierPeriod, setCarrierPeriod] = useState<CarrierPeriod>("tydzien");
  const [carrierFrequencyDate, setCarrierFrequencyDate] = useState(new Date().toISOString().slice(0, 10));
  const [carrierForm, setCarrierForm] = useState<CarrierFormDraft>({
    name: "",
    email: "",
    phone: "",
    notes: "",
    address: "",
    contactPerson: "",
    contactPhone: "",
    cooperationType: "Współpraca stała",
    active: true,
    logoDataUrl: ""
  });
  const [carrierEditForm, setCarrierEditForm] = useState<CarrierFormDraft>(() => carrierDraftFrom(appState.carriers[0]));
  const [selectedVehicleId, setSelectedVehicleId] = useState(appState.vehicles[0]?.id ?? "");
  const [vehicleForm, setVehicleForm] = useState<Vehicle>({
    id: "",
    carrierId: appState.carriers[0]?.id ?? "",
    model: "",
    name: "",
    registration: "",
    type: "Furgon",
    loadKg: 0,
    pallets: 0,
    active: true,
    imageDataUrl: "",
    notes: ""
  });
  const [vehicleImportText, setVehicleImportText] = useState("");
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [vehicleSort, setVehicleSort] = useState("registration");
  const [vehicleFiltersOpen, setVehicleFiltersOpen] = useState(false);
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState("WSZYSTKIE");
  const [vehicleStatusFilter, setVehicleStatusFilter] = useState("WSZYSTKIE");
  const [carrierProfileOpen, setCarrierProfileOpen] = useState(false);
  const [carrierProfileTab, setCarrierProfileTab] = useState<CarrierProfileTab>("vehicles");
  const [carrierDataEditing, setCarrierDataEditing] = useState(false);
  const [selectedCarrierDocumentId, setSelectedCarrierDocumentId] = useState("");
  const [documentPage, setDocumentPage] = useState(1);
  const vehiclePhotoInputRef = useRef<HTMLInputElement | null>(null);
  const carrierLogoInputRef = useRef<HTMLInputElement | null>(null);
  const carrierDocumentInputRef = useRef<HTMLInputElement | null>(null);
  const [documentFolder, setDocumentFolder] = useState("Ogólne");
  const [newDocumentFolder, setNewDocumentFolder] = useState("");
  const [taskDraft, setTaskDraft] = useState({ title: "", due: "today" as TaskItem["due"], priority: "normalny" as TaskItem["priority"] });
  const [notificationForm, setNotificationForm] = useState({
    recipient: "WSZYSCY",
    role: "st. dyspozytor",
    userId: appState.users[0]?.id ?? "",
    carrierId: appState.carriers[0]?.id ?? "",
    message: ""
  });
  const selectedCarrierForProfile = appState.carriers.find((carrier) => carrier.id === selectedCarrierId) ?? appState.carriers[0];
  const selectedCarrierVehiclesForProfile = selectedCarrierForProfile
    ? appState.vehicles.filter((vehicle) => vehicle.carrierId === selectedCarrierForProfile.id)
    : [];
  const selectedCarrierDriversForProfile = selectedCarrierForProfile
    ? appState.drivers.filter((driver) => driver.carrierId === selectedCarrierForProfile.id && isDriverActive(driver))
    : [];
  const selectedCarrierDriverIdsForProfile = new Set(selectedCarrierDriversForProfile.map((driver) => driver.id));
  const selectedCarrierRoutesForProfile = appState.routes.filter((route) => route.driverId && selectedCarrierDriverIdsForProfile.has(route.driverId));
  const activeVehicleCountForProfile = selectedCarrierVehiclesForProfile.filter(isVehicleActive).length;
  const selectedCarrierDocumentsForProfile = selectedCarrierForProfile
    ? (appState.carrierDocuments ?? []).filter((document) => document.carrierId === selectedCarrierForProfile.id)
    : [];
  const selectedCarrierFolderNames = Array.from(new Set([
    "Ogólne",
    ...(appState.carrierDocumentFolders ?? [])
      .filter((folder) => folder.carrierId === selectedCarrierForProfile?.id)
      .map((folder) => folder.name),
    ...selectedCarrierDocumentsForProfile.map((document) => document.folder)
  ]));
  const selectedCarrierDocumentsInFolder = selectedCarrierDocumentsForProfile.filter((document) => document.folder === documentFolder);
  const carrierDocumentsPerPage = 6;
  const carrierDocumentPageCount = Math.max(1, Math.ceil(selectedCarrierDocumentsInFolder.length / carrierDocumentsPerPage));
  const activeDocumentPage = Math.min(documentPage, carrierDocumentPageCount);
  const pagedCarrierDocuments = selectedCarrierDocumentsInFolder.slice(
    (activeDocumentPage - 1) * carrierDocumentsPerPage,
    activeDocumentPage * carrierDocumentsPerPage
  );
  const selectedCarrierDocument =
    selectedCarrierDocumentsInFolder.find((document) => document.id === selectedCarrierDocumentId) ??
    pagedCarrierDocuments[0] ??
    selectedCarrierDocumentsInFolder[0];
  const selectedVehicleForProfile = appState.vehicles.find((vehicle) => vehicle.id === selectedVehicleId) ?? selectedCarrierVehiclesForProfile[0];
  const vehicleTypeOptions = Array.from(new Set(selectedCarrierVehiclesForProfile.map((vehicle) => vehicle.type).filter(Boolean))).sort((a, b) => a.localeCompare(b, "pl"));
  const filteredCarrierVehiclesForProfile = selectedCarrierVehiclesForProfile
    .filter((vehicle) => {
      const query = normalizeCity(vehicleSearch);
      const matchesQuery = !query || normalizeCity([vehicle.model, vehicle.registration, vehicle.type, vehicle.name].join(" ")).includes(query);
      const matchesType = vehicleTypeFilter === "WSZYSTKIE" || normalizeCity(vehicle.type) === normalizeCity(vehicleTypeFilter);
      const matchesStatus =
        vehicleStatusFilter === "WSZYSTKIE" ||
        (vehicleStatusFilter === "AKTYWNE" && isVehicleActive(vehicle)) ||
        (vehicleStatusFilter === "NIEAKTYWNE" && !isVehicleActive(vehicle));
      return matchesQuery && matchesType && matchesStatus;
    })
    .sort((a, b) => {
      if (vehicleSort === "model") return a.model.localeCompare(b.model, "pl");
      if (vehicleSort === "type") return a.type.localeCompare(b.type, "pl");
      if (vehicleSort === "status") return Number(isVehicleActive(b)) - Number(isVehicleActive(a));
      return (a.registration || a.name || a.model).localeCompare(b.registration || b.name || b.model, "pl");
    });

  function navButton(label: string, nextTab: AdminTab) {
    return (
      <button className={clsx(tab === nextTab && "is-active")} onClick={() => setTab(nextTab)}>
        {label}
      </button>
    );
  }

  function carrierDraftFrom(carrier?: Carrier): CarrierFormDraft {
    return {
      name: carrier?.name ?? "",
      email: carrier?.email ?? "",
      phone: carrier?.phone ?? "",
      notes: carrier?.notes ?? "",
      address: carrier?.address ?? "",
      contactPerson: carrier?.contactPerson ?? "",
      contactPhone: carrier?.contactPhone ?? "",
      cooperationType: carrier?.cooperationType ?? "Współpraca stała",
      active: parseActiveFlag(carrier?.active, true),
      logoDataUrl: carrier?.logoDataUrl ?? ""
    };
  }

  function syncCarrierEditForm(carrierId: string) {
    setCarrierEditForm(carrierDraftFrom(appState.carriers.find((carrier) => carrier.id === carrierId)));
  }

  function selectUser(account: AppUser) {
    setSelectedUserId(account.id);
    setRoleDraft(account.role);
  }

  function acceptRequest() {
    if (!selectedRequest) return;

    onAppStateChange((current) => {
      let users = current.users;
      let carriers = current.carriers;
      let drivers = current.drivers;
      let schedule = current.schedule;
      const payload = selectedRequest.payload;

      if (selectedRequest.type === "REG") {
        users = users.map((account) =>
          account.id === selectedRequest.target ? { ...account, status: "aktywny" } : account
        );
      }

      if (selectedRequest.type === "RESET") {
        users = users.map((account) =>
          account.id === selectedRequest.target ? { ...account, password: resetPassword, status: "aktywny" } : account
        );
      }

      if (selectedRequest.type === "ZMIANA_DANYCH") {
        const value = payload?.value ?? selectedRequest.description.split("=>")[1]?.split("\n")[0]?.split(".")[0]?.trim();
        const field = payload?.field ?? selectedRequest.subject;

        if (payload?.targetKind === "Przewoźnik" && payload.carrierId && value) {
          carriers = carriers.map((carrier) => {
            if (carrier.id !== payload.carrierId) return carrier;
            if (field.includes("Telefon")) return { ...carrier, phone: value };
            if (field.includes("Uwagi")) return { ...carrier, notes: value };
            if (field.includes("Imię") || field.includes("Nazwa")) return { ...carrier, name: value };
            return { ...carrier, email: value };
          });
        }

        drivers = drivers.map((driver) => {
          if (driver.id !== (payload?.driverId ?? selectedRequest.target) || !value) return driver;
          if (field.includes("Telefon")) return { ...driver, workPhone: value };
          if (field.includes("Auto")) return { ...driver, vehicle: value };
          if (field.includes("Rejestracja")) return { ...driver, registration: value };
          if (field.includes("Typ") && current.routeTypes.includes(value)) return { ...driver, type: value as DriverType };
          if (field.includes("Uwagi")) return { ...driver, notes: value };
          return { ...driver, name: value };
        });
      }

      if (selectedRequest.type === "BRAK_KIEROWCY") {
        const subjectDates = selectedRequest.subject.match(/(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})/);
        const from = payload?.dateFrom ?? subjectDates?.[1] ?? new Date().toISOString().slice(0, 10);
        const to = payload?.dateTo ?? subjectDates?.[2] ?? from;
        const routeId = payload?.routeId ?? current.routes[0]?.id ?? "";
        const route = current.routes.find((candidate) => candidate.id === routeId);
        const status = payload?.status ?? "BRAK";
        const createdEntries = dateRange(from, to).map((date) => ({
          id: createId("sch"),
          date,
          dateTo: to,
          driverId: payload?.driverId,
          carrierId: payload?.carrierId ?? current.carriers[0]?.id ?? "",
          routeId,
          status,
          start: route?.defaultStart ?? "06:00",
          end: "",
          awizacjaOd: route?.defaultStart ?? "06:00",
          notes: `Korekta z zaakceptowanego zgłoszenia ${selectedRequest.id}. ${selectedRequest.description}`,
          createdBy: "Stobima"
        }));
        schedule = [...createdEntries, ...schedule];
      }

      return {
        ...current,
        users,
        carriers,
        drivers,
        schedule,
        requests: current.requests.map((request) =>
          request.id === selectedRequest.id ? { ...request, status: "zaakceptowane" } : request
        ),
        archive: [archiveItem("Admin", `Zaakceptowano ${selectedRequest.subject}`, "Stobima", selectedRequest.description), ...current.archive]
      };
    });
  }

  function rejectRequest() {
    if (!selectedRequest) return;

    onAppStateChange((current) => ({
      ...current,
      requests: current.requests.map((request) =>
        request.id === selectedRequest.id ? { ...request, status: "odrzucone" } : request
      ),
      archive: [archiveItem("Admin", `Odrzucono ${selectedRequest.subject}`, "Stobima", selectedRequest.description), ...current.archive]
    }));
  }

  function applyRole() {
    if (!selectedUser || selectedUser.login.toLowerCase() === "stobima") {
      window.alert("Konta Stobima nie można zdegradować ani usunąć.");
      return;
    }

    onRoleChange(selectedUser.id, roleDraft);
    onAppStateChange((current) => ({
      ...current,
      archive: [archiveItem("Role", `Nadano rolę ${roleLabel(roleDraft)} dla ${selectedUser.login}`, "Stobima"), ...current.archive]
    }));
  }

  function deleteUser() {
    if (!selectedUser || selectedUser.login.toLowerCase() === "stobima") {
      window.alert("Konta Stobima nie można usunąć.");
      return;
    }

    onAppStateChange((current) => ({
      ...current,
      users: current.users.filter((account) => account.id !== selectedUser.id),
      archive: [archiveItem("Konta", `Usunięto konto ${selectedUser.login}`, "Stobima"), ...current.archive]
    }));
  }

  function toggleUserStatus() {
    if (!selectedUser || selectedUser.login.toLowerCase() === "stobima") {
      window.alert("Konta Stobima nie można dezaktywować.");
      return;
    }

    const nextStatus = selectedUser.status === "aktywny" ? "zablokowany" : "aktywny";
    onAppStateChange((current) => ({
      ...current,
      users: current.users.map((account) =>
        account.id === selectedUser.id ? { ...account, status: nextStatus } : account
      ),
      archive: [archiveItem("Konta", `${nextStatus === "aktywny" ? "Aktywowano" : "Dezaktywowano"} konto ${selectedUser.login}`, "Stobima"), ...current.archive]
    }));
  }

  function assignTaskToSelectedUser() {
    if (!selectedUser || !taskDraft.title.trim()) {
      window.alert("Wybierz użytkownika i wpisz treść zadania.");
      return;
    }

    const task: TaskItem = {
      id: createId("task"),
      title: taskDraft.title.trim(),
      assignedTo: selectedUser.login,
      createdBy: "Stobima",
      due: taskDraft.due,
      priority: taskDraft.priority,
      completed: false,
      createdAt: nowStamp()
    };

    onAppStateChange((current) => ({
      ...current,
      tasks: [task, ...current.tasks],
      archive: [archiveItem("Zadania", `Przypisano zadanie dla ${selectedUser.login}`, "Stobima", task.title), ...current.archive]
    }));
    setTaskDraft((current) => ({ ...current, title: "" }));
  }

  function saveCarrier() {
    if (!carrierForm.name.trim()) {
      window.alert("Podaj nazwę przewoźnika.");
      return;
    }

    const carrier: Carrier = {
      id: createId("car"),
      name: carrierForm.name.trim(),
      email: carrierForm.email,
      phone: carrierForm.phone,
      notes: carrierForm.notes,
      address: carrierForm.address,
      contactPerson: carrierForm.contactPerson,
      contactPhone: carrierForm.contactPhone,
      cooperationType: carrierForm.cooperationType,
      active: carrierForm.active,
      logoDataUrl: carrierForm.logoDataUrl
    };

    onAppStateChange((current) => ({
      ...current,
      carriers: [carrier, ...current.carriers],
      archive: [archiveItem("Przewoźnicy", `Dodano przewoźnika ${carrier.name}`, "Stobima"), ...current.archive]
    }));
    setCarrierForm({
      name: "",
      email: "",
      phone: "",
      notes: "",
      address: "",
      contactPerson: "",
      contactPhone: "",
      cooperationType: "Współpraca stała",
      active: true,
      logoDataUrl: ""
    });
  }

  function deleteCarrier(id: string) {
    const hasDrivers = appState.drivers.some((driver) => driver.carrierId === id);
    if (hasDrivers) {
      window.alert("Nie można usunąć przewoźnika, który ma przypisanych kierowców.");
      return;
    }

    onAppStateChange((current) => ({
      ...current,
      carriers: current.carriers.filter((carrier) => carrier.id !== id),
      archive: [archiveItem("Przewoźnicy", `Usunięto przewoźnika ${id}`, "Stobima"), ...current.archive]
    }));
  }

  function saveCarrierProfile() {
    if (!selectedCarrierForProfile || !carrierEditForm.name.trim()) {
      window.alert("Podaj nazwę przewoźnika.");
      return;
    }
    const updatedCarrier: Carrier = {
      ...selectedCarrierForProfile,
      name: carrierEditForm.name.trim(),
      email: carrierEditForm.email.trim(),
      phone: carrierEditForm.phone.trim(),
      notes: carrierEditForm.notes,
      address: carrierEditForm.address,
      contactPerson: carrierEditForm.contactPerson,
      contactPhone: carrierEditForm.contactPhone,
      cooperationType: carrierEditForm.cooperationType,
      active: carrierEditForm.active,
      logoDataUrl: carrierEditForm.logoDataUrl
    };
    onAppStateChange((current) => ({
      ...current,
      carriers: current.carriers.map((carrier) => carrier.id === updatedCarrier.id ? updatedCarrier : carrier),
      archive: [archiveItem("Przewoźnicy", `Edycja danych przewoźnika ${updatedCarrier.name}`, "Stobima"), ...current.archive]
    }));
    setCarrierDataEditing(false);
  }

  async function loadCarrierLogo(file?: File) {
    if (!file || !selectedCarrierForProfile) return;
    const logoDataUrl = await fileToDataUrl(file);
    setCarrierEditForm((current) => ({ ...current, logoDataUrl }));
    onAppStateChange((current) => ({
      ...current,
      carriers: current.carriers.map((carrier) => carrier.id === selectedCarrierForProfile.id ? { ...carrier, logoDataUrl } : carrier),
      archive: [archiveItem("Przewoźnicy", `Zmieniono logo przewoźnika ${selectedCarrierForProfile.name}`, "Stobima"), ...current.archive]
    }));
  }

  function createDocumentFolder() {
    const name = newDocumentFolder.trim();
    if (!name || !selectedCarrierForProfile) return;
    const exists = selectedCarrierFolderNames.some((folder) => normalizeCity(folder) === normalizeCity(name));
    if (exists) {
      setDocumentFolder(name);
      setSelectedCarrierDocumentId("");
      setDocumentPage(1);
      setNewDocumentFolder("");
      return;
    }
    onAppStateChange((current) => ({
      ...current,
      carrierDocumentFolders: [
        ...(current.carrierDocumentFolders ?? []),
        { id: createId("cdf"), carrierId: selectedCarrierForProfile.id, name, createdAt: nowStamp() }
      ],
      archive: [archiveItem("Dokumenty", `Dodano folder ${name} dla ${selectedCarrierForProfile.name}`, "Stobima"), ...current.archive]
    }));
    setDocumentFolder(name);
    setSelectedCarrierDocumentId("");
    setDocumentPage(1);
    setNewDocumentFolder("");
  }

  async function importCarrierDocuments(files?: FileList | null) {
    if (!files?.length || !selectedCarrierForProfile) return;
    const documents: CarrierDocument[] = [];
    for (const file of Array.from(files)) {
      let previewText = "";
      let previewRows: string[][] | undefined;
      const lowerName = file.name.toLowerCase();
      if (file.type.startsWith("text/") || /\.(txt|csv|log|json|md)$/i.test(file.name)) {
        previewText = await file.text();
      }
      if (lowerName.endsWith(".csv")) {
        previewRows = parseDelimitedRows(previewText).slice(0, 30).map((row) => row.map((cell) => String(cell ?? "")));
      } else if (lowerName.endsWith(".xlsx")) {
        try {
          previewRows = (await readXlsxFile(file)).slice(0, 30).map((row) => row.map((cell) => String(cell ?? "")));
        } catch {
          previewRows = undefined;
        }
      }
      documents.push({
        id: createId("doc"),
        carrierId: selectedCarrierForProfile.id,
        folder: documentFolder || "Ogólne",
        name: file.name,
        size: file.size,
        mimeType: file.type || "application/octet-stream",
        uploadedAt: nowStamp(),
        dataUrl: await fileToDataUrl(file),
        previewText: previewText.slice(0, 12000),
        previewRows
      });
    }
    onAppStateChange((current) => ({
      ...current,
      carrierDocuments: [...documents, ...(current.carrierDocuments ?? [])],
      archive: [archiveItem("Dokumenty", `Dodano pliki dla ${selectedCarrierForProfile.name}: ${documents.length}`, "Stobima"), ...current.archive]
    }));
  }

  function deleteCarrierDocument(documentId: string) {
    const document = selectedCarrierDocumentsForProfile.find((candidate) => candidate.id === documentId);
    if (!document) return;
    onAppStateChange((current) => ({
      ...current,
      carrierDocuments: (current.carrierDocuments ?? []).filter((candidate) => candidate.id !== documentId),
      archive: [archiveItem("Dokumenty", `Usunięto dokument ${document.name}`, "Stobima"), ...current.archive]
    }));
  }

  function updateVehicleFormField<K extends keyof Vehicle>(key: K, value: Vehicle[K]) {
    setVehicleForm((current) => {
      const next = { ...current, [key]: value };
      if (key === "model" && !current.name) {
        next.name = vehicleModelFileName(String(value));
      }
      return next;
    });
  }

  function resetVehicleForm(carrierId = selectedCarrierId) {
    setVehicleForm({
      id: "",
      carrierId,
      model: "",
      name: "",
      registration: "",
      type: "Furgon",
      loadKg: 0,
      pallets: 0,
      active: true,
      imageDataUrl: "",
      notes: ""
    });
    setSelectedVehicleId("");
  }

  function selectCarrier(carrierId: string) {
    setSelectedCarrierId(carrierId);
    resetVehicleForm(carrierId);
    syncCarrierEditForm(carrierId);
    setDocumentFolder("Ogólne");
    setSelectedCarrierDocumentId("");
    setDocumentPage(1);
    setCarrierDataEditing(false);
  }

  function openCarrierProfile(carrierId: string) {
    selectCarrier(carrierId);
    setCarrierProfileTab("vehicles");
    setCarrierDataEditing(false);
    setCarrierProfileOpen(true);
  }

  function selectVehicle(vehicle: Vehicle) {
    setSelectedVehicleId(vehicle.id);
    setVehicleForm(vehicle);
    setCarrierProfileTab("vehicleDetails");
  }

  async function loadVehicleImage(file?: File) {
    if (!file) return;
    const imageDataUrl = await vehicleImageDataUrl(file);
    const fallbackModel = vehicleForm.model || file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ");
    setVehicleForm((current) => ({
      ...current,
      model: current.model || fallbackModel,
      name: current.name || fallbackModel,
      imageDataUrl
    }));
  }

  function saveVehicle() {
    if (!vehicleForm.model.trim()) {
      window.alert("Podaj model pojazdu.");
      return;
    }

    const vehicle: Vehicle = {
      ...vehicleForm,
      id: vehicleForm.id || createId("veh"),
      carrierId: vehicleForm.carrierId || selectedCarrierId,
      model: vehicleForm.model.trim(),
      name: vehicleForm.name.trim() || vehicleForm.model.trim(),
      registration: vehicleForm.registration.trim(),
      loadKg: Number(vehicleForm.loadKg) || 0,
      pallets: Number(vehicleForm.pallets) || 0,
      active: isVehicleActive(vehicleForm),
      imageDataUrl: vehicleForm.imageDataUrl
    };

    onAppStateChange((current) => ({
      ...current,
      vehicles: vehicleForm.id
        ? current.vehicles.map((candidate) => candidate.id === vehicle.id ? vehicle : candidate)
        : [vehicle, ...(current.vehicles ?? [])],
      archive: [archiveItem("Pojazdy", `${vehicleForm.id ? "Edycja" : "Dodano"} pojazd ${vehicleDisplayName(vehicle)}`, "Stobima"), ...current.archive]
    }));
    setSelectedVehicleId(vehicle.id);
    setVehicleForm(vehicle);
    setCarrierProfileTab("vehicleDetails");
  }

  function toggleVehicleActive(vehicleId: string) {
    const vehicle = appState.vehicles.find((candidate) => candidate.id === vehicleId);
    if (!vehicle) return;
    onAppStateChange((current) => ({
      ...current,
      vehicles: current.vehicles.map((candidate) =>
        candidate.id === vehicleId ? { ...candidate, active: !isVehicleActive(candidate) } : candidate
      ),
      archive: [archiveItem("Pojazdy", `${isVehicleActive(vehicle) ? "Dezaktywowano" : "Aktywowano"} pojazd ${vehicleDisplayName(vehicle)}`, "Stobima"), ...current.archive]
    }));
  }

  function deleteVehicle(vehicleId: string) {
    const vehicle = appState.vehicles.find((candidate) => candidate.id === vehicleId);
    if (!vehicle) return;
    if (!window.confirm(`Usunac pojazd ${vehicleDisplayName(vehicle)}? Kierowcy straca przypisanie pojazdu.`)) return;
    onAppStateChange((current) => ({
      ...current,
      vehicles: current.vehicles.filter((candidate) => candidate.id !== vehicleId),
      drivers: current.drivers.map((driver) => driver.vehicleId === vehicleId ? { ...driver, vehicleId: "" } : driver),
      archive: [archiveItem("Pojazdy", `Usunieto pojazd ${vehicleDisplayName(vehicle)}`, "Stobima"), ...current.archive]
    }));
    resetVehicleForm(vehicle.carrierId);
  }

  function importVehicleRows(rows: unknown[][]) {
    const [header, ...body] = rows.filter((row) => row.some((cell) => String(cell ?? "").trim()));
    if (!header) {
      window.alert("Nie rozpoznano szablonu pojazdow.");
      return;
    }
    const columns = header.map((cell) => normalizeCity(String(cell ?? "")));
    const readCell = (row: unknown[], names: string[]) => {
      const index = columns.findIndex((column) => names.some((name) => column === normalizeCity(name) || column.includes(normalizeCity(name))));
      return index >= 0 ? String(row[index] ?? "").trim() : "";
    };

    onAppStateChange((current) => {
      const vehicles = [...(current.vehicles ?? [])];
      let changed = 0;
      body.forEach((row) => {
        const model = readCell(row, ["model", "pojazd", "nazwa"]);
        if (!model) return;
        const carrierNameFromRow = readCell(row, ["przewoznik", "przewoźnik"]);
        const carrier = current.carriers.find((candidate) => normalizeCity(candidate.name) === normalizeCity(carrierNameFromRow)) ??
          current.carriers.find((candidate) => candidate.id === selectedCarrierId) ??
          current.carriers[0];
        if (!carrier) return;
        const registration = readCell(row, ["rejestracja", "nr rejestracyjny", "numer"]);
        const existingIndex = vehicles.findIndex((vehicle) =>
          vehicle.carrierId === carrier.id &&
          ((registration && normalizeCity(vehicle.registration) === normalizeCity(registration)) || normalizeCity(vehicle.model) === normalizeCity(model))
        );
        const vehicle: Vehicle = {
          id: existingIndex >= 0 ? vehicles[existingIndex].id : createId("veh"),
          carrierId: carrier.id,
          model,
          name: readCell(row, ["nazwa"]) || `${model}${registration ? ` ${registration}` : ""}`,
          registration,
          type: readCell(row, ["typ"]) || "Furgon",
          loadKg: Number(readCell(row, ["ladownosc", "ładowność", "udzwig", "udźwig"])) || 0,
          pallets: Number(readCell(row, ["palety"])) || 0,
          active: parseActiveFlag(readCell(row, ["aktywny", "status"]), true),
          imageDataUrl: existingIndex >= 0 ? vehicles[existingIndex].imageDataUrl : "",
          notes: readCell(row, ["uwagi"])
        };
        if (existingIndex >= 0) vehicles[existingIndex] = vehicle;
        else vehicles.unshift(vehicle);
        changed += 1;
      });
      return {
        ...current,
        vehicles,
        archive: [archiveItem("Import pojazdow", `Zaimportowano pojazdy: ${changed}`, "Stobima"), ...current.archive]
      };
    });
    setVehicleImportText("");
  }

  async function importVehicleFile(file?: File) {
    if (!file) return;
    if (file.name.toLowerCase().endsWith(".xlsx")) {
      importVehicleRows(await readXlsxFile(file));
      return;
    }
    importVehicleRows(parseDelimitedRows(await file.text()));
  }

  function downloadVehicleTemplate() {
    const rows = [
      ["przewoznik", "model", "nazwa", "rejestracja", "typ", "ladownosc", "palety", "aktywny", "uwagi"],
      ["SNG Transport", "Renault Megane 2", "Renault_Megane_2", "WE-12345TS", "Furgon", "1300", "4", "tak", ""]
    ];
    const blob = new Blob([rows.map((row) => row.join(";")).join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "szablon-pojazdow-sng.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  function renderVehicleWorkspace(selectedCarrier: Carrier) {
    return (
      <div className="carrier-vehicles-workspace">
        <section>
          <div className="carrier-profile-section-head">
            <div>
              <h3>Wszystkie pojazdy ({filteredCarrierVehiclesForProfile.length})</h3>
              <p>Galeria pojazdów przewoźnika, przypisania do kierowców i status aktywności.</p>
            </div>
            <div className="vehicle-toolbar">
              <NeonSearchInput
                value={vehicleSearch}
                onChange={setVehicleSearch}
                placeholder="Szukaj pojazdu..."
                filterActive={vehicleFiltersOpen}
                onFilterClick={() => setVehicleFiltersOpen((current) => !current)}
                className="vehicle-search"
              />
              <button type="button" className="is-save" onClick={() => { resetVehicleForm(selectedCarrier.id); setCarrierProfileTab("vehicleForm"); }}>Dodaj pojazd</button>
            </div>
          </div>
          {vehicleFiltersOpen && (
            <div className="vehicle-filter-row">
              <label>Sortuj
                <select value={vehicleSort} onChange={(event) => setVehicleSort(event.target.value)}>
                  <option value="registration">Nr rejestracyjny</option>
                  <option value="model">Model</option>
                  <option value="type">Typ</option>
                  <option value="status">Status</option>
                </select>
              </label>
              <label>Typ
                <select value={vehicleTypeFilter} onChange={(event) => setVehicleTypeFilter(event.target.value)}>
                  <option value="WSZYSTKIE">Wszystkie typy</option>
                  {vehicleTypeOptions.map((type) => <option value={type} key={type}>{type}</option>)}
                </select>
              </label>
              <label>Status
                <select value={vehicleStatusFilter} onChange={(event) => setVehicleStatusFilter(event.target.value)}>
                  <option value="WSZYSTKIE">Wszystkie statusy</option>
                  <option value="AKTYWNE">Aktywne</option>
                  <option value="NIEAKTYWNE">Nieaktywne</option>
                </select>
              </label>
            </div>
          )}
          <div className="vehicle-card-grid">
            {filteredCarrierVehiclesForProfile.map((vehicle) => (
              <article className={clsx(vehicle.id === selectedVehicleId && "is-selected", !isVehicleActive(vehicle) && "is-inactive")} key={vehicle.id}>
                <button type="button" className="vehicle-image" onDoubleClick={() => selectVehicle(vehicle)} onClick={() => setSelectedVehicleId(vehicle.id)}>
                  {vehicle.imageDataUrl ? <img src={vehicle.imageDataUrl} alt="" /> : <VehiclePhotoPlaceholder />}
                </button>
                <header>
                  <strong>{vehicle.registration || vehicle.model}</strong>
                  <span className={clsx("vehicle-status", isVehicleActive(vehicle) ? "is-active" : "is-inactive")}>{isVehicleActive(vehicle) ? "Aktywny" : "Nieaktywny"}</span>
                </header>
                <dl>
                  <div><dt>Model</dt><dd>{vehicle.model}</dd></div>
                  <div><dt>Typ</dt><dd>{vehicle.type}</dd></div>
                  <div><dt>Ładowność</dt><dd>{vehicle.loadKg} kg</dd></div>
                  <div><dt>Palety</dt><dd>{vehicle.pallets}</dd></div>
                </dl>
                <div className="vehicle-actions">
                  <button type="button" onClick={() => selectVehicle(vehicle)}>Szczegóły</button>
                  <button type="button" onClick={() => { setVehicleForm(vehicle); setSelectedVehicleId(vehicle.id); setCarrierProfileTab("vehicleForm"); }}>Edytuj</button>
                  <button type="button" className="is-danger" onClick={() => deleteVehicle(vehicle.id)}>Usuń</button>
                </div>
              </article>
            ))}
            {!filteredCarrierVehiclesForProfile.length && (
              <div className="carrier-profile-empty">Brak pojazdów. Dodaj auto ręcznie albo importuj szablon.</div>
            )}
          </div>
        </section>
      </div>
    );
  }

  function renderVehicleEditor(selectedCarrier: Carrier) {
    return (
      <section className="carrier-profile-tab-panel vehicle-form-tab">
        <div className="carrier-profile-section-head">
          <div>
            <h3>{vehicleForm.id ? "Edytuj pojazd" : "Dodaj pojazd"}</h3>
            <p>Zdjecie dodajesz recznie. Kliknij lub dwukliknij podglad, zeby wybrac plik.</p>
          </div>
          <button type="button" onClick={() => setCarrierProfileTab("vehicles")}>Wróć do pojazdów</button>
        </div>
        <div className="vehicle-editor vehicle-editor--tab">
          <button type="button" className="vehicle-editor__image" onDoubleClick={() => vehiclePhotoInputRef.current?.click()} onClick={() => vehiclePhotoInputRef.current?.click()}>
            {vehicleForm.imageDataUrl ? <img src={vehicleForm.imageDataUrl} alt="" /> : <VehiclePhotoPlaceholder size={74} />}
            <span>Dodaj / zmien zdjecie</span>
          </button>
          <input ref={vehiclePhotoInputRef} hidden type="file" accept="image/*" onChange={(event) => void loadVehicleImage(event.target.files?.[0])} />
          <div className="vba-form-grid">
            <label>Przewoźnik <select value={vehicleForm.carrierId || selectedCarrier.id} onChange={(event) => updateVehicleFormField("carrierId", event.target.value)}>
              {appState.carriers.map((carrier) => <option value={carrier.id} key={carrier.id}>{carrier.name}</option>)}
            </select></label>
            <label>Model <input value={vehicleForm.model} onChange={(event) => updateVehicleFormField("model", event.target.value)} placeholder="Renault Master" /></label>
            <label>Nazwa w galerii <input value={vehicleForm.name} onChange={(event) => updateVehicleFormField("name", event.target.value)} placeholder="Renault_Megane_2" /></label>
            <label>Rejestracja <input value={vehicleForm.registration} onChange={(event) => updateVehicleFormField("registration", event.target.value)} /></label>
            <label>Typ <input value={vehicleForm.type} onChange={(event) => updateVehicleFormField("type", event.target.value)} /></label>
            <label>Ładowność kg <input type="number" value={vehicleForm.loadKg} onChange={(event) => updateVehicleFormField("loadKg", Number(event.target.value))} /></label>
            <label>Palety <input type="number" value={vehicleForm.pallets} onChange={(event) => updateVehicleFormField("pallets", Number(event.target.value))} /></label>
            <label>Przebieg km <input type="number" value={vehicleForm.mileageKm ?? 0} onChange={(event) => updateVehicleFormField("mileageKm", Number(event.target.value))} /></label>
            <label>VIN <input value={vehicleForm.vin ?? ""} onChange={(event) => updateVehicleFormField("vin", event.target.value)} /></label>
            <label>Rok produkcji <input value={vehicleForm.productionYear ?? ""} onChange={(event) => updateVehicleFormField("productionYear", event.target.value)} /></label>
            <label>Paliwo <input value={vehicleForm.fuel ?? ""} onChange={(event) => updateVehicleFormField("fuel", event.target.value)} /></label>
            <label>Skrzynia <input value={vehicleForm.gearbox ?? ""} onChange={(event) => updateVehicleFormField("gearbox", event.target.value)} /></label>
            <label>DMC kg <input type="number" value={vehicleForm.dmcKg ?? 0} onChange={(event) => updateVehicleFormField("dmcKg", Number(event.target.value))} /></label>
            <label>Kierowca główny <select value={vehicleForm.mainDriverId ?? ""} onChange={(event) => updateVehicleFormField("mainDriverId", event.target.value)}>
              <option value="">Brak</option>
              {selectedCarrierDriversForProfile.map((driver) => <option value={driver.id} key={driver.id}>{driver.name}</option>)}
            </select></label>
            <label>OC do <input type="date" value={vehicleForm.insuranceDate ?? ""} onChange={(event) => updateVehicleFormField("insuranceDate", event.target.value)} /></label>
            <label>Badanie do <input type="date" value={vehicleForm.technicalDate ?? ""} onChange={(event) => updateVehicleFormField("technicalDate", event.target.value)} /></label>
            <label>Tachograf do <input type="date" value={vehicleForm.tachographDate ?? ""} onChange={(event) => updateVehicleFormField("tachographDate", event.target.value)} /></label>
            <label>Leasing <input value={vehicleForm.leasingStatus ?? ""} onChange={(event) => updateVehicleFormField("leasingStatus", event.target.value)} /></label>
            <label>Aktywny <ActiveCheck checked={isVehicleActive(vehicleForm)} label={isVehicleActive(vehicleForm) ? "Aktywny" : "Nieaktywny"} onChange={(checked) => updateVehicleFormField("active", checked)} /></label>
            <label className="span-2">Uwagi <input value={vehicleForm.notes ?? ""} onChange={(event) => updateVehicleFormField("notes", event.target.value)} /></label>
            <button className="is-save" type="button" onClick={saveVehicle}>Zapisz pojazd</button>
          </div>
        </div>
      </section>
    );
  }

  function renderVehicleDetails(vehicle?: Vehicle) {
    if (!vehicle) {
      return <div className="carrier-profile-empty">Wybierz pojazd, aby zobaczyć szczegóły.</div>;
    }
    const mainDriver =
      appState.drivers.find((driver) => driver.id === vehicle.mainDriverId) ??
      appState.drivers.find((driver) => driver.vehicleId === vehicle.id);
    const mainDriverId = mainDriver?.id ?? "";
    const currentMonth = new Date().toISOString().slice(0, 7);
    const vehicleRoutes = mainDriverId ? appState.routes.filter((route) => route.driverId === mainDriverId).slice(0, 6) : [];
    const vehicleMonthlyOrders = mainDriverId
      ? appState.schedule.filter((entry) => entry.driverId === mainDriverId && entry.date.startsWith(currentMonth)).length
      : 0;
    const metricCards = [
      ["Przebieg", `${vehicle.mileageKm ?? 0} km`],
      ["Aktywne trasy", String(vehicleRoutes.length)],
      ["Zlecenia w miesiącu", String(vehicleMonthlyOrders)],
      ["Status", isVehicleActive(vehicle) ? "Aktywny" : "Nieaktywny"]
    ];
    return (
      <section className="vehicle-detail-view">
        <header className="vehicle-detail-head">
          <div className="carrier-profile-brand">
            <div className="carrier-profile-logo"><Truck size={24} /></div>
            <div>
              <h2>{vehicle.registration || vehicle.model}</h2>
              <div className="carrier-profile-badges">
                <span className={clsx(isVehicleActive(vehicle) && "is-active")}>{isVehicleActive(vehicle) ? "Aktywny" : "Nieaktywny"}</span>
                <span>Pojazd przypisany do {selectedCarrierForProfile?.name ?? "przewoźnika"}</span>
              </div>
            </div>
          </div>
          <div className="carrier-profile-modal__actions">
            <button type="button" onClick={() => { setVehicleForm(vehicle); setCarrierProfileTab("vehicleForm"); }}>Edytuj</button>
            <button type="button" onClick={() => setCarrierProfileTab("history")}>Historia</button>
            <button type="button" onClick={() => setCarrierProfileTab("vehicles")}>Wróć</button>
          </div>
        </header>
        <div className="carrier-profile-kpis vehicle-detail-kpis">
          {metricCards.map(([label, value]) => <article key={label}><Truck size={20} /><span>{label}</span><strong>{value}</strong></article>)}
        </div>
        <div className="vehicle-detail-grid">
          <section className="vehicle-detail-media">
            {vehicle.imageDataUrl ? <img src={vehicle.imageDataUrl} alt="" /> : <VehiclePhotoPlaceholder size={90} />}
            <div className="vehicle-detail-pills">
              <span>{vehicle.type || "Typ"}</span>
              <span>{vehicle.loadKg || 0} kg</span>
              <span>{vehicle.pallets || 0} palet</span>
              <span>{vehicle.fuel || "Paliwo"}</span>
            </div>
          </section>
          <aside className="vehicle-detail-data">
            <h3>Podstawowe dane</h3>
            <dl>
              <div><dt>Model</dt><dd>{vehicle.model || "-"}</dd></div>
              <div><dt>Typ</dt><dd>{vehicle.type || "-"}</dd></div>
              <div><dt>Przewoźnik</dt><dd>{selectedCarrierForProfile?.name ?? "-"}</dd></div>
              <div><dt>Kierowca główny</dt><dd>{mainDriver?.name ?? "brak"}</dd></div>
              <div><dt>Numer VIN</dt><dd>{vehicle.vin || "-"}</dd></div>
              <div><dt>Rok produkcji</dt><dd>{vehicle.productionYear || "-"}</dd></div>
            </dl>
            <h3>Parametry</h3>
            <div className="vehicle-parameter-grid">
              <span>Ładowność <strong>{vehicle.loadKg || 0} kg</strong></span>
              <span>Paliwo <strong>{vehicle.fuel || "-"}</strong></span>
              <span>Palety <strong>{vehicle.pallets || 0}</strong></span>
              <span>Skrzynia <strong>{vehicle.gearbox || "-"}</strong></span>
              <span>DMC <strong>{vehicle.dmcKg || 0} kg</strong></span>
            </div>
            <h3>Dokumenty i terminy</h3>
            <div className="vehicle-doc-status">
              <span>OC <strong>{vehicle.insuranceDate || "do uzupełnienia"}</strong></span>
              <span>Badanie techniczne <strong>{vehicle.technicalDate || "do uzupełnienia"}</strong></span>
              <span>Tachograf <strong>{vehicle.tachographDate || "do uzupełnienia"}</strong></span>
              <span>Leasing <strong>{vehicle.leasingStatus || "brak danych"}</strong></span>
            </div>
          </aside>
        </div>
        <section className="vehicle-detail-bottom">
          <nav><button className="is-active">Dane</button><button>Trasy</button><button>Serwis</button><button>Dokumenty</button><button>Koszty</button></nav>
          <div className="carrier-driver-mini-list">
            {vehicleRoutes.map((route) => <div key={route.id}><strong>{route.name}</strong><span>{route.defaultStart} | {route.serviceType}</span></div>)}
            {!vehicleRoutes.length && <p>Brak aktywnych tras przypisanych do tego pojazdu.</p>}
          </div>
        </section>
      </section>
    );
  }

  function renderCarrierDocuments() {
    const decodeDocumentText = (document: CarrierDocument) => {
      if (document.previewText) return document.previewText;
      const [, payload = ""] = document.dataUrl.split(",");
      try {
        const decoded = document.dataUrl.includes(";base64,")
          ? atob(payload)
          : decodeURIComponent(payload);
        return decoded.slice(0, 12000);
      } catch {
        return "";
      }
    };

    const documentCanPreview = (document: CarrierDocument) =>
      Boolean(document.previewRows?.length) ||
      document.mimeType.startsWith("image/") ||
      document.mimeType.includes("pdf") ||
      document.mimeType.startsWith("text/") ||
      /\.(txt|csv|log|json|md|xlsx?)$/i.test(document.name);

    const renderDocumentPreview = (document: CarrierDocument) => {
      if (document.mimeType.startsWith("image/")) {
        return <img src={document.dataUrl} alt={document.name} />;
      }

      if (document.previewRows?.length) {
        return (
          <div className="carrier-document-sheet-preview">
            <table>
              <tbody>
                {document.previewRows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.slice(0, 12).map((cell, cellIndex) => <td key={cellIndex}>{cell || "-"}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      if (document.mimeType.startsWith("text/") || /\.(txt|csv|log|json|md)$/i.test(document.name)) {
        return <pre className="carrier-document-text-preview">{decodeDocumentText(document) || "Brak czytelnego podgladu tekstowego."}</pre>;
      }

      if (document.mimeType.includes("pdf")) {
        return <iframe title={"Podglad " + document.name} src={document.dataUrl} />;
      }

      return (
        <div className="carrier-document-preview-fallback">
          <FileText size={38} />
          <strong>Ten typ pliku najlepiej otworzyc w osobnym podgladzie.</strong>
          <button type="button" onClick={() => window.open(document.dataUrl, "_blank")}>Otworz podglad</button>
        </div>
      );
    };

    return (
      <section className="carrier-profile-tab-panel carrier-documents-tab">
        <div className="carrier-profile-section-head">
          <div>
            <h3>Dokumenty przewoznika</h3>
            <p>Foldery i pliki sa zapisane w stanie aplikacji, wiec po synchronizacji sa widoczne dla wszystkich.</p>
          </div>
          <div className="vehicle-toolbar document-toolbar">
            <NeonSearchInput
              value={newDocumentFolder}
              onChange={setNewDocumentFolder}
              placeholder="Nowy folder..."
              className="folder-input"
            />
            <button type="button" onClick={createDocumentFolder}>Utworz folder</button>
            <button type="button" className="is-save" onClick={() => carrierDocumentInputRef.current?.click()}>Importuj pliki</button>
          </div>
        </div>
        <input ref={carrierDocumentInputRef} hidden multiple type="file" onChange={(event) => void importCarrierDocuments(event.target.files)} />
        <div className="carrier-documents-layout">
          <aside>
            {selectedCarrierFolderNames.map((folder) => (
              <button type="button" className={clsx(folder === documentFolder && "is-active")} key={folder} onClick={() => { setDocumentFolder(folder); setSelectedCarrierDocumentId(""); setDocumentPage(1); }}>
                {folder}
              </button>
            ))}
          </aside>
          <div className="carrier-documents-main">
            <div className="carrier-document-grid">
              {pagedCarrierDocuments.map((document) => (
                <article className={clsx(document.id === selectedCarrierDocument?.id && "is-active")} key={document.id} onClick={() => setSelectedCarrierDocumentId(document.id)}>
                  <FileText size={24} />
                  <div>
                    <strong>{document.name}</strong>
                    <span>{document.folder} | {Math.max(1, Math.round(document.size / 1024))} KB</span>
                    <span>{document.uploadedAt}</span>
                  </div>
                  <div className="carrier-document-card-actions">
                    <button type="button" onClick={(event) => { event.stopPropagation(); setSelectedCarrierDocumentId(document.id); }}>Podglad</button>
                    <a href={document.dataUrl} download={document.name} onClick={(event) => event.stopPropagation()}>Pobierz</a>
                    <button type="button" className="is-danger" onClick={(event) => { event.stopPropagation(); deleteCarrierDocument(document.id); }}>Usun</button>
                  </div>
                </article>
              ))}
              {!selectedCarrierDocumentsInFolder.length && <div className="carrier-profile-empty">Brak plikow w folderze {documentFolder}.</div>}
            </div>
            {selectedCarrierDocumentsInFolder.length > carrierDocumentsPerPage && (
              <div className="carrier-document-pagination">
                <button type="button" disabled={activeDocumentPage <= 1} onClick={() => setDocumentPage((page) => Math.max(1, page - 1))}>{"<"}</button>
                <span>{activeDocumentPage} / {carrierDocumentPageCount}</span>
                <button type="button" disabled={activeDocumentPage >= carrierDocumentPageCount} onClick={() => setDocumentPage((page) => Math.min(carrierDocumentPageCount, page + 1))}>{">"}</button>
              </div>
            )}
          </div>
          {selectedCarrierDocument && (
            <aside className="carrier-document-preview">
              <div className="carrier-document-preview-frame">
                {renderDocumentPreview(selectedCarrierDocument)}
              </div>
              <strong>{selectedCarrierDocument.name}</strong>
              <span>{selectedCarrierDocument.folder}</span>
              <span>{Math.max(1, Math.round(selectedCarrierDocument.size / 1024))} KB</span>
              <span>{selectedCarrierDocument.uploadedAt}</span>
              <div className="carrier-document-preview-actions">
                <button type="button" onClick={() => window.open(selectedCarrierDocument.dataUrl, "_blank")}>Otworz</button>
                <a href={selectedCarrierDocument.dataUrl} download={selectedCarrierDocument.name}>Pobierz</a>
                <button type="button" className="is-danger" onClick={() => deleteCarrierDocument(selectedCarrierDocument.id)}>Usun</button>
              </div>
            </aside>
          )}
        </div>
      </section>
    );
  }

  function renderCarrierData() {
    return (
      <section className="carrier-profile-tab-panel carrier-data-tab">
        <div className="carrier-profile-section-head">
          <div>
            <h3>Dane przewoźnika</h3>
            <p>Edytuj dane, które potem widać w profilu przewoźnika i przy wysyłce przypomnień.</p>
          </div>
          <button type="button" className="is-save" onClick={saveCarrierProfile}>Zapisz dane</button>
        </div>
        <div className="carrier-data-cards">
          {[
            ["Nazwa przewoźnika", carrierEditForm.name, "name"],
            ["Telefon", carrierEditForm.phone, "phone"],
            ["E-mail", carrierEditForm.email, "email"],
            ["Adres", carrierEditForm.address, "address"],
            ["Osoba kontaktowa", carrierEditForm.contactPerson, "contactPerson"],
            ["Telefon kontaktowy", carrierEditForm.contactPhone, "contactPhone"],
            ["Typ współpracy", carrierEditForm.cooperationType, "cooperationType"],
            ["Uwagi", carrierEditForm.notes, "notes"]
          ].map(([label, value, key]) => (
            <label key={key}>
              <span>{label}</span>
              <input value={value} onChange={(event) => setCarrierEditForm((current) => ({ ...current, [key]: event.target.value }))} />
            </label>
          ))}
          <label>
            <span>Status</span>
            <ActiveCheck checked={carrierEditForm.active} label={carrierEditForm.active ? "Aktywny" : "Nieaktywny"} onChange={(checked) => setCarrierEditForm((current) => ({ ...current, active: checked }))} />
          </label>
        </div>
        <h3>Kierowcy przewoźnika</h3>
        <div className="carrier-driver-mini-list carrier-driver-mini-list--grid">
          {selectedCarrierDriversForProfile.map((driver) => (
            <div key={driver.id}>
              <strong>{driver.name}</strong>
              <span>{driver.type} | {vehicleDisplayName(appState.vehicles.find((vehicle) => vehicle.id === driver.vehicleId)) || driver.vehicle || "brak pojazdu"}</span>
            </div>
          ))}
          {!selectedCarrierDriversForProfile.length && <p>Brak aktywnych kierowców przypisanych do przewoźnika.</p>}
        </div>
      </section>
    );
  }

  function renderCarrierDataView() {
    if (!selectedCarrierForProfile) return <div className="carrier-profile-empty">Brak wybranego przewoznika.</div>;
    const fields: Array<{ label: string; value: string; key: keyof CarrierFormDraft }> = [
      { label: "Nazwa przewoznika", value: carrierEditForm.name, key: "name" },
      { label: "Telefon", value: carrierEditForm.phone, key: "phone" },
      { label: "E-mail", value: carrierEditForm.email, key: "email" },
      { label: "Adres", value: carrierEditForm.address, key: "address" },
      { label: "Osoba kontaktowa", value: carrierEditForm.contactPerson, key: "contactPerson" },
      { label: "Telefon kontaktowy", value: carrierEditForm.contactPhone, key: "contactPhone" },
      { label: "Typ wspolpracy", value: carrierEditForm.cooperationType, key: "cooperationType" },
      { label: "Uwagi", value: carrierEditForm.notes, key: "notes" }
    ];
    const displayRows = [
      ["Nazwa przewoznika", selectedCarrierForProfile.name],
      ["Telefon", selectedCarrierForProfile.phone || "brak telefonu"],
      ["E-mail", selectedCarrierForProfile.email || "brak e-maila"],
      ["Adres", selectedCarrierForProfile.address || "brak adresu"],
      ["Osoba kontaktowa", selectedCarrierForProfile.contactPerson || "brak danych"],
      ["Telefon kontaktowy", selectedCarrierForProfile.contactPhone || "brak telefonu"],
      ["Typ wspolpracy", selectedCarrierForProfile.cooperationType || "Wspolpraca stala"],
      ["Uwagi", selectedCarrierForProfile.notes || "brak uwag"],
      ["Status", parseActiveFlag(selectedCarrierForProfile.active, true) ? "Aktywny" : "Nieaktywny"]
    ];

    return (
      <section className="carrier-profile-tab-panel carrier-data-tab">
        <div className="carrier-profile-section-head">
          <div>
            <h3>Dane przewoznika</h3>
            <p>Dane widoczne w profilu przewoznika, przypomnieniach i przypisaniach kierowcow.</p>
          </div>
          {carrierDataEditing ? (
            <div className="carrier-profile-modal__actions">
              <button type="button" className="is-save" onClick={saveCarrierProfile}>Zapisz dane</button>
              <button type="button" onClick={() => { setCarrierEditForm(carrierDraftFrom(selectedCarrierForProfile)); setCarrierDataEditing(false); }}>Anuluj</button>
            </div>
          ) : (
            <button type="button" onClick={() => { setCarrierEditForm(carrierDraftFrom(selectedCarrierForProfile)); setCarrierDataEditing(true); }}>Edytuj</button>
          )}
        </div>
        {carrierDataEditing ? (
          <div className="carrier-data-cards">
            {fields.map((field) => (
              <label key={field.key}>
                <span>{field.label}</span>
                <input value={field.value} onChange={(event) => setCarrierEditForm((current) => ({ ...current, [field.key]: event.target.value }))} />
              </label>
            ))}
            <label>
              <span>Status</span>
              <ActiveCheck checked={carrierEditForm.active} label={carrierEditForm.active ? "Aktywny" : "Nieaktywny"} onChange={(checked) => setCarrierEditForm((current) => ({ ...current, active: checked }))} />
            </label>
          </div>
        ) : (
          <div className="carrier-data-cards carrier-data-cards--read">
            {displayRows.map(([label, value]) => (
              <div key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        )}
        <h3>Kierowcy przewoznika</h3>
        <div className="carrier-driver-mini-list carrier-driver-mini-list--grid">
          {selectedCarrierDriversForProfile.map((driver) => {
            const vehicle = appState.vehicles.find((candidate) => candidate.id === driver.vehicleId);
            return (
              <div key={driver.id}>
                <strong>{driver.name}</strong>
                <span>{driver.type} | {vehicleDisplayName(vehicle) || driver.vehicle || "brak pojazdu"}</span>
              </div>
            );
          })}
          {!selectedCarrierDriversForProfile.length && <p>Brak aktywnych kierowcow przypisanych do przewoznika.</p>}
        </div>
      </section>
    );
  }

  function renderCarrierFrequencyView(selectedCarrier: Carrier) {
    const selectedStats = carrierAttendanceStats(selectedCarrier.id, carrierPeriod);
    const periodInputValue = carrierPeriod === "miesiac" || carrierPeriod === "kwartal"
      ? carrierFrequencyDate.slice(0, 7)
      : carrierFrequencyDate;
    const updateFrequencyDate = (value: string) => {
      if (!value) return;
      setCarrierFrequencyDate(value.length === 7 ? `${value}-01` : value);
    };
    const driverStats = (driver: Driver) => {
      const range = carrierPeriodRange(carrierPeriod);
      const entries = range.dates.map((date) =>
        appState.schedule.find((entry) => entry.driverId === driver.id && entry.date === date) ??
        syntheticShortageEntry(appState, driver, date)
      );
      const work = entries.filter((entry) => entry.status === "P").length;
      const planned = entries.filter((entry) => entry.status === "PLAN").length;
      const free = entries.filter((entry) => entry.status === "W" || entry.status === "U").length;
      const absent = entries.filter((entry) => entry.status === "L4" || entry.status === "NIEOBECNY").length;
      const missing = entries.filter((entry) => entry.status === "BRAK").length;
      const total = Math.max(1, entries.length);
      return { work, planned, free, absent, missing, total, percent: Math.round(work / total * 100) };
    };

    return (
      <section className="carrier-profile-tab-panel carrier-frequency-tab">
        <div className="carrier-profile-section-head">
          <div>
            <h3>Frekwencja</h3>
            <p>Okres, wykresy i szczegoly kierowcow dla przewoznika {selectedCarrier.name}.</p>
          </div>
          <div className="carrier-frequency-controls">
            <div className="carrier-period-switch">
              {carrierChartPeriods.map((period) => (
                <button className={clsx(carrierPeriod === period && "is-active")} type="button" key={period} onClick={() => setCarrierPeriod(period)}>
                  {carrierPeriodLabels[period]}
                </button>
              ))}
            </div>
            <label className="carrier-frequency-picker">
              <span>{carrierPeriod === "kwartal" ? "Miesiac kwartalu" : carrierPeriodLabels[carrierPeriod]}</span>
              <input
                type={carrierPeriod === "miesiac" || carrierPeriod === "kwartal" ? "month" : "date"}
                value={periodInputValue}
                onChange={(event) => updateFrequencyDate(event.target.value)}
              />
            </label>
          </div>
        </div>
        <div className="carrier-frequency-charts carrier-frequency-charts--profile">
          {carrierChartPeriods.map((period) => {
            const stats = carrierAttendanceStats(selectedCarrier.id, period);
            return (
              <article className={clsx(carrierPeriod === period && "is-active")} key={period} onClick={() => setCarrierPeriod(period)}>
                <h3>{carrierPeriodLabels[period]}</h3>
                <div className="carrier-donut" style={carrierDonutStyle(stats)}>
                  <strong>{carrierWorkPercent(stats)}%</strong>
                  <span>{stats.work}/{stats.total}</span>
                </div>
                <p>{stats.from} - {stats.to}</p>
                <div className="carrier-segments" aria-label="Legenda frekwencji">
                  <span className="is-work" title={`Praca: ${stats.work}`} aria-label={`Praca: ${stats.work}`} />
                  <span className="is-plan" title={`Plan: ${stats.planned}`} aria-label={`Plan: ${stats.planned}`} />
                  <span className="is-free" title={`Wolne/urlop: ${stats.free}`} aria-label={`Wolne/urlop: ${stats.free}`} />
                  <span className="is-absent" title={`L4 / nieobecni: ${stats.absent}`} aria-label={`L4 / nieobecni: ${stats.absent}`} />
                  <span className="is-missing" title={`Braki: ${stats.missing}`} aria-label={`Braki: ${stats.missing}`} />
                </div>
                <dl>
                  <div><dt>Kierowcy</dt><dd>{stats.drivers}</dd></div>
                  <div><dt>Praca</dt><dd>{stats.work}</dd></div>
                  <div><dt>Plan</dt><dd>{stats.planned}</dd></div>
                  <div><dt>Wolne</dt><dd>{stats.free}</dd></div>
                  <div><dt>L4 / nieobecni</dt><dd>{stats.absent}</dd></div>
                  <div><dt>Braki</dt><dd>{stats.missing}</dd></div>
                </dl>
              </article>
            );
          })}
        </div>
        <section className="carrier-frequency-summary">
          <div className="carrier-donut carrier-donut--large" style={carrierDonutStyle(selectedStats)}>
            <strong>{carrierWorkPercent(selectedStats)}%</strong>
            <span>{selectedStats.work}/{selectedStats.total}</span>
          </div>
          <div>
            <h3>Podsumowanie okresu</h3>
            <p>{selectedStats.from} - {selectedStats.to}</p>
            <div className="carrier-frequency-legend">
              <span className="is-work">Praca: {selectedStats.work}</span>
              <span className="is-plan">Plan: {selectedStats.planned}</span>
              <span className="is-free">Wolne: {selectedStats.free}</span>
              <span className="is-absent">L4 / nieobecni: {selectedStats.absent}</span>
              <span className="is-missing">Braki: {selectedStats.missing}</span>
            </div>
          </div>
        </section>
        <div className="carrier-frequency-table">
          <div className="carrier-frequency-table__head">
            <span>Kierowca</span><span>Typ</span><span>Praca</span><span>Wolne</span><span>L4</span><span>Braki</span><span>Frekwencja</span><span>Status</span>
          </div>
          {selectedCarrierDriversForProfile.map((driver) => {
            const stats = driverStats(driver);
            return (
              <div key={driver.id}>
                <span>{driver.name}</span>
                <span>{driver.type}</span>
                <span>{stats.work}</span>
                <span>{stats.free}</span>
                <span>{stats.absent}</span>
                <span>{stats.missing}</span>
                <span>{stats.percent}%</span>
                <span className={clsx("vehicle-status", stats.missing ? "is-inactive" : "is-active")}>{stats.missing ? "Braki" : "Aktywny"}</span>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  function renderCarrierProfileModal() {
    if (!carrierProfileOpen || !selectedCarrierForProfile) return null;
    const initials = selectedCarrierForProfile.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "P";
    const history = appState.archive
      .filter((item) => item.title.includes(selectedCarrierForProfile.name) || item.details?.includes(selectedCarrierForProfile.name))
      .slice(0, 12);

    return (
      <div className="carrier-profile-backdrop" role="dialog" aria-modal="true" onMouseDown={(event) => {
        if (event.target === event.currentTarget) setCarrierProfileOpen(false);
      }}>
        <section className="carrier-profile-modal">
          <header className="carrier-profile-modal__head">
            <div className="carrier-profile-brand">
              <button type="button" className="carrier-profile-logo" title="Kliknij, aby zmienić logo" onClick={() => carrierLogoInputRef.current?.click()}>
                {selectedCarrierForProfile.logoDataUrl ? <img src={selectedCarrierForProfile.logoDataUrl} alt="" /> : initials}
              </button>
              <input ref={carrierLogoInputRef} hidden type="file" accept="image/*" onChange={(event) => void loadCarrierLogo(event.target.files?.[0])} />
              <div>
                <h2>{selectedCarrierForProfile.name}</h2>
                <div className="carrier-profile-badges">
                  <span className={clsx(parseActiveFlag(selectedCarrierForProfile.active, true) && "is-active")}>{parseActiveFlag(selectedCarrierForProfile.active, true) ? "Aktywny" : "Nieaktywny"}</span>
                  <span>{selectedCarrierForProfile.cooperationType || "Współpraca stała"}</span>
                </div>
              </div>
            </div>
            <div className="carrier-profile-modal__actions">
              <button type="button" onClick={() => { setCarrierEditForm(carrierDraftFrom(selectedCarrierForProfile)); setCarrierDataEditing(true); setCarrierProfileTab("data"); }}>Edytuj</button>
              <button type="button" className="is-save" onClick={() => { resetVehicleForm(selectedCarrierForProfile.id); setCarrierProfileTab("vehicleForm"); }}>Dodaj pojazd</button>
              <button type="button" onClick={() => setCarrierProfileOpen(false)}>Zamknij</button>
            </div>
          </header>

          <div className="carrier-profile-kpis">
            <article><Truck size={22} /><span>Pojazdy</span><strong>{selectedCarrierVehiclesForProfile.length}</strong></article>
            <article><RouteIcon size={22} /><span>Aktywne trasy</span><strong>{selectedCarrierRoutesForProfile.length}</strong></article>
            <article><UsersRound size={22} /><span>Kierowcy</span><strong>{selectedCarrierDriversForProfile.length}</strong></article>
            <article><CheckCircle2 size={22} /><span>Status</span><strong>{parseActiveFlag(selectedCarrierForProfile.active, true) ? "Aktywny" : "Nieaktywny"}</strong></article>
          </div>

          <div className="carrier-profile-body">
            <aside className="carrier-profile-details">
              <h3>Dane przewoźnika</h3>
              <dl>
                <div><dt>Nazwa przewoźnika</dt><dd>{selectedCarrierForProfile.name}</dd></div>
                <div><dt>Telefon</dt><dd>{selectedCarrierForProfile.phone || "brak telefonu"}</dd></div>
                <div><dt>E-mail</dt><dd>{selectedCarrierForProfile.email || "brak e-maila"}</dd></div>
                <div><dt>Adres</dt><dd>{selectedCarrierForProfile.address || "brak adresu"}</dd></div>
                <div><dt>Typ współpracy</dt><dd>{selectedCarrierForProfile.cooperationType || "Współpraca stała"}</dd></div>
                <div><dt>Status</dt><dd><span className={clsx("vehicle-status", parseActiveFlag(selectedCarrierForProfile.active, true) ? "is-active" : "is-inactive")}>{parseActiveFlag(selectedCarrierForProfile.active, true) ? "Aktywny" : "Nieaktywny"}</span></dd></div>
                <div><dt>Uwagi</dt><dd>{selectedCarrierForProfile.notes || "brak uwag"}</dd></div>
              </dl>
              <div className="carrier-profile-side-actions">
                <button type="button" onClick={downloadVehicleTemplate}>Pobierz szablon importu</button>
                <label className="file-button">Importuj pojazdy<input hidden type="file" accept=".csv,.txt,.xlsx" onChange={(event) => void importVehicleFile(event.target.files?.[0])} /></label>
              </div>
            </aside>

            <main className="carrier-profile-content">
              <nav className="carrier-profile-tabs" aria-label="Profil przewoźnika">
                {[
                  ["data", "Dane"],
                  ["vehicles", "Pojazdy"],
                  ["documents", "Dokumenty"],
                  ["history", "Historia"],
                  ["frequency", "Frekwencja"]
                ].map(([key, label]) => (
                  <button
                    className={clsx(carrierProfileTab === key && "is-active")}
                    key={key}
                    type="button"
                    onClick={() => setCarrierProfileTab(key as CarrierProfileTab)}
                  >
                    {label}
                  </button>
                ))}
              </nav>

              {carrierProfileTab === "data" && renderCarrierDataView()}
              {carrierProfileTab === "vehicles" && renderVehicleWorkspace(selectedCarrierForProfile)}
              {carrierProfileTab === "vehicleForm" && renderVehicleEditor(selectedCarrierForProfile)}
              {carrierProfileTab === "vehicleDetails" && renderVehicleDetails(selectedVehicleForProfile)}
              {carrierProfileTab === "documents" && renderCarrierDocuments()}
              {carrierProfileTab === "frequency" && renderCarrierFrequencyView(selectedCarrierForProfile)}
              {carrierProfileTab === "history" && (
                <section className="carrier-profile-tab-panel">
                  <h3>Historia</h3>
                  <div className="carrier-driver-mini-list">
                    {history.map((item) => (
                      <div key={item.id}>
                        <strong>{item.title}</strong>
                        <span>{item.archivedAt} | {item.owner} | {item.category}</span>
                      </div>
                    ))}
                    {!history.length && <p>Brak historii dla tego przewoźnika.</p>}
                  </div>
                </section>
              )}
            </main>
          </div>
        </section>
      </div>
    );
  }

  function sendNotification() {
    if (!notificationForm.message.trim()) {
      window.alert("Wpisz treść powiadomienia.");
      return;
    }

    const recipient =
      notificationForm.recipient === "ROLA"
        ? `ROLA:${notificationForm.role}`
        : notificationForm.recipient === "UZYTKOWNIK"
          ? `USER:${appState.users.find((account) => account.id === notificationForm.userId)?.login ?? ""}`
          : notificationForm.recipient === "PRZEWOZNIK"
            ? `CARRIER:${notificationForm.carrierId}`
            : notificationForm.recipient;

    onAppStateChange((current) => ({
      ...current,
      notifications: [
        {
          id: createId("not"),
          recipient,
          message: notificationForm.message,
          createdAt: nowStamp(),
          read: false,
          readBy: []
        },
        ...current.notifications
      ],
      archive: [archiveItem("Powiadomienia", `Wysłano powiadomienie: ${notificationForm.message.slice(0, 40)}`, "Stobima"), ...current.archive]
    }));
    setNotificationForm((current) => ({ ...current, message: "" }));
  }

  function escapeHtml(value: string) {
    return value.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char] ?? char));
  }

  function remindCarriers(carriersToRemind: Carrier[]) {
    const today = new Date();
    const weekStartDate = new Date(today);
    const day = (weekStartDate.getDay() + 6) % 7;
    weekStartDate.setDate(today.getDate() - day);
    const nextEnd = new Date(weekStartDate);
    nextEnd.setDate(weekStartDate.getDate() + 13);
    const dates = dateRange(weekStartDate.toISOString().slice(0, 10), nextEnd.toISOString().slice(0, 10));
    const sections = carriersToRemind.map((carrier) => {
      const carrierDrivers = appState.drivers.filter((driver) => isDriverActive(driver) && driver.carrierId === carrier.id);
      return `<h2>Grafik do uzupelnienia - ${escapeHtml(carrier.name)}</h2>
      <table><thead><tr><th>Kierowca</th>${dates.map((date) => `<th>${date}<br>${dayName(date)}</th>`).join("")}</tr></thead><tbody>
      ${carrierDrivers.map((driver) => `<tr><td>${escapeHtml(driver.name)}</td>${dates.map((date) => {
        const entry = appState.schedule.find((candidate) => candidate.driverId === driver.id && candidate.date === date);
        return `<td class="${entry ? "" : "hint"}">${entry?.status === "P" ? "X" : ""}</td>`;
      }).join("")}</tr>`).join("")}
      </tbody></table>`;
    }).join("<br style='page-break-after:always'>");
    const html = `<!doctype html><html><head><meta charset="utf-8" />
      <style>
        table{border-collapse:collapse;font-family:Arial,sans-serif;font-size:12px}
        th,td{border:1px solid #9ca3af;padding:6px 8px;min-width:118px;text-align:center;white-space:nowrap}
        th:first-child,td:first-child{min-width:230px;text-align:left;font-weight:700}
        .hint{background:#fff4e6;font-weight:700}
        h2{font-family:Arial,sans-serif;margin:18px 0 10px}
      </style></head><body>
      <p>Wpisz X w dniach pracy kierowcy. Puste pole oznacza wolne/brak pracy.</p>
      ${sections}</body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `grafik-do-uzupelnienia-${carriersToRemind.length === 1 ? carriersToRemind[0].name : "wszyscy-przewoznicy"}-${Date.now()}.xls`;
    link.click();
    URL.revokeObjectURL(url);
    const emails = carriersToRemind.map((carrier) => carrier.email).filter(Boolean);
    const mailTarget = carriersToRemind.length === 1 ? emails[0] ?? "" : "";
    const bcc = carriersToRemind.length > 1 ? `&bcc=${encodeURIComponent(emails.join(";"))}` : "";
    window.location.href = `mailto:${mailTarget}?subject=${encodeURIComponent("Przypomnienie o grafiku SNG")}${bcc}&body=${encodeURIComponent("Wygenerowałem czytelny plik Excel z kierowcami i datami. Dodaj pobrany plik jako załącznik do wiadomości.")}`;
  }

  function remindCarrier(carrier: Carrier) {
    remindCarriers([carrier]);
  }

  function remindAllCarriers() {
    remindCarriers(appState.carriers.filter((carrier) => carrier.email));
  }

  const carrierPeriodLabels: Record<CarrierPeriod, string> = {
    dzien: "Dzień",
    tydzien: "Tydzień",
    miesiac: "Miesiąc",
    kwartal: "Kwartał"
  };
  const carrierChartPeriods: CarrierPeriod[] = ["dzien", "tydzien", "miesiac", "kwartal"];

  function carrierPeriodRange(period: CarrierPeriod, anchorDate = carrierFrequencyDate) {
    const anchor = new Date(`${anchorDate || new Date().toISOString().slice(0, 10)}T12:00:00`);
    const safeAnchor = Number.isNaN(anchor.getTime()) ? new Date() : anchor;
    const start = new Date(safeAnchor);
    const end = new Date(safeAnchor);

    if (period === "tydzien") {
      const day = (safeAnchor.getDay() + 6) % 7;
      start.setDate(safeAnchor.getDate() - day);
      end.setTime(start.getTime());
      end.setDate(start.getDate() + 6);
    } else if (period === "miesiac") {
      start.setDate(1);
      end.setTime(start.getTime());
      end.setMonth(start.getMonth() + 1, 0);
    } else if (period === "kwartal") {
      const quarterStartMonth = Math.floor(safeAnchor.getMonth() / 3) * 3;
      start.setMonth(quarterStartMonth, 1);
      end.setTime(start.getTime());
      end.setMonth(quarterStartMonth + 3, 0);
    }

    const from = start.toISOString().slice(0, 10);
    const to = end.toISOString().slice(0, 10);
    return { from, to, dates: dateRange(from, to) };
  }

  function carrierAttendanceStats(carrierId: string, period: CarrierPeriod, anchorDate = carrierFrequencyDate) {
    const drivers = appState.drivers.filter((driver) => driver.carrierId === carrierId && isDriverActive(driver));
    const range = carrierPeriodRange(period, anchorDate);
    const entries = drivers.flatMap((driver) =>
      range.dates.map((date) =>
        appState.schedule.find((entry) => entry.driverId === driver.id && entry.date === date) ??
        syntheticShortageEntry(appState, driver, date)
      )
    );
    const work = entries.filter((entry) => entry.status === "P").length;
    const planned = entries.filter((entry) => entry.status === "PLAN").length;
    const free = entries.filter((entry) => entry.status === "W" || entry.status === "U").length;
    const absent = entries.filter((entry) => entry.status === "L4" || entry.status === "NIEOBECNY").length;
    const missing = entries.filter((entry) => entry.status === "BRAK").length;
    const total = Math.max(1, entries.length);
    return { ...range, drivers: drivers.length, work, planned, free, absent, missing, total };
  }

  function carrierFrequency(carrierId: string) {
    return carrierAttendanceStats(carrierId, carrierPeriod, carrierFrequencyDate).work;
  }

  function carrierWorkPercent(stats: ReturnType<typeof carrierAttendanceStats>) {
    return Math.round(stats.work / Math.max(1, stats.total) * 100);
  }

  function carrierDonutStyle(stats: ReturnType<typeof carrierAttendanceStats>) {
    const workPct = stats.work / stats.total * 100;
    const plannedPct = stats.planned / stats.total * 100;
    const freePct = stats.free / stats.total * 100;
    const absentPct = stats.absent / stats.total * 100;
    return {
      background: `radial-gradient(circle, #101824 0 44%, transparent 45%), conic-gradient(#58d77b 0 ${workPct}%, #ff9f33 ${workPct}% ${workPct + plannedPct}%, #2f80ed ${workPct + plannedPct}% ${workPct + plannedPct + freePct}%, #ef6464 ${workPct + plannedPct + freePct}% ${workPct + plannedPct + freePct + absentPct}%, #9a3842 ${workPct + plannedPct + freePct + absentPct}% 100%)`
    };
  }

  function renderMainPanel() {
    if (tab === "queue" || tab === "ops") {
      const queue = appState.requests.filter((request) => tab === "queue" ? request.status === "oczekuje" : request.type !== "REG" && request.type !== "RESET");
      return (
        <>
          <PanelTitle title={tab === "queue" ? "OCZEKUJĄCE ZGŁOSZENIA" : "ZGŁOSZENIA OPERACYJNE"} />
          <p>Rejestracje, resety hasła, braki i prośby o zmianę danych.</p>
          <div className="vba-list-box vba-list-box--admin">
            {queue.map((request) => (
              <button className={clsx(request.id === selectedRequest?.id && "is-active")} type="button" key={request.id} onClick={() => setSelectedRequestId(request.id)}>
                {request.id} | {request.type} | {request.sender} | {request.subject} | {request.status}
              </button>
            ))}
          </div>
          <PanelTitle title="SZCZEGÓŁY" />
          <pre className="vba-description-box">{selectedRequest ? `${selectedRequest.subject}\nNadawca: ${selectedRequest.sender}\nData: ${selectedRequest.createdAt}\nObiekt: ${selectedRequest.target}\n${selectedRequest.description}\nStatus: ${selectedRequest.status}` : "Zaznacz zgłoszenie z listy."}</pre>
          {selectedRequest?.type === "RESET" && (
            <label className="admin-inline-field">Nowe hasło <input type="password" value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} /></label>
          )}
          <div className="vba-footer-actions">
            <button>Odśwież</button>
            <button className="is-save" onClick={acceptRequest}>Akceptuj i zastosuj</button>
            <button className="is-danger" onClick={rejectRequest}>Odrzuć</button>
          </div>
        </>
      );
    }

    if (tab === "accounts") {
      return (
        <>
          <PanelTitle title="UŻYTKOWNICY SYSTEMU" />
          <p>Wybierz konto, nadaj rolę, aktywuj przez kolejkę rejestracji albo usuń konto.</p>
          <div className="vba-list-box vba-list-box--admin">
            {appState.users.map((account, index) => (
              <button className={clsx(account.id === selectedUser?.id && "is-active")} key={account.id} onClick={() => selectUser(account)}>
                #{index + 1} | {account.login} | {account.status} | {account.lastLogin ?? "-"} | {roleLabel(account.role)}
              </button>
            ))}
          </div>
          <div className="vba-admin-actions">
            <select value={roleDraft} onChange={(event) => setRoleDraft(event.target.value as Role)}>
              {allRoleOptions.map((role) => <option value={role.name} key={role.name}>{role.label}</option>)}
            </select>
            <button className="is-save" onClick={applyRole}>Nadaj wybraną rolę</button>
            <button onClick={onResetLocalState}>Odśwież</button>
            <button onClick={toggleUserStatus}>{selectedUser?.status === "aktywny" ? "Dezaktywuj" : "Aktywuj"}</button>
            <button className="is-danger" onClick={deleteUser}>Usuń</button>
          </div>
          <PanelTitle title="ZADANIA UŻYTKOWNIKA" />
          <div className="vba-form-grid">
            <label className="span-2">Treść zadania <input value={taskDraft.title} onChange={(event) => setTaskDraft((current) => ({ ...current, title: event.target.value }))} placeholder={`Zadanie dla ${selectedUser?.login ?? "użytkownika"}`} /></label>
            <label>Termin <select value={taskDraft.due} onChange={(event) => setTaskDraft((current) => ({ ...current, due: event.target.value as TaskItem["due"] }))}>
              <option value="today">Dzisiaj</option><option value="tomorrow">Jutro</option><option value="week">Ten tydzień</option><option value="nextWeek">Następny tydzień</option>
            </select></label>
            <label>Priorytet <select value={taskDraft.priority} onChange={(event) => setTaskDraft((current) => ({ ...current, priority: event.target.value as TaskItem["priority"] }))}>
              <option value="niski">Niski</option><option value="normalny">Normalny</option><option value="wysoki">Wysoki</option>
            </select></label>
            <button className="is-save" onClick={assignTaskToSelectedUser}>Przypisz zadanie</button>
          </div>
        </>
      );
    }

    if (tab === "carriers") {
      const selectedCarrier = selectedCarrierForProfile;
      return (
        <>
          <PanelTitle title="PRZEWOZNICY" />
          <p className="carrier-open-hint">Kliknij przewoźnika dwukrotnie, żeby otworzyć osobne okno z pojazdami, galerią i importem szablonu.</p>
          <div className="vba-admin-actions">
            <select value={selectedCarrierId} onChange={(event) => selectCarrier(event.target.value)}>
              {appState.carriers.map((carrier) => <option value={carrier.id} key={carrier.id}>{carrier.name}</option>)}
            </select>
            <button className="is-save" onClick={() => {
              const carrier = appState.carriers.find((candidate) => candidate.id === selectedCarrierId);
              if (carrier) remindCarrier(carrier);
            }}>Wyślij przypomnienie o grafiku</button>
            <button onClick={remindAllCarriers}>Wyślij formatkę do wszystkich</button>
          </div>
          <div className="vba-list-box">
            {appState.carriers.map((carrier) => (
              <button
                className={clsx(carrier.id === selectedCarrierId && "is-active")}
                key={carrier.id}
                onClick={() => selectCarrier(carrier.id)}
                onDoubleClick={() => openCarrierProfile(carrier.id)}
                title="Kliknij dwukrotnie, aby otworzyć okno przewoźnika"
              >
                {carrier.name} | {appState.drivers.filter((driver) => driver.carrierId === carrier.id && isDriverActive(driver)).length} kier. | {carrier.email} | {carrier.phone}
              </button>
            ))}
          </div>
          <div className="vba-admin-actions">
            <button type="button" className="is-save" disabled={!selectedCarrier} onClick={() => selectedCarrier && openCarrierProfile(selectedCarrier.id)}>Otwórz okno przewoźnika</button>
          </div>
          <button className="is-danger" onClick={() => deleteCarrier(selectedCarrierId)}>Usuń zaznaczonego</button>
          <PanelTitle title="NOWY PRZEWOŹNIK" />
          <div className="vba-form-grid">
            <label>Nazwa <input value={carrierForm.name} onChange={(event) => setCarrierForm((current) => ({ ...current, name: event.target.value }))} /></label>
            <label>E-mail <input value={carrierForm.email} onChange={(event) => setCarrierForm((current) => ({ ...current, email: event.target.value }))} /></label>
            <label>Telefon <input value={carrierForm.phone} onChange={(event) => setCarrierForm((current) => ({ ...current, phone: event.target.value }))} /></label>
            <label>Adres <input value={carrierForm.address} onChange={(event) => setCarrierForm((current) => ({ ...current, address: event.target.value }))} /></label>
            <label>Osoba kontaktowa <input value={carrierForm.contactPerson} onChange={(event) => setCarrierForm((current) => ({ ...current, contactPerson: event.target.value }))} /></label>
            <label>Telefon kontaktowy <input value={carrierForm.contactPhone} onChange={(event) => setCarrierForm((current) => ({ ...current, contactPhone: event.target.value }))} /></label>
            <label>Typ współpracy <input value={carrierForm.cooperationType} onChange={(event) => setCarrierForm((current) => ({ ...current, cooperationType: event.target.value }))} /></label>
            <label>Aktywny <ActiveCheck checked={carrierForm.active} label={carrierForm.active ? "Aktywny" : "Nieaktywny"} onChange={(checked) => setCarrierForm((current) => ({ ...current, active: checked }))} /></label>
            <label className="span-2">Uwagi <input value={carrierForm.notes} onChange={(event) => setCarrierForm((current) => ({ ...current, notes: event.target.value }))} /></label>
            <button className="is-save" onClick={saveCarrier}>Zapisz przewoźnika</button>
          </div>
          {false && selectedCarrier && (
            <section className="carrier-frequency-panel">
              <PanelTitle title={`WYKRESY FREKWENCJI: ${selectedCarrier.name}`} />
              <div className="carrier-frequency-charts">
                {carrierChartPeriods.map((period) => {
                  const stats = carrierAttendanceStats(selectedCarrier.id, period);
                  return (
                    <article className={clsx(carrierPeriod === period && "is-active")} key={period} onClick={() => setCarrierPeriod(period)}>
                      <h3>{carrierPeriodLabels[period]}</h3>
                      <div className="carrier-donut" style={carrierDonutStyle(stats)}>
                        <strong>{carrierWorkPercent(stats)}%</strong>
                        <span>{stats.work}/{stats.total}</span>
                      </div>
                      <p>{stats.from} - {stats.to}</p>
                      <div className="carrier-segments" aria-label="Legenda frekwencji">
                        <span className="is-work" title={`Praca: ${stats.work}`} aria-label={`Praca: ${stats.work}`} />
                        <span className="is-plan" title={`Plan: ${stats.planned}`} aria-label={`Plan: ${stats.planned}`} />
                        <span className="is-free" title={`Wolne/urlop: ${stats.free}`} aria-label={`Wolne/urlop: ${stats.free}`} />
                        <span className="is-absent" title={`L4 / nieobecni: ${stats.absent}`} aria-label={`L4 / nieobecni: ${stats.absent}`} />
                        <span className="is-missing" title={`Braki: ${stats.missing}`} aria-label={`Braki: ${stats.missing}`} />
                      </div>
                      <dl>
                        <div><dt>Kierowcy</dt><dd>{stats.drivers}</dd></div>
                        <div><dt>Praca</dt><dd>{stats.work}</dd></div>
                        <div><dt>Plan</dt><dd>{stats.planned}</dd></div>
                        <div><dt>Wolne</dt><dd>{stats.free}</dd></div>
                        <div><dt>L4 / nieobecni</dt><dd>{stats.absent}</dd></div>
                        <div><dt>Braki</dt><dd>{stats.missing}</dd></div>
                      </dl>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </>
      );
    }

    if (tab === "notifications") {
      return (
        <>
          <PanelTitle title="WYŚLIJ POWIADOMIENIE" />
          <div className="vba-form-grid">
            <label>Odbiorca <select value={notificationForm.recipient} onChange={(event) => setNotificationForm((current) => ({ ...current, recipient: event.target.value }))}>
              <option value="WSZYSCY">Wszyscy użytkownicy</option>
              <option value="ROLA">Wybrana rola</option>
              <option value="UZYTKOWNIK">Wybrany użytkownik</option>
              <option value="PRZEWOZNIK">Wybrany przewoźnik</option>
              <option value="PRZEWOZNICY">Wszyscy przewoźnicy</option>
            </select></label>
            {notificationForm.recipient === "ROLA" && (
              <label>Rola <select value={notificationForm.role} onChange={(event) => setNotificationForm((current) => ({ ...current, role: event.target.value }))}>{allRoleOptions.map((role) => <option value={role.name} key={role.name}>{role.label}</option>)}</select></label>
            )}
            {notificationForm.recipient === "UZYTKOWNIK" && (
              <label>Użytkownik <select value={notificationForm.userId} onChange={(event) => setNotificationForm((current) => ({ ...current, userId: event.target.value }))}>{appState.users.map((account) => <option value={account.id} key={account.id}>{account.login}</option>)}</select></label>
            )}
            {notificationForm.recipient === "PRZEWOZNIK" && (
              <label>Przewoźnik <select value={notificationForm.carrierId} onChange={(event) => setNotificationForm((current) => ({ ...current, carrierId: event.target.value }))}>{appState.carriers.map((carrier) => <option value={carrier.id} key={carrier.id}>{carrier.name}</option>)}</select></label>
            )}
            <label className="span-2">Treść <textarea value={notificationForm.message} onChange={(event) => setNotificationForm((current) => ({ ...current, message: event.target.value }))} /></label>
            <button className="is-save" onClick={sendNotification}>Wyślij</button>
          </div>
          <PanelTitle title="WYSŁANE" />
          <div className="vba-list-box">
            {appState.notifications.map((notification) => <div key={notification.id}>{notification.createdAt} | {notification.recipient} | {notification.message}</div>)}
          </div>
        </>
      );
    }

    if (tab === "options") {
      return <AdminOptions appState={appState} onAppStateChange={onAppStateChange} />;
    }

    return (
      <>
        <PanelTitle title="ARCHIWUM DZIAŁAŃ" />
        <div className="vba-list-box vba-list-box--admin">
          {appState.archive.map((item) => (
            <button className={clsx(item.id === selectedAdminArchive?.id && "is-active")} type="button" key={item.id} onClick={() => setSelectedAdminArchiveId(item.id)}>
              {item.archivedAt} | {item.owner} | {item.category} | {item.title}
            </button>
          ))}
        </div>
        <PanelTitle title="PEŁNY OPIS / KOMENTARZ" />
        <pre className="vba-description-box">
          {selectedAdminArchive ? `${selectedAdminArchive.archivedAt}\n${selectedAdminArchive.owner} | ${selectedAdminArchive.category}\n${selectedAdminArchive.title}\n${selectedAdminArchive.details || "brak dodatkowego komentarza"}` : "Zaznacz wpis archiwum."}
        </pre>
      </>
    );
  }

  return (
    <>
      <section className="vba-window vba-admin-panel">
        <aside className="vba-admin-sidebar">
          <h2>SNG</h2>
          <strong>PANEL ADMINISTRACYJNY</strong>
          <span>NAWIGACJA</span>
          {navButton("Kolejka zgłoszeń", "queue")}
          {navButton("Zgłoszenia oper.", "ops")}
          {navButton("Konta i uprawnienia", "accounts")}
          {navButton("Przewoźnicy", "carriers")}
          {navButton("Powiadomienia", "notifications")}
          {navButton("Opcje", "options")}
          {navButton("Archiwum", "archive")}
        </aside>
        <main className="vba-admin-main">
          <div className="vba-admin-head">
            <div>
              <h1>Panel administratora</h1>
              <span>Sesja: stobima | Widok: {tab}</span>
            </div>
            <button onClick={onClose}>Zamknij</button>
          </div>
          {renderMainPanel()}
        </main>
      </section>
      {renderCarrierProfileModal()}
    </>
  );
}

function PanelTitle({ title, icon }: { title: string; icon?: ReactNode }) {
  return (
    <div className="vba-panel-title">
      {icon}
      <strong>{title}</strong>
    </div>
  );
}

function AdminOptions({
  appState,
  onAppStateChange
}: {
  appState: AppState;
  onAppStateChange: (updater: (current: AppState) => AppState) => void;
}) {
  const [routeType, setRouteType] = useState("");
  const [pointType, setPointType] = useState("");
  const [roleName, setRoleName] = useState("");
  const [roleTemplate, setRoleTemplate] = useState<CustomRole["template"]>("standardowe");
  const [roleAccessDraft, setRoleAccessDraft] = useState<NavKey[]>([...roleAccess.podglad]);
  const [adminImportText, setAdminImportText] = useState("");

  const roleTemplates: Array<{ value: CustomRole["template"]; label: string; access: NavKey[] }> = [
    { value: "standardowe", label: "Standardowe - jak podgląd", access: roleAccess.podglad },
    { value: "srednie", label: "Średnie - jak st. dyspozytor", access: roleAccess["st. dyspozytor"] },
    { value: "wysokie", label: "Wysokie - jak kierownik", access: roleAccess.kierownik },
    { value: "admin", label: "Administrator - pełne", access: roleAccess.administrator }
  ];

  function setTemplate(template: CustomRole["template"]) {
    setRoleTemplate(template);
    setRoleAccessDraft([...(roleTemplates.find((item) => item.value === template)?.access ?? roleAccess.podglad)]);
  }

  function toggleAccess(key: NavKey) {
    setRoleAccessDraft((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    );
  }

  function addRouteType() {
    if (!routeType.trim()) return;
    onAppStateChange((current) => ({
      ...current,
      routeTypes: Array.from(new Set([...current.routeTypes, routeType.trim()])),
      archive: [archiveItem("Opcje", `Dodano typ trasy ${routeType.trim()}`, "Stobima"), ...current.archive]
    }));
    setRouteType("");
  }

  function addPointType() {
    if (!pointType.trim()) return;
    onAppStateChange((current) => ({
      ...current,
      pointTypes: Array.from(new Set([...current.pointTypes, pointType.trim()])),
      archive: [archiveItem("Opcje", `Dodano typ punktu ${pointType.trim()}`, "Stobima"), ...current.archive]
    }));
    setPointType("");
  }

  function removeRouteType(type: string) {
    if (appState.routes.some((route) => route.serviceType === type) || appState.drivers.some((driver) => driver.type === type)) {
      window.alert("Nie można usunąć typu, który jest używany przez trasę albo kierowcę.");
      return;
    }

    onAppStateChange((current) => ({
      ...current,
      routeTypes: current.routeTypes.filter((candidate) => candidate !== type),
      archive: [archiveItem("Opcje", `Usunięto typ trasy ${type}`, "Stobima"), ...current.archive]
    }));
  }

  function removePointType(type: string) {
    if (appState.routes.some((route) => route.points?.some((point) => point.type === type))) {
      window.alert("Nie można usunąć typu punktu, który jest używany w trasie.");
      return;
    }

    onAppStateChange((current) => ({
      ...current,
      pointTypes: current.pointTypes.filter((candidate) => candidate !== type),
      archive: [archiveItem("Opcje", `Usunięto typ punktu ${type}`, "Stobima"), ...current.archive]
    }));
  }

  function addCustomRole() {
    const name = roleName.trim();
    if (!name) return;
    const exists = getAllRoles(appState.customRoles).some((role) => role.name.toLowerCase() === name.toLowerCase());
    if (exists) {
      window.alert("Taka rola już istnieje.");
      return;
    }

    const created: CustomRole = {
      id: createId("role"),
      name,
      template: roleTemplate,
      access: Array.from(new Set(["pulpit" as NavKey, ...roleAccessDraft]))
    };

    onAppStateChange((current) => ({
      ...current,
      customRoles: [...current.customRoles, created],
      archive: [archiveItem("Opcje", `Dodano rolę ${created.name}`, "Stobima", `Uprawnienia: ${created.access.join(", ")}`), ...current.archive]
    }));
    setRoleName("");
  }

  function removeCustomRole(role: CustomRole) {
    if (appState.users.some((account) => account.role === role.name)) {
      window.alert("Nie można usunąć roli przypisanej do użytkownika.");
      return;
    }

    onAppStateChange((current) => ({
      ...current,
      customRoles: current.customRoles.filter((candidate) => candidate.id !== role.id),
      archive: [archiveItem("Opcje", `Usunięto rolę ${role.name}`, "Stobima"), ...current.archive]
    }));
  }

  function parseAdminImportText(text: string) {
    return parseDelimitedRows(text);
  }

  function importAdminRows(rows: unknown[][]) {
    const [rawHeader, ...body] = rows.filter((row) => row.some((cell) => String(cell ?? "").trim()));
    if (!rawHeader?.length || body.length === 0) {
      window.alert("Nie rozpoznano danych. Użyj szablonu importu z nagłówkiem.");
      return;
    }

    const header = rawHeader.map((cell) => normalizeCity(String(cell ?? "")));
    const getCell = (row: unknown[], name: string) => String(row[header.indexOf(normalizeCity(name))] ?? "").trim();

    onAppStateChange((current) => {
      let carriers = [...current.carriers];
      let drivers = [...current.drivers];
      let vehicles = [...(current.vehicles ?? [])];
      let carriersChanged = 0;
      let driversChanged = 0;
      let vehiclesChanged = 0;

      function findOrCreateCarrier(name: string) {
        const normalizedName = normalizeCity(name);
        let carrier = carriers.find((candidate) => normalizeCity(candidate.name) === normalizedName);
        if (!carrier) {
          carrier = {
            id: createId("car"),
            name: name || "Nowy przewoźnik",
            email: "",
            phone: "",
            notes: ""
          };
          carriers = [carrier, ...carriers];
          carriersChanged += 1;
        }
        return carrier;
      }

      body.forEach((row) => {
        const recordType = normalizeCity(getCell(row, "typ"));
        const name = getCell(row, "nazwa");
        if (!recordType || !name) return;

        if (recordType.includes("przewoznik")) {
          const id = getCell(row, "id");
          const existingIndex = carriers.findIndex((carrier) =>
            (id && carrier.id === id) || normalizeCity(carrier.name) === normalizeCity(name)
          );
          const carrier: Carrier = {
            id: id || carriers[existingIndex]?.id || createId("car"),
            name,
            email: getCell(row, "email") || carriers[existingIndex]?.email || "",
            phone: getCell(row, "telefon") || carriers[existingIndex]?.phone || "",
            notes: getCell(row, "uwagi") || carriers[existingIndex]?.notes || ""
          };
          carriers = existingIndex >= 0
            ? carriers.map((candidate, index) => index === existingIndex ? carrier : candidate)
            : [carrier, ...carriers];
          carriersChanged += 1;
          return;
        }

        if (recordType.includes("pojazd") || recordType.includes("auto")) {
          const carrier = findOrCreateCarrier(getCell(row, "przewoznik") || current.carriers[0]?.name || "SNG Transport");
          const registration = getCell(row, "rejestracja");
          const existingIndex = vehicles.findIndex((vehicle) =>
            vehicle.carrierId === carrier.id &&
            ((registration && normalizeCity(vehicle.registration) === normalizeCity(registration)) || normalizeCity(vehicle.model) === normalizeCity(name))
          );
          const vehicle: Vehicle = {
            id: getCell(row, "id") || vehicles[existingIndex]?.id || createId("veh"),
            carrierId: carrier.id,
            model: name,
            name: getCell(row, "nazwa_galerii") || getCell(row, "nazwa") || name,
            registration,
            type: getCell(row, "typ_pojazdu") || getCell(row, "typ_samochodu") || "Furgon",
            loadKg: Number(getCell(row, "ladownosc") || getCell(row, "udzwig") || vehicles[existingIndex]?.loadKg || 0),
            pallets: Number(getCell(row, "palety") || vehicles[existingIndex]?.pallets || 0),
            active: parseActiveFlag(getCell(row, "aktywny") || "tak", true),
            imageDataUrl: vehicles[existingIndex]?.imageDataUrl || "",
            notes: getCell(row, "uwagi") || vehicles[existingIndex]?.notes || ""
          };
          vehicles = existingIndex >= 0
            ? vehicles.map((candidate, index) => index === existingIndex ? vehicle : candidate)
            : [vehicle, ...vehicles];
          vehiclesChanged += 1;
          return;
        }

        if (recordType.includes("kierowca")) {
          const carrier = findOrCreateCarrier(getCell(row, "przewoznik") || current.carriers[0]?.name || "SNG Transport");
          const id = getCell(row, "id");
          const existingIndex = drivers.findIndex((driver) =>
            (id && driver.id === id) ||
            (normalizeCity(driver.name) === normalizeCity(name) && driver.carrierId === carrier.id)
          );
          const currentDriver = existingIndex >= 0 ? drivers[existingIndex] : undefined;
          const vehicleText = getCell(row, "pojazd") || getCell(row, "auto") || currentDriver?.vehicle || "";
          const registration = getCell(row, "rejestracja") || currentDriver?.registration || "";
          const matchedVehicle = vehicles.find((vehicle) =>
            vehicle.carrierId === carrier.id &&
            ((registration && normalizeCity(vehicle.registration) === normalizeCity(registration)) || (vehicleText && normalizeCity(vehicleDisplayName(vehicle)).includes(normalizeCity(vehicleText))))
          );
          const driver: Driver = {
            id: id || currentDriver?.id || createId("drv"),
            name,
            type: getCell(row, "typ_kierowcy") || currentDriver?.type || current.routeTypes[0] || "Food",
            active: parseActiveFlag(getCell(row, "aktywny") || "tak", true),
            carrierId: carrier.id,
            vehicleId: matchedVehicle?.id || currentDriver?.vehicleId || "",
            privatePhone: getCell(row, "telefon_prywatny") || currentDriver?.privatePhone || "",
            workPhone: getCell(row, "telefon_sluzbowy") || getCell(row, "telefon_służbowy") || currentDriver?.workPhone || "",
            phoneId: getCell(row, "id_telefonu") || currentDriver?.phoneId || "",
            awizacja: getCell(row, "awizacja") || currentDriver?.awizacja || "",
            vehicle: matchedVehicle?.model || vehicleText,
            registration,
            pallets: Number(matchedVehicle?.pallets || getCell(row, "palety") || currentDriver?.pallets || 0),
            capacityKg: Number(matchedVehicle?.loadKg || getCell(row, "udzwig") || getCell(row, "udźwig") || currentDriver?.capacityKg || 0),
            city: getCell(row, "miasto") || currentDriver?.city || "",
            postalCode: getCell(row, "kod") || currentDriver?.postalCode || "",
            address: getCell(row, "adres") || currentDriver?.address || "",
            notes: getCell(row, "uwagi") || currentDriver?.notes || "",
            awizacjaOd1: getCell(row, "awizacja_od_1") || currentDriver?.awizacjaOd1 || "",
            awizacjaOd2: getCell(row, "awizacja_od_2") || currentDriver?.awizacjaOd2 || "",
            photoDataUrl: currentDriver?.photoDataUrl
          };
          drivers = existingIndex >= 0
            ? drivers.map((candidate, index) => index === existingIndex ? driver : candidate)
            : [driver, ...drivers];
          driversChanged += 1;
        }
      });

      return {
        ...current,
        carriers,
        drivers,
        vehicles,
        archive: [archiveItem("Import bazy", `Przewoźnicy: ${carriersChanged}, kierowcy: ${driversChanged}, pojazdy: ${vehiclesChanged}`, "Stobima"), ...current.archive]
      };
    });
    setAdminImportText("");
  }

  async function importAdminFile(file?: File) {
    if (!file) return;
    if (file.name.toLowerCase().endsWith(".xlsx")) {
      importAdminRows(await readXlsxFile(file));
      return;
    }
    importAdminRows(parseAdminImportText(await file.text()));
  }

  function downloadImportTemplate() {
    const link = document.createElement("a");
    link.href = "/szablon-importu-sng.csv";
    link.download = "szablon-importu-sng.csv";
    link.click();
  }

  function optionId(group: string, value: string) {
    return `${group}-${value.toLowerCase().replace(/[^a-z0-9]+/gi, "-")}`;
  }

  function adminToggle(id: string, label: string, checked: boolean, onChange: (checked: boolean) => void, title?: string) {
    return (
      <Fragment key={id}>
        <input
          className="customCheckBoxInput"
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
        />
        <label className="customCheckBoxWrapper" htmlFor={id} title={title}>
          <div className="customCheckBox">
            <div className="inner">{label}</div>
          </div>
        </label>
      </Fragment>
    );
  }

  return (
    <div className="admin-options">
      <PanelTitle title="OPCJE SYSTEMU" />
      <div className="admin-options__grid">
        <section>
          <h3>Typy tras</h3>
          <label>Nowy typ trasy <input value={routeType} onChange={(event) => setRouteType(event.target.value)} /></label>
          <button className="is-save" onClick={addRouteType}>Dodaj typ trasy</button>
          <div className="customCheckBoxHolder customCheckBoxHolder--wrap">
            {appState.routeTypes.map((type) =>
              adminToggle(optionId("route-type", type), type, true, (checked) => {
                if (!checked) removeRouteType(type);
              }, "Odznacz, aby usunąć typ trasy")
            )}
          </div>
        </section>
        <section>
          <h3>Typy punktów</h3>
          <label>Nowy typ punktu <input value={pointType} onChange={(event) => setPointType(event.target.value)} /></label>
          <button className="is-save" onClick={addPointType}>Dodaj typ punktu</button>
          <div className="customCheckBoxHolder customCheckBoxHolder--wrap">
            {appState.pointTypes.map((type) =>
              adminToggle(optionId("point-type", type), type, true, (checked) => {
                if (!checked) removePointType(type);
              }, "Odznacz, aby usunąć typ punktu")
            )}
          </div>
        </section>
      </div>
      <section className="admin-options__import">
        <h3>Import przewoźników i kierowców</h3>
        <p>Wgraj CSV/XLSX z szablonu. Przewoźnicy i kierowcy z tym samym ID albo nazwą zostaną zaktualizowani, a nowe rekordy dopisane.</p>
        <div className="vba-form-grid">
          <label>Plik importu
            <input type="file" accept=".csv,.tsv,.txt,.xlsx" onChange={(event) => importAdminFile(event.target.files?.[0])} />
          </label>
          <button type="button" onClick={downloadImportTemplate}>Pobierz szablon CSV</button>
        </div>
        <textarea
          value={adminImportText}
          onChange={(event) => setAdminImportText(event.target.value)}
          placeholder={"typ;id;nazwa;przewoznik;email;telefon;typ_kierowcy;aktywny\nPRZEWOZNIK;car-demo;Demo Transport;;demo@firma.pl;500 000 000;;;\nKIEROWCA;;Jan Kowalski;Demo Transport;;;Food;TAK"}
        />
        <button className="is-save" onClick={() => importAdminRows(parseAdminImportText(adminImportText))}>Importuj wklejone dane</button>
      </section>
      <section className="admin-options__roles">
        <h3>Role i uprawnienia</h3>
        <div className="vba-form-grid">
          <label>Nazwa roli <input value={roleName} onChange={(event) => setRoleName(event.target.value)} placeholder="np. użytkownik zewnętrzny" /></label>
          <label>Szablon <select value={roleTemplate} onChange={(event) => setTemplate(event.target.value as CustomRole["template"])}>
            {roleTemplates.map((template) => <option value={template.value} key={template.value}>{template.label}</option>)}
          </select></label>
        </div>
        <div className="permission-grid permission-grid--custom customCheckBoxHolder">
          {navItems.map((item) =>
            adminToggle(optionId("role-access", item.key), item.label, roleAccessDraft.includes(item.key), () => toggleAccess(item.key))
          )}
        </div>
        <button className="is-save" onClick={addCustomRole}>Dodaj rolę</button>
        <div className="vba-list-box">
          {appState.customRoles.length === 0 && <span>Brak ról niestandardowych.</span>}
          {appState.customRoles.map((role) => (
            <button type="button" key={role.id} onClick={() => removeCustomRole(role)}>
              {role.name} | {role.template} | {role.access.map((key) => navItems.find((item) => item.key === key)?.label ?? key).join(", ")} | usuń
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function PreviewModal({
  title,
  children,
  onClose,
  onPrint
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  onPrint?: () => void;
}) {
  const printBodyRef = useRef<HTMLDivElement | null>(null);

  function printOnlyPreview() {
    const content = printBodyRef.current?.innerHTML;
    const printWindow = window.open("", "_blank", "width=1280,height=900");
    if (!content || !printWindow) {
      onPrint?.();
      return;
    }

    const styleMarkup = Array.from(document.querySelectorAll<HTMLLinkElement | HTMLStyleElement>('link[rel="stylesheet"], style'))
      .map((node) => node.tagName.toLowerCase() === "link" ? (node as HTMLLinkElement).outerHTML : `<style>${(node as HTMLStyleElement).innerHTML}</style>`)
      .join("\n");

    printWindow.document.open();
    printWindow.document.write(`<!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${title}</title>
          ${styleMarkup}
          <style>
            @page { size: A4 landscape; margin: 4mm; }
            html, body { width: 100%; min-height: 0; margin: 0; background: #fff !important; overflow: visible !important; }
            body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .print-preview-sheet { display: block !important; min-height: 0 !important; height: auto !important; padding: 0 !important; border: 0 !important; box-shadow: none !important; background: #fff !important; }
            .print-preview-sheet * { color: #111 !important; }
            .print-summary-table, .print-preview-sheet table { break-inside: avoid; page-break-inside: avoid; }
            .print-preview-sheet--daily { width: 100% !important; max-width: none !important; gap: 4px !important; margin: 0 !important; }
            .print-preview-sheet--daily .print-daily-grid { break-before: avoid !important; page-break-before: avoid !important; break-inside: auto !important; page-break-inside: auto !important; table-layout: fixed !important; font-size: 6.4px !important; background: #fff !important; background-image: none !important; }
            .print-preview-sheet--daily .print-daily-grid th { height: 12px !important; padding: 0 2px !important; font-size: 8px !important; }
            .print-preview-sheet--daily .print-daily-grid td { height: 8.5px !important; padding: 0 2px !important; line-height: 1 !important; white-space: nowrap !important; overflow: hidden !important; text-overflow: ellipsis !important; }
            .print-preview-sheet--daily .print-daily-grid tr { break-inside: avoid; page-break-inside: avoid; }
            .print-preview-sheet--daily .print-brand-head { break-after: avoid; page-break-after: avoid; }
            .print-preview-sheet--daily .print-summary-table { margin-top: 4px !important; break-before: avoid !important; page-break-before: avoid !important; }
            @media print {
              body * { visibility: visible !important; }
              .print-preview-sheet, .print-preview-sheet * { visibility: visible !important; }
            }
          </style>
        </head>
        <body>${content}<script>window.addEventListener("load",function(){setTimeout(function(){window.print();window.close();},120);});<\/script></body>
      </html>`);
    printWindow.document.close();
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={title}>
      <section className="preview-modal">
        <header>
          <h2>{title}</h2>
          <div>
            <button onClick={printOnlyPreview}>Drukuj</button>
            <button className="is-danger" onClick={onClose}>Zamknij</button>
          </div>
        </header>
        <div className="preview-modal__body" ref={printBodyRef}>{children}</div>
      </section>
    </div>
  );
}

function StatusChip({ status }: { status: ScheduleEntry["status"] }) {
  return (
    <span className={clsx("status-chip", `status-chip--${status.toLowerCase()}`)}>
      {statusLabels[status]}
    </span>
  );
}
